import React, { useState, useEffect } from "react";
import { setCompanyMessageRegister } from "../lib/manager-nudge";
import { challengePermissions, resumeBlockedByEndDate } from "../lib/challenge-permissions";
import { canTargetAction } from "../lib/target-permissions";
import { canBeProposedAsParticipant } from "../lib/participant-eligibility";
import { challengeObjectiveLabel } from "../lib/challenge-objectives";
import { challengeErrorMessage } from "../lib/challenge-participants";
import { ChallengeObjectiveSelect } from "../components/ChallengeObjectiveSelect";
import { hasLicencesTab, canSeeInvoices, canBuyLicences } from "../lib/encadrant";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { SubscriptionStateBadge } from "../components/SubscriptionStateBadge";
import { isSubscriptionOpen } from "../lib/subscription-access";
import { Button } from "../components/ui/button";
import { AiChat } from "../components/AiChat";
import { SimulationCaseWizard } from "../components/SimulationCaseWizard";
import { ReportingTab } from "../components/ReportingTab";
import { CertificationPathsPage } from "./CertificationPathsPage";
import { OneShotPackConfig } from "../components/OneShotPackConfig";
import { EvaluationCriteriaTab } from "../components/EvaluationCriteriaTab";
import { ContextsTab } from "../components/ContextsTab";
import { CollaboratorLanguageFields } from "../components/CollaboratorLanguageFields";
import {
  getPersonaLabel, getPersonaSubtitle, getPersonaDetailFields, isPersonaComplete,
  mapPersonaFromApi, PersonaBadges, PersonaDetailView,
  CONTACT_REASONS as CC_CONTACT_REASONS, EXPRESSION_STYLES as CC_EXPRESSION_STYLES, MAIN_EXPECTATIONS as CC_MAIN_EXPECTATIONS,
  PERSONA_FUNCTIONS as SHARED_PERSONA_FUNCTIONS, CSP_LIST as SHARED_CSP_LIST, AGE_RANGES as SHARED_AGE_RANGES,
  PURCHASE_CRITERIA as SHARED_PURCHASE_CRITERIA, PURCHASE_PHASES as SHARED_PURCHASE_PHASES,
  DECISION_MAKER_CODES, DECISION_MAKER_LABELS,
  isCC as isPersonaCC, isB2C as isPersonaB2CUtil,
  PersonalityStyleSelect, MaturityLevelSelect, InternalPressureSelect, PersonaStatusBadges,
  personaNeedsPersonalityMigration, pickDecisionMaker, PersonaForm,
} from "../components/PersonaUtils";
import { localizePersonaOption } from "../lib/persona-options-i18n";
import { pickPersonaField } from "../lib/persona-i18n";
import { SALES_MODELS } from "../lib/sales-models";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Separator } from "../components/ui/separator";
import { Badge } from "../components/ui/badge";
import {
  User,
  Users,
  Building,
  Target,
  Save,
  Upload,
  Plus,
  UserX,
  Pencil,
  X,
  Check,
  Camera,
  Link,
  Image,
  Mail,
  RotateCcw,
  ArrowLeft,
  Send,
  Building2,
  Briefcase,
  Factory,
  UserCircle,
  Trash2,
  Trophy,
  Play,
  Square,
  Calendar,
  Eye,
  Loader2,
  Info,
  ChevronRight,
  Shield,
  Settings,
  BookOpen,
  FileText,
  Download,
  ShoppingCart,
  Package,
  ChevronLeft,
  BarChart3,
  Sparkles,
  Bell,
} from "lucide-react";
import { Switch } from "../components/ui/switch";
import type { User as UserType, Category, Challenge, ChallengeTeam, ChallengeParticipant, Simulator as SimulatorType } from "../types";
import { authApi, accountApi, categoriesApi, challengesApi, packsApi, type OneShotPack, type PackSimulatorOption } from "../lib/api";
import { pickSimulatorDescription } from "../lib/simulator-description-i18n";
import { detectCoachKind } from "../lib/coach-kind";
import { pickCompanyDescription } from "../lib/company-i18n";
import { pickFrEn } from "../lib/texte-i18n";
import { MultiSelect } from "../components/ui/multi-select";
import { ConfirmDeleteDialog } from "../components/ui/confirm-dialog";
import { useTranslation, Trans } from "react-i18next";
import { localizeCompanySize, localizeIndustry } from "../lib/target-options-i18n";
import { INDUSTRY_OPTIONS, COMPANY_SIZE_OPTIONS } from "../lib/company-options";
import { pickOfferLabel, pickOfferDescription } from "../lib/offer-i18n";
import { changeLanguage, getCurrentLanguage } from "../i18n";
import { useResizable } from "../hooks/useResizable";
import { PseudoModal } from "../components/PseudoModal";
import { CollaboratorRowActions } from "../components/CollaboratorRowActions";
import { ProfilePhotoDialog } from "../components/ProfilePhotoDialog";
import { CollaboratorFicheDialog, type FicheFieldKey, type FicheCollaborator } from "../components/CollaboratorFicheDialog";
import { LicenceSimulatorsDialog, type SelectableSimulator } from "../components/LicenceSimulatorsDialog";
import { licenceSimulatorChoice } from "../lib/licence-simulators";
import { canCollaborator, checkCollaboratorAction, assignableProfiles, creatableProfiles, type CollaboratorAction } from "../lib/collaborator-permissions";
import { buildCollaboratorPatch } from "../lib/collaborator-patch";
import { Gamepad2, Award, Gift } from "lucide-react";
import { badgesApi } from "../lib/api";
import { BizRewardsCard } from "../components/BizRewardsCard";
import { getSubscriptionPlanCode, getTierFromPlanCode, type PlanTier } from "../lib/plan-codes";
import { simulateurUtilisable } from "../lib/simulateurs-internes";

interface AccountPageProps {
  user: UserType;
  onUpdate: (user: Partial<UserType>) => void;
  adminAccountId?: string;
  onBackToAdmin?: () => void;
  onNavigateToChallenge?: (id: string) => void;
  /** Ouvre les réglages de notifications de l'entreprise (canaux verrouillables par le compte). */
  onOpenCompanyNotifications?: () => void;
  // "full" (Paramètres = tous les onglets) ou "profile" (Mon profil = onglet profil seul).
  variant?: "full" | "profile";
  // Mode « onglet focalisé » : rend UNIQUEMENT le contenu de cet onglet, sans la
  // TabsList ni le chrome de page. Utilisé pour embarquer un onglet (ex. "targets"
  // = Cas de simulation) dans l'espace encadrant (Concevoir), sans passer par « Mon compte ».
  embedTab?: string;
  /** Onglet sur lequel ouvrir la page, sans masquer les autres. Sert aux boutons
   *  d'alerte du cockpit : « Gérer les collaborateurs » doit arriver SUR les
   *  collaborateurs, pas sur le profil. */
  initialTab?: string;
}

interface PersonaLocal {
  id: string;
  targetId: string;
  function: string;
  decisionMakerProfile: string;
  painPoints: string;
  category: string;
  personaType?: string;
  csp?: string;
  ageRange?: string;
  gender?: string;
  purchaseCriteria?: string;
  purchasePhase?: string;
  productRelationship?: string;
  contactReason?: string;
  expressionStyle?: string;
  mainExpectation?: string;
  // Section "Réalisme" (B2B Sales)
  personalMotivations?: string;
  dailyIrritants?: string;
  typicalObjections?: string;
  // Section "Psychologie"
  personalityStyle?: string;
  maturityLevel?: string;
  internalPressure?: string;
  isNew?: boolean;
}

interface TargetProspect {
  id: string;
  caseType: string;
  simulatorId?: string | null;
  contextId?: string | null;
  companySize: string;
  industry: string;
  offerIds: string[];
  personae: PersonaLocal[];
  // Métadonnées utilisées pour les onglets "Cas d'équipe" / "Cas personnels"
  // et l'affichage de la date sur les cartes (cf. AccountPage targets tab).
  createdAt?: string | null;
  visibility?: "public" | "private";
  sourceRealCallSessionId?: string | null;
  // Nom libre du cas — titre affiché sur la carte (fallback : companySize).
  // Saisi dans le wizard côté ORG/COO, auto-généré par Coach Debrief / Coach Prepa.
  displayName?: string | null;
  // Propriétaire du cas : un MNG ne modifie que les siens et n'en supprime aucun
  // (`canTargetAction`, src/lib/target-permissions.ts — Q1 du 01/10/2026).
  createdBy?: string | null;
  ownerUserId?: string | null;
  // Cas DÉSACTIVÉ (Q1 du 01/10/2026) : montré ici avec « Désactivé » + « Réactiver », proposé nulle part ailleurs.
  archivedAt?: string | null;
  // `socle` = cas générique de la banque Kestio (onglet « Cas génériques », lecture seule).
  kind?: string | null;
  // Offres du cas telles que renvoyées par `GET /api/account/targets` : seules à connaître les
  // offres Kestio des cas génériques (globales, absentes de `accountOffers`).
  offers?: any[];
  // Entreprise du COMMERCIAL (le vendeur) d'un cas générique — fictive, fournie par Kestio.
  userCompanyNameFr?: string | null;
  userCompanyNameEn?: string | null;
  userCompanyProfileFr?: string | null;
  userCompanyProfileEn?: string | null;
}

type LicenceType = string;

const decisionMakerCodes = [
  "decideurFinal",
  "decideurTechnique",
  "influenceur",
  "prescripteur",
  "champion",
  "utilisateurFinal",
  "nonDecideur",
  "gardien",
];

interface Collaborator {
  id: string;
  civility: "M." | "Mme";
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  profileType: string;
  status: string;
  position: string;
  /** Profil LinkedIn (colonne `users.linkedin_url`, partagée avec le profil /me). */
  linkedinUrl?: string;
  /** Photo de profil (lien https:// ou image réduite — `src/lib/profile-picture.ts`). */
  profilePicture?: string;
  licences: LicenceType[];
  managerId?: string | null;
  /** Langue d'AFFICHAGE : interface, menus, emails. `fr` | `en`. */
  language?: string;
  /** Langue d'ENTRAÎNEMENT : scénario, simulation, débrief. `null` = suit `language`. */
  spokenLanguage?: string | null;
}

interface LicencePool {
  type: LicenceType;
  total: number;
  used: number;
  label: string;
  color: string;
  code: string;
  /** Licence d'abonnement : périmètre de simulateurs (`lib/licence-simulators.ts`). */
  tier?: string | null;
  maxSimulators?: number | null;
  isOneShot?: boolean;
}

// Helper to parse multi-value fields (stored as JSON array or legacy single value)
function parseMultiValue(value: string | undefined | null): string[] {
  if (!value) return [];
  try {
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed)) return parsed;
    return [value];
  } catch {
    return value ? [value] : [];
  }
}

// Helper to stringify multi-value fields for storage
function stringifyMultiValue(values: string[]): string {
  return JSON.stringify(values);
}

// Valeur sentinelle de l'option "Autre" du MultiSelect Secteur d'activité (édition d'un cas).
// Quand présente dans la value affichée, on montre le champ texte libre. La sentinelle n'est
// JAMAIS persistée : à la sauvegarde, on stocke la valeur libre saisie (ex "Pharmacie").
// Aligné sur SimulationCaseWizard.tsx (création). Préfixe __ pour éviter toute collision.
const CUSTOM_INDUSTRY = "__autre_industry__";

// `accountId` : compte géré en « gérer ce compte » (ADM/COO) — transmis à la liste ET au lien PDF.
function InvoicesTab({ accountId }: { accountId?: string }) {
  const { t, i18n } = useTranslation();
  const [invoices, setInvoices] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    accountApi.getInvoices(accountId)
      .then((data: any[]) => setInvoices(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [accountId]);

  if (loading) return <p className="text-sm text-muted-foreground text-center py-8">{t("account.loadingInvoices")}</p>;

  if (invoices.length === 0) {
    return (
      <div className="text-center py-12">
        <FileText className="h-12 w-12 mx-auto text-muted-foreground/30 mb-3" />
        <p className="text-muted-foreground">{t("account.noInvoices")}</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {invoices.map((inv: any) => (
        <div key={inv.invoiceId} className="flex items-center justify-between py-3 px-4 rounded-lg border bg-card">
          <div className="flex items-center gap-4">
            <FileText className="h-5 w-5 text-muted-foreground shrink-0" />
            <div>
              <p className="text-sm font-medium">{inv.invoiceNumber || inv.invoiceId}</p>
              <p className="text-xs text-muted-foreground">
                {inv.date ? new Date(inv.date).toLocaleDateString(i18n.language === "fr" ? "fr-FR" : "en-GB") : "—"} — {inv.total != null ? `${inv.total} ${inv.currencyCode || "EUR"}` : "—"}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant={inv.status === "paid" ? "default" : "secondary"} className="text-xs">
              {inv.status === "paid" ? t("account.invoiceStatusPaid") : inv.status === "sent" ? t("account.invoiceStatusSent") : inv.status}
            </Badge>
            <a
              href={accountApi.getInvoicePdfUrl(String(inv.invoiceId), accountId)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline"
            >
              <Download className="h-3.5 w-3.5" /> PDF
            </a>
          </div>
        </div>
      ))}
    </div>
  );
}

// === Listes de valeurs pour Personae ===
const PERSONA_FUNCTIONS = [
  "Directeur Général / CEO",
  "Directeur Commercial",
  "Directeur Marketing",
  "Directeur Financier / CFO",
  "Directeur des Achats",
  "Directeur Technique / CTO",
  "Directeur des Ressources Humaines",
  "Directeur des Opérations / COO",
  "Responsable IT / DSI",
  "Chef de projet",
  "Responsable Qualité",
  "Responsable Logistique",
];

const CSP_LIST = [
  "Agriculteur exploitant",
  "Artisan",
  "Commerçant",
  "Chef d'entreprise",
  "Cadre et profession intellectuelle supérieure",
  "Profession intermédiaire",
  "Employé",
  "Ouvrier",
  "Retraité",
  "Étudiant",
];

const AGE_RANGES = ["18-24", "25-34", "35-44", "45-54", "55-64", "65+"];
const GENDERS = [{ value: "homme", label: "Homme" }, { value: "femme", label: "Femme" }];
const PURCHASE_CRITERIA = ["Prix", "Qualité", "Marque", "Avis", "Praticité"];
const PURCHASE_PHASES = ["Réflexion", "Recherche d'infos", "Décision", "Déjà client"];

// Customer Care fields
const CONTACT_REASONS = ["Panne", "Dysfonctionnement", "Incompréhension produit/service", "Mécontentement livraison", "Demande de remboursement"];
const EXPRESSION_STYLES = [
  { value: "Laconique", description: "Réponses courtes, peu de détails" },
  { value: "Bavard", description: "Parle beaucoup, digressions fréquentes" },
  { value: "Précis et technique", description: "Vocabulaire technique, attend des réponses précises" },
  { value: "Confus", description: "Difficulté à expliquer le problème, mélange les sujets" },
  { value: "Emotionnel", description: "Exprime frustration, colère ou déception" },
];
const MAIN_EXPECTATIONS = ["Résolution rapide", "Explication", "Compensation", "Réassurance"];

function PersonaeTab({ accountId }: { accountId?: string }) {
  const { t, i18n } = useTranslation();
  const [personae, setPersonae] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<any | null>(null);
  const [creating, setCreating] = useState(false);
  const [showAiChat, setShowAiChat] = useState(false);
  const [companyDescription, setCompanyDescription] = useState("");
  const [offersText, setOffersText] = useState("");

  const emptyPersona = {
    function: "", personaType: "B2B", gender: "", decisionMakerProfile: "", painPoints: "",
    ageRange: "", csp: "", purchaseCriteria: "", purchasePhase: "", productRelationship: "",
    category: "sales",
  };

  useEffect(() => {
    Promise.all([
      accountApi.getAccountPersonae(accountId),
      accountApi.getCompany(accountId).catch(() => null),
      accountApi.getOffers(accountId).catch(() => []),
    ]).then(([data, company, offers]) => {
      setPersonae(data || []);
      setCompanyDescription(company?.description || "");
      setOffersText((offers || []).map((o: any) => `${o.name}: ${o.description}`).join("\n"));
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const handler = () => { setCreating(true); setEditing({ ...emptyPersona }); };
    window.addEventListener("bizcoach:create-persona", handler);
    return () => window.removeEventListener("bizcoach:create-persona", handler);
  }, []);

  const save = async (persona: any) => {
    try {
      if (persona.id) {
        const updated = await accountApi.updateAccountPersona(persona.id, persona, accountId);
        setPersonae(personae.map((p) => p.id === persona.id ? updated : p));
      } else {
        const created = await accountApi.createAccountPersona(persona, accountId);
        setPersonae([...personae, created]);
      }
      setEditing(null);
      setCreating(false);
    } catch (err: any) {
      alert(err.message);
    }
  };

  const remove = async (id: string) => {
    if (!confirm(t("account.confirmDeletePersona"))) return;
    try {
      await accountApi.deleteAccountPersona(id, accountId);
      setPersonae(personae.filter((p) => p.id !== id));
    } catch (err: any) {
      alert(err.message);
    }
  };


  if (loading) return <p className="text-sm text-muted-foreground text-center py-8">{t("common.loading")}</p>;

  return (
    <div className="space-y-4">
      {showAiChat ? (
        <div className="space-y-3">
          <Button variant="outline" size="sm" onClick={() => setShowAiChat(false)}>{t("account.backToList")}</Button>
          <div style={{ height: "70vh" }}>
            <AiChat
              agentCode="persona_creator"
              robotColor="#877BF2"
              bgColor="#f8f5ff"
              context={{ companyDescription, offers: offersText, language: i18n.language }}
              onItemCreated={async (_type, data) => {
                // Pas de catch ici : AiChat affiche l'erreur et CONSERVE la
                // proposition, pour que rien de la saisie ne soit perdu.
                const created = await accountApi.createAccountPersona(data, accountId);
                setPersonae((prev) => [...prev, created]);
              }}
              onComplete={() => setShowAiChat(false)}
              title={t("onboarding.personaeTitle")}
              subtitle={t("onboarding.personaeSubtitle")}
            />
          </div>
        </div>
      ) : (
      <>
      <div className="flex justify-between items-center">
        <p className="text-sm text-muted-foreground">{t("account.personaeIntro")}</p>
        <div className="flex gap-2">
          {!creating && <Button size="sm" variant="outline" className="border-[#988EF2] text-[#5A4BBE] hover:bg-[#988EF2]/10" onClick={() => setShowAiChat(true)}>{t("account.createWithAi")}</Button>}
          {!creating && <Button size="sm" data-tour="account-new-persona" onClick={() => { setCreating(true); setEditing({ ...emptyPersona }); }}><Plus className="h-4 w-4 mr-2" /> {t("account.newPersona")}</Button>}
        </div>
      </div>

      {creating && <div data-tour="persona-form"><PersonaForm persona={emptyPersona} onSave={save} onCancel={() => { setEditing(null); setCreating(false); }} /></div>}

      {personae.length === 0 && !creating ? (
        <div className="text-center py-12">
          <UserCircle className="h-12 w-12 mx-auto text-muted-foreground/30 mb-3" />
          <p className="text-muted-foreground">{t("account.noPersonaCreated")}</p>
          <p className="text-xs text-muted-foreground mt-1">{t("account.noPersonaCreatedHelp")}</p>
        </div>
      ) : (
        personae.map((p) => (
          editing?.id === p.id ? <PersonaForm key={`edit-${p.id}`} persona={p} onSave={save} onCancel={() => { setEditing(null); setCreating(false); }} /> : (
            <Card key={p.id}>
              <CardContent className="!py-3 flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <PersonaBadges persona={p} />
                    <span className="font-medium text-sm">{getPersonaLabel(p)}</span>
                    <PersonaStatusBadges persona={p} />
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {getPersonaSubtitle(p)}
                  </p>
                </div>
                {/* Persona partagée avec un cas d'autrui (D1 du 01/10/2026) : ni crayon ni corbeille pour
                    le MNG — `viewerCanEdit`, calculé par le serveur avec la règle qui refuse. */}
                {p.viewerCanEdit === false ? (
                  <p data-testid="persona-shared-readonly" className="text-xs text-muted-foreground max-w-[14rem] text-right">{t("account.personaSharedReadOnly")}</p>
                ) : (
                <div className="flex gap-1">
                  <Button size="sm" variant="ghost" className="h-7" aria-label={t("account.edit")} onClick={() => setEditing(p)}><Pencil className="h-3.5 w-3.5" /></Button>
                  <Button size="sm" variant="ghost" className="h-7 text-red-500" aria-label={t("account.delete")} onClick={() => remove(p.id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
                )}
              </CardContent>
            </Card>
          )
        ))
      )}
      </>
      )}
    </div>
  );
}

function OffersTab({ accountId }: { accountId?: string }) {
  const { t, i18n } = useTranslation();
  const [offers, setOffers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<any | null>(null);
  const [creating, setCreating] = useState(false);
  const [showAiChat, setShowAiChat] = useState(false);
  const [companyDescription, setCompanyDescription] = useState("");

  useEffect(() => {
    Promise.all([
      accountApi.getOffers(accountId),
      accountApi.getCompany(accountId).catch(() => null),
    ]).then(([data, company]) => {
      setOffers(data || []);
      setCompanyDescription(company?.description || "");
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const handler = () => { setCreating(true); setEditing({}); };
    window.addEventListener("bizcoach:create-offer", handler);
    return () => window.removeEventListener("bizcoach:create-offer", handler);
  }, []);

  const save = async (offer: any) => {
    try {
      if (offer.id) {
        const updated = await accountApi.updateOffer(offer.id, offer, accountId);
        setOffers(offers.map((o) => o.id === offer.id ? updated : o));
      } else {
        const created = await accountApi.createOffer(offer, accountId);
        setOffers([...offers, created]);
      }
      setEditing(null);
      setCreating(false);
    } catch (err: any) {
      alert(err.message);
    }
  };

  const remove = async (id: string) => {
    if (!confirm(t("account.confirmDeleteOffer"))) return;
    try {
      await accountApi.deleteOffer(id, accountId);
      setOffers(offers.filter((o) => o.id !== id));
    } catch (err: any) {
      alert(err.message);
    }
  };

  const OfferForm = ({ offer, onSave }: { offer: any; onSave: (o: any) => void }) => {
    // i18n : on édite la variante de la langue courante (sinon en EN on voyait/écrivait le FR).
    const isFrLang = (i18n.language || "fr").toLowerCase().startsWith("fr");
    const [form, setForm] = useState({
      displayName: offer.displayName || "",
      name: (isFrLang ? (offer.nameFr || offer.name) : (offer.nameEn || offer.name)) || "",
      description: (isFrLang ? (offer.descriptionFr || offer.description) : (offer.descriptionEn || offer.description)) || "",
      valueProposition: (isFrLang ? (offer.valuePropositionFr || offer.valueProposition) : (offer.valuePropositionEn || offer.valueProposition)) || "",
    });
    return (
      <Card className="border-2 border-blue-200">
        <CardContent className="!py-4 space-y-3">
          {/* Titre optionnel — pour distinguer 2 offres de même nom marketing.
              Purement UI, jamais envoyé à N8N. Cf. Mariam 10/07/26. */}
          <div className="space-y-1">
            <Label>{t("account.offerDisplayName")}</Label>
            <Input
              value={form.displayName}
              onChange={(e) => setForm({ ...form, displayName: e.target.value })}
              placeholder={t("account.offerDisplayNamePlaceholder")}
              maxLength={120}
            />
          </div>
          <div className="space-y-1">
            <Label>{t("account.offerName")}</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={t("account.offerNamePlaceholder")} />
          </div>
          <div className="space-y-1">
            <Label>{t("account.offerDescription")}</Label>
            <textarea
              className="w-full min-h-[80px] px-3 py-2 rounded-md border border-input bg-background text-sm resize-y"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder={t("account.offerDescriptionPlaceholder")}
            />
          </div>
          <div className="space-y-1">
            <Label>{t("account.offerValueProposition")}</Label>
            <textarea
              className="w-full min-h-[70px] px-3 py-2 rounded-md border border-input bg-background text-sm resize-y"
              value={form.valueProposition}
              onChange={(e) => setForm({ ...form, valueProposition: e.target.value })}
              placeholder={t("account.offerValuePropositionPlaceholder")}
            />
            <p className="text-xs text-muted-foreground">{t("account.offerValuePropositionHint")}</p>
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" size="sm" onClick={() => { setEditing(null); setCreating(false); }}>{t("common.cancel")}</Button>
            <Button size="sm" disabled={!form.name} onClick={() => onSave({ ...offer, ...form, _sourceLang: isFrLang ? "fr" : "en" })}>{t("common.save")}</Button>
          </div>
        </CardContent>
      </Card>
    );
  };

  if (loading) return <p className="text-sm text-muted-foreground text-center py-8">{t("common.loading")}</p>;

  return (
    <div className="space-y-4">
      {showAiChat ? (
        <div className="space-y-3">
          <Button variant="outline" size="sm" onClick={() => setShowAiChat(false)}>{t("account.backToList")}</Button>
          <div style={{ height: "70vh" }}>
            <AiChat
              agentCode="offer_creator"
              robotColor="#7CBF94"
              bgColor="#f7faf8"
              context={{ companyDescription, language: i18n.language }}
              onItemCreated={async (_type, data) => {
                // Pas de catch ici : AiChat affiche l'erreur et CONSERVE la
                // proposition, pour que rien de la saisie ne soit perdu.
                const created = await accountApi.createOffer(data, accountId);
                setOffers((prev) => [...prev, created]);
              }}
              onComplete={() => setShowAiChat(false)}
              title={t("onboarding.offersTitle")}
              subtitle={t("onboarding.offersSubtitle")}
            />
          </div>
        </div>
      ) : (
      <>
      <div className="flex justify-between items-center">
        <p className="text-sm text-muted-foreground">{t("account.offersIntro")}</p>
        <div className="flex gap-2">
          {!creating && <Button size="sm" variant="outline" className="border-[#7CBF94] text-[#1E7A3C] hover:bg-[#7CBF94]/10" onClick={() => setShowAiChat(true)}>{t("account.createWithAi")}</Button>}
          {!creating && <Button size="sm" data-tour="account-new-offer" onClick={() => { setCreating(true); setEditing({}); }}><Plus className="h-4 w-4 mr-2" /> {t("account.newOffer")}</Button>}
        </div>
      </div>

      {creating && <div data-tour="offer-form"><OfferForm offer={{}} onSave={save} /></div>}

      {offers.length === 0 && !creating ? (
        <div className="text-center py-12">
          <BookOpen className="h-12 w-12 mx-auto text-muted-foreground/30 mb-3" />
          <p className="text-muted-foreground">{t("account.noOffers")}</p>
          <p className="text-xs text-muted-foreground mt-1">{t("account.noOffersHelp")}</p>
        </div>
      ) : (
        offers.map((o) => (
          editing?.id === o.id ? <OfferForm key={o.id} offer={o} onSave={save} /> : (
            <Card key={o.id}>
              <CardContent className="!py-3 flex items-center justify-between">
                <div>
                  <span className="font-medium text-sm">{pickOfferLabel(o, i18n.language)}</span>
                  {pickOfferDescription(o, i18n.language) && <p className="text-xs text-muted-foreground mt-0.5 whitespace-pre-wrap break-words">{pickOfferDescription(o, i18n.language)}</p>}
                </div>
                <div className="flex gap-1">
                  <Button size="sm" variant="ghost" className="h-7" aria-label={t("account.edit")} onClick={() => setEditing(o)}><Pencil className="h-3.5 w-3.5" /></Button>
                  <Button size="sm" variant="ghost" className="h-7 text-red-500" aria-label={t("account.delete")} onClick={() => remove(o.id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              </CardContent>
            </Card>
          )
        ))
      )}
      </>
      )}
    </div>
  );
}

/**
 * Construit l'ordre d'affichage hiérarchique d'une liste de collaborateurs.
 * Les managers apparaissent au-dessus de leurs subordonnés ; chaque collab
 * reçoit une `_depth` (0 = racine, 1 = direct, etc.) pour permettre une
 * indentation visuelle dans le rendu.
 *
 * Si `viewerId` est fourni, le viewer est exclu de la liste (cas vue MNG où
 * on ne veut pas se voir soi-même) et les collabs dont le manager est le
 * viewer deviennent des racines.
 */
function buildHierarchicalOrder(
  collabs: Collaborator[],
  viewerId?: string,
): Array<Collaborator & { _depth: number }> {
  const filtered = viewerId ? collabs.filter((c) => c.id !== viewerId) : collabs;
  const byId = new Map(filtered.map((c) => [c.id, c]));
  const result: Array<Collaborator & { _depth: number }> = [];
  const visited = new Set<string>();

  const sortFn = (a: Collaborator, b: Collaborator) =>
    `${a.lastName || ""} ${a.firstName || ""}`.localeCompare(`${b.lastName || ""} ${b.firstName || ""}`);

  function visit(collab: Collaborator, depth: number) {
    if (visited.has(collab.id)) return;
    visited.add(collab.id);
    result.push({ ...collab, _depth: depth });
    filtered
      .filter((c) => c.managerId === collab.id)
      .sort(sortFn)
      .forEach((child) => visit(child, depth + 1));
  }

  const roots = filtered.filter((c) => !c.managerId || !byId.has(c.managerId));
  roots.sort(sortFn).forEach((root) => visit(root, 0));

  return result;
}

/**
 * Dates par défaut d'un nouveau challenge : de demain à J+14.
 *
 * Elles étaient vides alors qu'elles sont OBLIGATOIRES pour enregistrer : deux
 * champs à remplir avant même de pouvoir tester l'idée. Deux semaines est la
 * durée d'un sprint d'animation — assez court pour créer l'urgence, assez long
 * pour que tout le monde ait le temps de jouer. Le manager peut toujours changer.
 */
function defaultChallengeDates(): { startDate: string; endDate: string } {
  const day = 24 * 60 * 60 * 1000;
  const iso = (d: Date) => d.toISOString().split("T")[0];
  // Demain plutôt qu'aujourd'hui : laisse le temps de prévenir l'équipe.
  return { startDate: iso(new Date(Date.now() + day)), endDate: iso(new Date(Date.now() + 15 * day)) };
}


/**
 * Un abonnement « ouvre-t-il l'accès » ? L'écran lisait `status === "live" || "trial"` à QUATRE
 * endroits (carte, vue mobile, vue desktop, cartes legacy) : un client résilié basculait alors
 * sur l'ancienne vue « pool de licences », comme s'il n'avait jamais eu d'abonnement.
 * La décision vient du serveur ; ce repli sert aux réponses qui ne portent pas encore `access`.
 */
const abonnementOuvert = (s: any): boolean =>
  s?.access ? !!s.access.isOpen : isSubscriptionOpen(s);

/**
 * Cartes d'abonnement à afficher : tous ceux qui ouvrent encore l'accès ; s'il n'y en a aucun,
 * le plus récent, pour dire « Abonnement terminé le … » au lieu de faire disparaître la carte.
 *
 * ⚠️ Ne PAS tout afficher : l'API ne filtre aucun statut, un compte ayant eu plusieurs
 * abonnements (dont des créations échouées) verrait des cartes mortes empilées.
 */
const cartesAbonnement = (subs: any[] | undefined): any[] => {
  const lignes = (subs ?? []).filter((s) => s?.status !== "created" && s?.status !== "creation_failed");
  const ouverts = lignes.filter(abonnementOuvert);
  if (ouverts.length > 0) return ouverts;
  const parDate = [...lignes].sort(
    (a, b) => new Date(b.activatedAt ?? 0).getTime() - new Date(a.activatedAt ?? 0).getTime(),
  );
  return parDate.slice(0, 1);
};

export function AccountPage({ user, onUpdate, adminAccountId, onBackToAdmin, onNavigateToChallenge, onOpenCompanyNotifications, variant = "full", embedTab, initialTab }: AccountPageProps) {
  const isProfileOnly = variant === "profile";
  const isEmbed = !!embedTab;
  const { t, i18n } = useTranslation();
  const isFr = i18n.language === "fr";
  const isAdminMode = !!adminAccountId;
  const accountId = adminAccountId; // undefined for normal mode, accountId for admin mode
  const [activeTab, setActiveTab] = useState(() => {
    if (embedTab) return embedTab; // mode focalisé (embarqué dans l'espace encadrant)
    if (initialTab && !isProfileOnly) return initialTab; // arrivée ciblée (alerte du cockpit)
    // Retour d'un achat (Zoho hosted page) → ?tab=licences ouvre directement « Licences et Packs ».
    if (typeof window !== "undefined") {
      const tab = new URLSearchParams(window.location.search).get("tab");
      if (tab === "licences") return "licences";
    }
    return isProfileOnly ? "profile" : isAdminMode ? "company" : "profile";
  });
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [isEditingCompany, setIsEditingCompany] = useState(false);
  const [companyDataBackup, setCompanyDataBackup] = useState<any>(null);

  const [editingTargetId, setEditingTargetId] = useState<string | null>(null);
  // Secteur "Autre" en édition : un seul target édité à la fois (editingTargetId),
  // donc un state simple suffit. Initialisé à l'ouverture de l'édition (bouton Modifier).
  const [customIndustryEdit, setCustomIndustryEdit] = useState("");
  // Mémorise que l'option "Autre" est cochée même tant que la valeur libre n'est pas
  // encore saisie. Sans ça, la case se décocherait au re-render (target.industry ne
  // contient pas encore de valeur custom) et le champ texte n'apparaîtrait jamais.
  const [customIndustryMode, setCustomIndustryMode] = useState(false);
  const [showSimWizard, setShowSimWizard] = useState(false);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const [isPhotoDialogOpen, setIsPhotoDialogOpen] = useState(false);

  // Pseudo + avatar (Challenges V2)
  const [isPseudoModalOpen, setIsPseudoModalOpen] = useState(false);

  // Mes badges (Challenges V2)
  const [myBadges, setMyBadges] = useState<any[]>([]);
  const [badgesLoaded, setBadgesLoaded] = useState(false);
  useEffect(() => {
    if (!isAdminMode) {
      badgesApi.getMyBadges().then((b: any[]) => { setMyBadges(b || []); setBadgesLoaded(true); }).catch(() => setBadgesLoaded(true));
    }
  }, [isAdminMode]);

  // Logo entreprise dialog
  const [isLogoDialogOpen, setIsLogoDialogOpen] = useState(false);
  const [logoUrlInput, setLogoUrlInput] = useState("");
  const [tempLogo, setTempLogo] = useState<string | null>(null);
  const logoFileInputRef = React.useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState({
    civility: user.civility || "M.",
    firstName: user.firstName,
    lastName: user.lastName,
    email: user.email,
    phoneNumber: user.phoneNumber || "",
    profilePicture: user.profilePicture || "",
    linkedinUrl: user.linkedinUrl || "",
    experienceLevel: (user as any).experienceLevel || "confirme",
    // Langue d'entraînement : `null` = suit la langue d'affichage.
    spokenLanguage: ((user as any).spokenLanguage ?? null) as string | null,
  });

  // Packs one-shot du compte (simulateurs choisis + crédits) — pour l'affichage enrichi de l'onglet Licences et Packs.
  const [oneShotPacks, setOneShotPacks] = useState<OneShotPack[]>([]);
  const [oneShotSims, setOneShotSims] = useState<PackSimulatorOption[]>([]);
  const [configurePacksOpen, setConfigurePacksOpen] = useState(false);

  // Arrivée depuis le message de blocage « abonnement terminé » (?buy=1) : on ouvre la modale
  // d'achat d'abonnement — le même parcours qu'un client actif, qui réutilise son client Zoho.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("buy") !== "1") return;
    // Jamais une fenêtre d'achat que le serveur refuserait (même règle que `peutAcheter`).
    if (isAdminMode || canBuyLicences(user.profileType)) setShowBuyModal(true);
    params.delete("buy");
    const reste = params.toString();
    window.history.replaceState({}, "", `${window.location.pathname}${reste ? `?${reste}` : ""}`);
  }, []);

  // Lot D — Retour d'un achat de pack (?justPurchased=1) : le webhook Zoho crédite le pack de
  // façon asynchrone → on poll quelques secondes puis on ouvre l'écran de choix des simulateurs.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("justPurchased") !== "1") return;
    // Nettoyer l'URL (évite de rouvrir la modale à chaque refresh).
    params.delete("justPurchased");
    const q = params.toString();
    window.history.replaceState({}, "", window.location.pathname + (q ? `?${q}` : ""));
    let tries = 0;
    const timer = setInterval(async () => {
      tries++;
      try {
        const d = await packsApi.getOneShot(accountId);
        setOneShotPacks(d.packs || []);
        setOneShotSims(d.availableSimulators || []);
        if ((d.packs || []).some((p) => p.needsConfig)) {
          setConfigurePacksOpen(true);
          clearInterval(timer);
        }
      } catch { /* réseau : on retente */ }
      if (tries >= 6) clearInterval(timer); // ~18 s max (webhook lent)
    }, 3000);
    return () => clearInterval(timer);
  }, []);
  const [companyData, setCompanyData] = useState({
    name: "",
    siret: "",
    website: "",
    address: "",
    postalCode: "",
    city: "",
    country: "",
    industry: "",
    description: "",
    descriptionFr: "",
    descriptionEn: "",
    tva: "",
    linkedinUrl: "",
    logoUrl: "",
    salesModels: [] as string[],
    // Registre des messages des managers du compte (décision 6.2) : choisi par l'ORG.
    messageRegister: "tu" as "tu" | "vous",
  });

  const industryOptions = INDUSTRY_OPTIONS;
  const companySizeOptions = COMPANY_SIZE_OPTIONS;

  const [targets, setTargets] = useState<TargetProspect[]>([]);
  // « Utilisé par » (Pilot cible, chantier E) : campagnes et parcours qui s'appuient sur chaque cas.
  const [targetUsage, setTargetUsage] = useState<Record<string, { campaigns: { id: string; title: string; status: string }[]; paths: { id: string; title: string; status: string }[] }>>({});
  useEffect(() => { accountApi.getTargetsUsage(accountId).then((r) => setTargetUsage(r.usage ?? {})).catch(() => setTargetUsage({})); }, [accountId, targets.length]);
  // Onglets « Cas d'équipe » (publics, créés par ORG/COO/manager), « Cas personnels » (privés,
  // créés par Coach Debrief ou Coach Prepa) et « Cas génériques » (banque Kestio, `kind = 'socle'`,
  // communs à tous les clients — ils se rangeaient dans « Cas d'équipe »). Par défaut : équipe.
  const [caseTab, setCaseTab] = useState<"team" | "personal" | "generic">("team");
  // Offres d'un cas : celles du compte, sinon celles que l'API joint au cas (offre Kestio d'un cas
  // générique, introuvable dans `accountOffers` — le cadre « Offre » restait vide).
  const offresDuCas = (target: TargetProspect): any[] =>
    (target.offerIds || [])
      .map((oid) => accountOffers.find((o) => o.id === oid) ?? (target.offers || []).find((o: any) => o.id === oid))
      .filter(Boolean);
  const filteredTargets = targets.filter((t) =>
    caseTab === "generic" ? t.kind === "socle"
      : t.kind === "socle" ? false
      : caseTab === "team" ? t.visibility !== "private" : t.visibility === "private",
  );
  const [accountOffers, setAccountOffers] = useState<any[]>([]);
  const [accountPersonae, setAccountPersonae] = useState<any[]>([]);
  const [accountContexts, setAccountContexts] = useState<any[]>([]);
  const [deletingTarget, setDeletingTarget] = useState<TargetProspect | null>(null);
  const [deletingPersona, setDeletingPersona] = useState<{ targetId: string; persona: PersonaLocal } | null>(null);
  const [revokingLicence, setRevokingLicence] = useState<{ collaboratorId: string; licenceType: LicenceType; collabName: string; licenceName: string } | null>(null);

  // Subscription-based licences
  const [subscriptionData, setSubscriptionData] = useState<any>(null);
  const [assignSeatModal, setAssignSeatModal] = useState<{ subscriptionId: string; planTier: string; maxSimulators: number } | null>(null);
  const [assignSeatUserId, setAssignSeatUserId] = useState<string>("");
  const [assignSeatSimulatorIds, setAssignSeatSimulatorIds] = useState<string[]>([]);
  const [showBuyModal, setShowBuyModal] = useState(false);
  const [buyPlan, setBuyPlan] = useState(getSubscriptionPlanCode("focus", "yearly"));
  const [buyInterval, setBuyInterval] = useState<"monthly" | "yearly">("yearly");
  const [buyQuantity, setBuyQuantity] = useState(1);
  const [buyLoading, setBuyLoading] = useState(false);

  // Users tab state
  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  // Snapshot des valeurs d'origine d'un collab existant en édition (utilisé par
  // handleCancelUser pour restaurer si l'utilisateur clique "Annuler").
  // null pour un nouveau draft (cas géré différemment dans handleCancelUser).
  const [editSnapshot, setEditSnapshot] = useState<Collaborator | null>(null);
  // Fiche collaborateur ouverte (popup). L'édition globale réutilise `editingUserId`.
  const [ficheUserId, setFicheUserId] = useState<string | null>(null);

  // Snapshot à l'ouverture de l'édition d'un collab existant. Volontairement
  // pas de `collaborators` dans les deps : on ne veut snapshoter qu'au moment
  // où on entre en édition, pas à chaque modif locale dans le formulaire.
  React.useEffect(() => {
    if (editingUserId && !newCollabIds.has(editingUserId)) {
      const collab = collaborators.find((c) => c.id === editingUserId);
      if (collab) setEditSnapshot({ ...collab });
    } else {
      setEditSnapshot(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingUserId]);
  const [expandedUserId, setExpandedUserId] = useState<string | null>(null);
  const [deleteUserId, setDeleteUserId] = useState<string | null>(null);
  const [selectedAgent, setSelectedAgent] = useState<LicenceType | null>(null);
  const [mobileUserFilter, setMobileUserFilter] = useState<"active" | "inactive">("active");
  const [showMobileAddPicker, setShowMobileAddPicker] = useState(false);
  const [desktopAgentFilter, setDesktopAgentFilter] = useState<LicenceType | null>(null);
  const [desktopStatusFilter, setDesktopStatusFilter] = useState<"all" | "active" | "disabled">("all");
  const [desktopSortBy, setDesktopSortBy] = useState<"lastName" | "firstName">("lastName");
  const [desktopSortDir, setDesktopSortDir] = useState<"asc" | "desc">("asc");
  const [createWizardStep, setCreateWizardStep] = useState<1 | 2 | 3>(1);

  const [collaborators, setCollaborators] = useState<Collaborator[]>([]);
  const [newCollabIds, setNewCollabIds] = useState<Set<string>>(new Set());
  // Map licenceId (UUID) → licence name (PROSPECTOR, etc.)
  const [licenceIdToName, setLicenceIdToName] = useState<Record<string, LicenceType>>({});
  const [licenceNameToId, setLicenceNameToId] = useState<Record<string, string>>({});
  const [licencePoolConfig, setLicencePoolConfig] = useState<{ type: LicenceType; total: number; label: string; color: string; code: string; tier?: string | null; maxSimulators?: number | null; isOneShot?: boolean }[]>([]);
  // Simulateurs choisis pour chaque licence d'abonnement Starter / Focus attribuée depuis le pool :
  // userId → licence → identifiants (`user_licences.simulator_ids`, `null` = pas encore choisis).
  const [licenceSimulatorsByUser, setLicenceSimulatorsByUser] = useState<Record<string, Record<LicenceType, string[] | null>>>({});
  // Choix fait AVANT la création d'un collaborateur (enregistré avec sa licence à la création).
  const [pendingLicenceSimulators, setPendingLicenceSimulators] = useState<Record<string, Record<LicenceType, string[]>>>({});
  // Simulateurs proposables (chargés à la demande) + fenêtre de choix ouverte.
  const [selectableSimulators, setSelectableSimulators] = useState<SelectableSimulator[] | null>(null);
  const [selectableSimulatorsError, setSelectableSimulatorsError] = useState<string | null>(null);
  const [simulatorChoice, setSimulatorChoice] = useState<{ collaboratorId: string; licenceType: LicenceType; mode: "pending" | "assign" | "edit"; max: number; initialIds: string[] } | null>(null);
  // Licences détenues par des personnes ABSENTES de la liste (un MNG ne voit que son équipe) :
  // sans elles, « X disponible(s) » comptait large et le serveur refusait au clic.
  const [licenceUsageOutside, setLicenceUsageOutside] = useState<Record<string, number>>({});

  // Masquer la licence ADMIN pour les utilisateurs non-internes KESTIO
  const isKestioInternal = user.profileType === "ADM" || user.profileType === "COO";
  const isAdminLicence = (lp: { code: string; label: string }) =>
    lp.code.toUpperCase() === "ADMIN" || lp.label.toUpperCase() === "ADMIN";
  const visibleLicencePoolConfig = isKestioInternal
    ? licencePoolConfig
    : licencePoolConfig.filter((lp) => !isAdminLicence(lp));

  const licencePool: LicencePool[] = visibleLicencePoolConfig.map((lp) => ({
    ...lp,
    used: (licenceUsageOutside[lp.type] || 0) + collaborators.reduce(
      (count, c) => count + (c.licences.includes(lp.type) ? 1 : 0),
      0
    ),
  }));

  // Rachat direct (onglet Licences) : packs « Acheter à nouveau » / licences grant
  // « Ajouter des licences » → page de paiement Zoho du même plan, client reconnu
  // via customer_id (pas de doublon Zoho). cf. spec packs §5.4.
  const [buyAgainQty, setBuyAgainQty] = useState<Record<string, number>>({});
  const [buyAgainLoading, setBuyAgainLoading] = useState<string | null>(null);
  const handleBuyAgain = async (lp: { code: string; zohoPlanCode?: string | null }) => {
    if (!lp.zohoPlanCode) return;
    setBuyAgainLoading(lp.code);
    try {
      const qty = Math.max(1, buyAgainQty[lp.code] || 1);
      // Carte enregistrée → débit direct sans re-saisie ; sinon redirection paiement.
      const result = await accountApi.buyAgain(lp.zohoPlanCode, qty);
      if (result.status === "paid") {
        alert(t("account.buyAgainPaid"));
        // Recharge + ouvre l'écran de choix des simulateurs du nouveau pack (Lot D).
        window.location.href = `${window.location.pathname}?tab=licences&justPurchased=1`;
      } else if (result.status === "redirect" && result.url) {
        window.location.href = result.url;
      } else {
        alert(t("account.buyAgainPending"));
      }
    } catch (e: any) {
      alert(e?.message || "Erreur lors de l'achat.");
    } finally {
      setBuyAgainLoading(null);
    }
  };

  const isOrganisateur = isAdminMode || user.profileType === "ORG" || user.profileType === "ADM";
  // Boutons d'ACHAT (réabonnement, ajout de licences, rachat d'un pack, autres formules) : la règle
  // partagée avec le serveur (`canBuyLicences` : ORG, ADM, GES — décision Valérie, Q3 du
  // 29-30/09/2026), jamais `isOrganisateur`, qui laissait le gestionnaire financier sans bouton.
  // En mode admin « gérer ce compte », l'ADM achète pour le compte.
  const peutAcheter = isAdminMode || canBuyLicences(user.profileType);
  const isManager = user.profileType === "MNG";
  // Droits sur les collaborateurs : la MÊME matrice que le serveur (`collaborator-permissions.ts`).
  // Les drapeaux écrits ici à la main montraient à RH/GES des boutons qui répondaient 403.
  // En mode admin « gérer ce compte », l'ADM agit avec ses propres droits.
  const collabActorProfile = isAdminMode ? "ADM" : user.profileType;
  const canManageUsers = canCollaborator(collabActorProfile, "view");
  // Onglet « Licences et Packs » (achat, configuration des packs) : distinct de l'ATTRIBUTION
  // d'une licence à un collaborateur, ouverte au MNG depuis la fiche.
  // Onglet Licences : règle partagée avec le Plan (`hasLicencesTab`) ; le mode admin l'ouvre aussi.
  const canManageLicences = isAdminMode || hasLicencesTab(user.profileType);
  const canCreateUsers = canCollaborator(collabActorProfile, "create");
  const canViewCompany = isOrganisateur;
  const canManageTargets = isOrganisateur || isManager;
  const canManageChallenges = isOrganisateur || isManager;

  const [newTargetIds, setNewTargetIds] = useState<Set<string>>(new Set());

  // Categories state
  const [categoryList, setCategoryList] = useState<Category[]>([]);
  const categoryMap = React.useMemo(() => {
    const map: Record<string, Category> = {};
    for (const cat of categoryList) map[cat.name] = cat;
    return map;
  }, [categoryList]);
  const getCategoryLabel = (name: string) => {
    const cat = categoryMap[name];
    if (!cat) return name;
    return pickFrEn(cat.labelFr, cat.labelEn, i18n.language);
  };
  const getCategoryColor = (name: string) => categoryMap[name]?.color || "#e5e7eb";
  const getCategoryTextColor = (name: string) => categoryMap[name]?.textColor || "#374151";

  // Challenges state
  const [challengesList, setChallengesList] = useState<Challenge[]>([]);
  const [simulatorsList, setSimulatorsList] = useState<SimulatorType[]>([]);
  const [isChallengeDialogOpen, setIsChallengeDialogOpen] = useState(false);
  const [editingChallenge, setEditingChallenge] = useState<Challenge | null>(null);
  const [challengeForm, setChallengeForm] = useState({
    title: "",
    description: "",
    simulatorId: "",
    ...defaultChallengeDates(),
    minSessions: 1,
    maxSessions: 10,
    orangeThresholdMinutes: "",
    redThresholdMinutes: "",
    primaryObjective: "" as string,
    secondaryObjective: "" as string,
    challengeType: "mandatory" as string,
    difficulty: "" as string,
    teamAssignmentMode: "" as string,
    teams: [] as Array<{ id?: string; name: string; color: string }>,
    // V2 inter
    scope: "intra" as string,
    fictiveCompanyName: "",
    fictiveCompanyDescription: "",
    badgeId: "" as string,
    giftName: "",
    giftPhotoUrl: "",
    giftDescription: "",
    offers: [] as Array<{ name: string; description: string }>,
    personae: [] as Array<Record<string, string>>,
    allowedAccountIds: [] as string[],
  });
  const [savingChallenge, setSavingChallenge] = useState(false);
  const [confirmAction, setConfirmAction] = useState<{ type: "activate" | "end" | "delete" | "pause" | "resume"; challenge: Challenge } | null>(null);
  /** Décision 3.6 : ni RH ni GES proposés comme participants ; ceux déjà prévus (`keep`) restent affichés. */
  const proposables = (list: any[], keep: Iterable<string> = []) => { const k = new Set(keep); return (list || []).filter((u: any) => canBeProposedAsParticipant(u.profileType) || k.has(u.id)); };

  const loadChallenges = () => {
    challengesApi.getChallenges(accountId).then((list: Challenge[]) => {
      setChallengesList(list);
    }).catch((err: any) => console.error("Erreur chargement challenges", err));
  };

  const resetChallengeForm = () => {
    setChallengeForm({
      title: "",
      description: "",
      simulatorId: "",
      ...defaultChallengeDates(),
      minSessions: 1,
      maxSessions: 10,
      orangeThresholdMinutes: "",
      redThresholdMinutes: "",
      primaryObjective: "",
      secondaryObjective: "",
      challengeType: "mandatory",
      difficulty: "",
      teamAssignmentMode: "",
      teams: [],
      scope: "intra",
      fictiveCompanyName: "",
      fictiveCompanyDescription: "",
      badgeId: "",
      giftName: "",
      giftPhotoUrl: "",
      giftDescription: "",
      offers: [],
      personae: [],
      allowedAccountIds: [],
    });
    setEditingChallenge(null);
    setDraftCollaborators([]);
    setDraftTeamAssignments({});
    setDraftSelectedParticipants(new Set());
  };

  // (Code mort retiré au lot 7, 30/09/2026 : la prop `challengeDraft` et `draftCampaignId` — plus
  //  aucun appelant ne les fournissait ; la création pré-remplie passe par `ChallengesView` de Pilot.)

  // Hydrate le state challengeForm depuis un challenge serveur (full) ou résumé (ch).
  // Garantit que tous les champs V2 sont initialisés (évite les undefined qui font planter
  // les .filter/.map/.length dans le formulaire).
  const hydrateChallengeForm = (data: any, teamsOverride?: Array<{ id?: string; name: string; color: string }>) => ({
    title: data.title || "",
    description: data.description || "",
    simulatorId: data.simulatorId || "",
    startDate: data.startDate ? new Date(data.startDate).toISOString().slice(0, 10) : "",
    endDate: data.endDate ? new Date(data.endDate).toISOString().slice(0, 10) : "",
    minSessions: data.minSessions ?? 1,
    maxSessions: data.maxSessions ?? 10,
    orangeThresholdMinutes: data.orangeThresholdMinutes?.toString() || "",
    redThresholdMinutes: data.redThresholdMinutes?.toString() || "",
    primaryObjective: data.primaryObjective || "",
    secondaryObjective: data.secondaryObjective || "",
    challengeType: data.challengeType || "mandatory",
    difficulty: data.difficulty || "",
    teamAssignmentMode: data.teamAssignmentMode || "",
    teams: teamsOverride ?? (data.teams || []).map((t: any) => ({ id: t.id, name: t.name, color: t.color || TEAM_PALETTE[0] })),
    scope: data.scope || "intra",
    fictiveCompanyName: data.fictiveCompanyName || "",
    fictiveCompanyDescription: data.fictiveCompanyDescription || "",
    badgeId: data.badgeId || "",
    giftName: data.giftName || "",
    giftPhotoUrl: data.giftPhotoUrl || "",
    giftDescription: data.giftDescription || "",
    offers: data.offers || [],
    personae: data.personae || [],
    allowedAccountIds: data.allowedAccountIds || [],
  });

  // Team management state
  const [isTeamManagementOpen, setIsTeamManagementOpen] = useState(false);
  const [managingChallenge, setManagingChallenge] = useState<Challenge | null>(null);
  const [managingTeams, setManagingTeams] = useState<ChallengeTeam[]>([]);
  const [managingParticipants, setManagingParticipants] = useState<ChallengeParticipant[]>([]);
  const [teamAssignments, setTeamAssignments] = useState<Record<string, string | null>>({});
  const [savingAssignments, setSavingAssignments] = useState(false);
  const [showAddParticipants, setShowAddParticipants] = useState(false);
  const [availableCollabs, setAvailableCollabs] = useState<Array<{ id: string; firstName: string; lastName: string }>>([]);
  const [selectedNewParticipants, setSelectedNewParticipants] = useState<Set<string>>(new Set());
  const [addingParticipants, setAddingParticipants] = useState(false);
  const [newTeamName, setNewTeamName] = useState("");
  const [showNewTeamInput, setShowNewTeamInput] = useState(false);
  const [creatingTeam, setCreatingTeam] = useState(false);

  // Draft pre-assignments: userId → team index (local, saved with challenge data)
  const [draftCollaborators, setDraftCollaborators] = useState<Array<{ id: string; firstName: string; lastName: string; email: string; status?: string; profileType?: string }>>([]);
  const [draftTeamAssignments, setDraftTeamAssignments] = useState<Record<string, number | null>>({});
  const [loadingDraftCollabs, setLoadingDraftCollabs] = useState(false);
  // Participants without teams (direct selection)
  const [draftSelectedParticipants, setDraftSelectedParticipants] = useState<Set<string>>(new Set());

  // Refresh challenge data in team management modal
  const refreshManagingChallenge = async (challengeId: string) => {
    try {
      const full = await challengesApi.getChallenge(challengeId, accountId);
      setManagingChallenge(full);
      setManagingTeams(full.teams || []);
      setManagingParticipants(full.participants || []);
      const assignments: Record<string, string | null> = {};
      for (const p of (full.participants || [])) {
        assignments[p.id] = p.teamId || null;
      }
      setTeamAssignments(assignments);
    } catch {}
  };

  const TEAM_PALETTE = ["#F49390", "#988EF2", "#72B76E", "#F6C847", "#3B82F6", "#F99002", "#A6D3F9", "#384872"];

  // Load company and targets data from backend
  useEffect(() => {
    if (!canManageUsers && !isOrganisateur) return;
    accountApi.getCompany(accountId).then((company) => {
      if (company) {
        const data = {
          name: company.name || "",
          siret: company.siret || "",
          website: company.website || "",
          address: company.address || "",
          postalCode: company.postalCode || company.postal_code || "",
          city: company.city || "",
          country: company.country || "",
          industry: company.industry || "",
          // ⚠️ Préserver les variantes i18n (piège mapping front) : sans elles, pickCompanyDescription
          // retombe sur la colonne legacy `description` et affiche vide dès que la desc ne vit que dans *Fr/*En.
          description: company.description || company.descriptionFr || company.description_fr || company.descriptionEn || company.description_en || "",
          descriptionFr: company.descriptionFr || company.description_fr || "",
          descriptionEn: company.descriptionEn || company.description_en || "",
          tva: company.tva || "",
          linkedinUrl: company.linkedinUrl || company.linkedin_url || "",
          logoUrl: company.logoUrl || company.logo_url || "",
          // Comment l'entreprise vend — pilote les cas Kestio qu'on lui propose.
          salesModels: company.salesModels || company.sales_models || [],
          messageRegister: ((company.messageRegister || company.message_register) === "vous" ? "vous" : "tu") as "tu" | "vous",
        };
        setCompanyData(data);
      }
    }).catch((err) => console.error(t("api.loadCompanyError"), err));

    accountApi.getTargets(accountId, { includeArchived: true }).then((list: any[]) => {
      setTargets(list.map((t: any) => ({
        id: t.id,
        caseType: t.caseType || t.case_type || "B2B",
        simulatorId: t.simulatorId || t.simulator_id || null,
        contextId: t.contextId || t.context_id || null,
        companySize: t.companySize || t.company_size || "",
        industry: t.industry || "",
        offerIds: t.offerIds || t.offer_ids || [],
        // Champs nécessaires aux onglets "Cas d'équipe" / "Cas personnels" + date + titre libre
        createdAt: t.createdAt || t.created_at || null,
        visibility: t.visibility || "public",
        sourceRealCallSessionId: t.sourceRealCallSessionId || t.source_real_call_session_id || null,
        displayName: t.displayName || t.display_name || null,
        createdBy: t.createdBy ?? t.created_by ?? null,
        ownerUserId: t.ownerUserId ?? t.owner_user_id ?? null,
        archivedAt: t.archivedAt ?? t.archived_at ?? null,
        kind: t.kind ?? null,
        offers: t.offers || [],
        userCompanyNameFr: t.userCompanyNameFr ?? null,
        userCompanyNameEn: t.userCompanyNameEn ?? null,
        userCompanyProfileFr: t.userCompanyProfileFr ?? null,
        userCompanyProfileEn: t.userCompanyProfileEn ?? null,
        personae: (t.personae || []).map((p: any) => ({
          // Spread d'abord pour PRÉSERVER toutes les variantes i18n (functionFr/En,
          // painPointsFr/En, decisionMakerProfileFr/En, …) que les helpers de lecture
          // (getPersonaLabel, pickPersonaField, pickDecisionMaker) attendent. Sans ce
          // spread, le state ne gardait que les champs legacy FR → affichage FR en EN.
          ...p,
          id: p.id,
          targetId: t.id, // M<->N : contexte = ce cas (personae.target_id legacy ignoré)
          function: p.function || "",
          decisionMakerProfile: p.decisionMakerProfile || p.decision_maker_profile || "",
          painPoints: p.painPoints || p.pain_points || "",
          category: p.category || "sales",
          personaType: p.personaType || p.persona_type || "B2B",
          csp: p.csp || "",
          ageRange: p.ageRange || p.age_range || "",
          gender: p.gender || "",
          purchaseCriteria: p.purchaseCriteria || p.purchase_criteria || "",
          purchasePhase: p.purchasePhase || p.purchase_phase || "",
          productRelationship: p.productRelationship || p.product_relationship || "", contactReason: p.contactReason || p.contact_reason || "", expressionStyle: p.expressionStyle || p.expression_style || "", mainExpectation: p.mainExpectation || p.main_expectation || "",
        })),
      })));
    }).catch((err) => console.error(t("api.loadTargetsError"), err));

    accountApi.getOffers(accountId).then((data) => setAccountOffers(data || [])).catch(() => {});
    accountApi.getAccountPersonae(accountId).then((data) => setAccountPersonae(data || [])).catch(() => {});
    accountApi.getContexts({}, accountId).then((data) => setAccountContexts(data || [])).catch(() => {});

    categoriesApi.getAll().then((cats) => {
      setCategoryList(cats as Category[]);
    }).catch((err) => console.error("Erreur chargement catégories", err));

    // Load challenges + simulators for challenge form
    loadChallenges();
    authApi.getAgents().then((agents: SimulatorType[]) => {
      setSimulatorsList(agents);
    }).catch((err: any) => console.error("Erreur chargement simulateurs", err));

    // Load subscription data (Zoho Billing)
    accountApi.getSubscriptions(accountId).then((data: any) => {
      setSubscriptionData(data);
    }).catch((err: any) => console.error("Erreur chargement abonnements", err));

    // Load licences, collaborators, user_licences and quotas
    // Packs one-shot (simulateurs choisis + crédits) pour l'affichage par pack.
    // (getOneShot cible l'utilisateur de session / impersonation — pas le mode admin « gérer ce compte ».)
    packsApi.getOneShot(accountId).then((data) => {
      setOneShotPacks(data.packs || []);
      setOneShotSims(data.availableSimulators || []);
    }).catch(() => { setOneShotPacks([]); setOneShotSims([]); });

    Promise.all([
      accountApi.getLicences(accountId),
      accountApi.getCollaborators(accountId),
      accountApi.getUserLicences(accountId),
      accountApi.getLicenceQuotas(accountId),
    ]).then(([licenceList, collabList, ulList, quotaMap]: [any[], any[], any[], Record<string, number>]) => {
      // Build licence maps
      const idToName: Record<string, LicenceType> = {};
      const nameToId: Record<string, string> = {};
      const poolConfig: { type: LicenceType; total: number; label: string; color: string; code: string }[] = [];
      for (const lic of licenceList) {
        const licCode = lic.code || lic.name;
        idToName[lic.id] = licCode as LicenceType;
        nameToId[licCode] = lic.id;
        poolConfig.push({
          type: licCode,
          total: quotaMap[lic.id] || 0,
          label: lic.name,
          color: lic.color || "#6B7280",
          code: licCode,
          priceHt: lic.priceHt || lic.price_ht || null,
          maxSimulators: lic.maxSimulators || lic.max_simulators || null,
          billingInterval: lic.billingInterval || lic.billing_interval || null,
          tier: lic.tier || null,
          zohoPlanCode: lic.zohoPlanCode || lic.zoho_plan_code || null,
          isOneShot: !!(lic.isOneShot ?? lic.is_one_shot),
        });
      }
      setLicenceIdToName(idToName);
      setLicenceNameToId(nameToId);
      setLicencePoolConfig(poolConfig);

      // Build user licence map: userId → LicenceType[]
      const userLicMap: Record<string, LicenceType[]> = {};
      const simsByUser: Record<string, Record<LicenceType, string[] | null>> = {};
      const visibleIds = new Set(collabList.map((c: any) => c.id));
      const outside: Record<string, number> = {};
      for (const ul of ulList) {
        const userId = ul.userId || ul.user_id;
        const licenceId = ul.licenceId || ul.licence_id;
        const licName = idToName[licenceId];
        if (licName) {
          if (!visibleIds.has(userId)) { outside[licName] = (outside[licName] || 0) + 1; continue; }
          if (!userLicMap[userId]) userLicMap[userId] = [];
          userLicMap[userId].push(licName);
          if (!simsByUser[userId]) simsByUser[userId] = {};
          const sims = ul.simulatorIds ?? ul.simulator_ids ?? null;
          simsByUser[userId][licName] = Array.isArray(sims) && sims.length > 0 ? sims : null;
        }
      }
      setLicenceUsageOutside(outside);
      setLicenceSimulatorsByUser(simsByUser);

      // Build collaborators
      setCollaborators(collabList.map((c: any) => ({
        id: c.id,
        civility: c.civility || "M.",
        firstName: c.firstName || c.first_name || "",
        lastName: c.lastName || c.last_name || "",
        email: c.email || "",
        phoneNumber: c.phoneNumber || c.phone_number || "",
        profileType: c.profileType || c.profile_type || "BNF",
        status: c.status || "created",
        position: c.position || "",
        linkedinUrl: c.linkedinUrl || c.linkedin_url || "",
        profilePicture: c.profilePicture || c.profile_picture || "",
        licences: userLicMap[c.id] || [],
        managerId: c.managerId || c.manager_id || null,
        // Les DEUX langues doivent survivre au mapping : sans elles, l'écran d'édition
        // repartait de « Français » pour tout le monde et écrasait la valeur en base.
        language: c.language || c.display_language || "fr",
        spokenLanguage: c.spokenLanguage ?? c.spoken_language ?? null,
      })));
    }).catch((err) => console.error(t("api.loadCollaboratorsError"), err));
  }, [canManageUsers, accountId]);

  const showSaveMessage = (message: string) => {
    setSaveMessage(message);
    setTimeout(() => setSaveMessage(null), 3000);
  };

  const [isSaving, setIsSaving] = useState(false);

  // Resize colonnes table collaborateurs
  const [collabColWidths, setCollabColWidths] = useState<Record<string, number>>({});
  const collabResizingRef = React.useRef<{ col: string; startX: number; startWidth: number } | null>(null);

  const handleCollabResizeStart = (col: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const th = (e.target as HTMLElement).parentElement!;
    collabResizingRef.current = { col, startX: e.clientX, startWidth: th.offsetWidth };
    const onMouseMove = (ev: MouseEvent) => {
      if (!collabResizingRef.current) return;
      const newWidth = Math.max(60, collabResizingRef.current.startWidth + ev.clientX - collabResizingRef.current.startX);
      setCollabColWidths(prev => ({ ...prev, [collabResizingRef.current!.col]: newWidth }));
    };
    const onMouseUp = () => {
      collabResizingRef.current = null;
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  };

  const handleSaveProfile = async () => {
    setIsSaving(true);
    try {
      const updatedUser = await authApi.updateProfile(formData);
      onUpdate(updatedUser);
      setIsEditingProfile(false);
      showSaveMessage(t("common.success"));
    } catch (err: any) {
      showSaveMessage(err.message || t("api.updateError"));
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelProfile = () => {
    setFormData({
      civility: user.civility || "M.",
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      phoneNumber: user.phoneNumber || "",
      profilePicture: user.profilePicture || "",
      linkedinUrl: user.linkedinUrl || "",
      experienceLevel: (user as any).experienceLevel || "confirme",
      spokenLanguage: ((user as any).spokenLanguage ?? null) as string | null,
    });
    setIsEditingProfile(false);
  };

  const handleSaveTarget = async (id: string) => {
    const target = targets.find((t) => t.id === id);
    if (!target) return;

    setIsSaving(true);
    try {
      if (newTargetIds.has(id)) {
        // Create new target
        const created = await accountApi.createTarget({ companySize: target.companySize, industry: target.industry }, accountId);
        const mapped: TargetProspect = {
          id: created.id,
          companySize: created.companySize || created.company_size || "",
          industry: created.industry || "",
          personae: [],
          caseType: created.caseType || created.case_type || "B2B",
          offerIds: created.offerIds || created.offer_ids || [],
          // Le créateur en est propriétaire : sans ce champ, un MNG perdait le crayon de son
          // propre cas jusqu'au rechargement (`canTargetAction`).
          createdBy: created.createdBy ?? user.id,
        };
        setTargets(targets.map((t) => (t.id === id ? mapped : t)));
        setNewTargetIds((prev) => { const next = new Set(prev); next.delete(id); return next; });
      } else {
        // Update existing target
        const updated = await accountApi.updateTarget(id, {
          companySize: target.companySize,
          industry: target.industry,
          offerIds: target.offerIds,
          simulatorId: target.simulatorId || undefined,
          contextId: target.contextId || undefined,
          caseType: target.caseType,
          // null explicite pour permettre de "vider" le nom (retour au fallback companySize)
          displayName: target.displayName ?? null,
        }, accountId);
        const mapped: TargetProspect = {
          // Préserve les métadonnées (createdAt, visibility, sourceRealCallSessionId)
          // que l'update n'a pas changées et que la mapped écraserait sinon.
          ...target,
          id: updated.id,
          caseType: target.caseType,
          simulatorId: updated.simulatorId || updated.simulator_id || target.simulatorId || null,
          contextId: updated.contextId || updated.context_id || target.contextId || null,
          companySize: updated.companySize || updated.company_size || "",
          industry: updated.industry || "",
          offerIds: target.offerIds || [],
          personae: target.personae,
          displayName: updated.displayName ?? updated.display_name ?? target.displayName ?? null,
        };
        setTargets(targets.map((t) => (t.id === id ? mapped : t)));
      }
      setEditingTargetId(null);
      setCustomIndustryMode(false);
      setCustomIndustryEdit("");
      showSaveMessage(t("common.success"));
    } catch (err: any) {
      showSaveMessage(err.message || t("api.updateError"));
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelTarget = () => {
    // If canceling a new unsaved target, remove it
    if (editingTargetId && newTargetIds.has(editingTargetId)) {
      setTargets(targets.filter((t) => t.id !== editingTargetId));
      setNewTargetIds((prev) => { const next = new Set(prev); next.delete(editingTargetId); return next; });
    }
    setEditingTargetId(null);
    setCustomIndustryMode(false);
    setCustomIndustryEdit("");
  };


  // Logo handlers
  const handleLogoFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => setTempLogo(reader.result as string);
      reader.readAsDataURL(file);
    }
    e.target.value = "";
  };
  const handleLogoUrlSubmit = () => {
    if (logoUrlInput.trim()) { setTempLogo(logoUrlInput.trim()); setLogoUrlInput(""); }
  };
  const handleConfirmLogo = async () => {
    if (tempLogo) {
      const newData = { ...companyData, logoUrl: tempLogo };
      setCompanyData(newData);
      try {
        const updated = await accountApi.updateCompany({ logoUrl: tempLogo }, accountId);
        onUpdate({ ...user, companyLogoUrl: updated?.logoUrl || updated?.logo_url || tempLogo, companyName: updated?.name || companyData.name } as any);
        showSaveMessage(t("common.success"));
      } catch (err: any) { showSaveMessage(err.message || t("api.updateError")); }
    }
    setTempLogo(null); setLogoUrlInput(""); setIsLogoDialogOpen(false);
  };
  const handleCancelLogo = () => { setTempLogo(null); setLogoUrlInput(""); setIsLogoDialogOpen(false); };

  const removeTarget = async (id: string) => {
    if (!newTargetIds.has(id)) {
      // Show confirmation dialog for persisted targets
      const target = targets.find((t) => t.id === id);
      if (target) {
        setDeletingTarget(target);
        return;
      }
    }
    setNewTargetIds((prev) => { const next = new Set(prev); next.delete(id); return next; });
    setTargets(targets.filter((t) => t.id !== id));
  };

  // Désactiver / réactiver un cas (Q1 du 01/10/2026). Désactiver demande une confirmation
  // (deux boutons) ; réactiver est immédiat. Les refus du serveur (cas utilisé par une campagne,
  // un parcours, un tournoi ou un duel en cours ; cas d'un autre) s'affichent traduits.
  const [archivingTarget, setArchivingTarget] = useState<TargetProspect | null>(null);
  const setTargetArchived = async (target: TargetProspect, archive: boolean) => {
    try {
      const updated = await accountApi.setTargetArchived(target.id, archive, accountId);
      const archivedAt = updated?.archivedAt ?? updated?.archived_at ?? null;
      setTargets((prev) => prev.map((x) => (x.id === target.id ? { ...x, archivedAt } : x)));
      showSaveMessage(t(archive ? "account.archivedDone" : "account.unarchivedDone"));
    } catch (err: any) {
      showSaveMessage(err?.message || t(archive ? "account.archiveError" : "account.unarchiveError"));
    }
  };

  const confirmDeleteTarget = async () => {
    if (!deletingTarget) return;
    try {
      await accountApi.deleteTarget(deletingTarget.id, accountId);
      setTargets((prev) => prev.filter((t) => t.id !== deletingTarget.id));
    } catch (err: any) {
      showSaveMessage(err.message || t("api.deleteTargetError"));
    }
    setDeletingTarget(null);
  };

  const updateTarget = (id: string, field: keyof TargetProspect, value: any) => {
    setTargets(
      targets.map((t) => (t.id === id ? { ...t, [field]: value } : t))
    );
  };

  // Persona handlers
  const [editingPersonaId, setEditingPersonaId] = useState<string | null>(null);
  const [newPersonaIds, setNewPersonaIds] = useState<Set<string>>(new Set());
  const [expandedPersonaId, setExpandedPersonaId] = useState<string | null>(null);
  const [addPersonaMode, setAddPersonaMode] = useState<{ targetId: string; mode: "choose" | "select" | "manual" | "ai" } | null>(null);
  const [addOfferMode, setAddOfferMode] = useState<{ targetId: string; mode: "choose" | "select" | "manual" | "ai" } | null>(null);
  const [inlineOfferForm, setInlineOfferForm] = useState({ name: "", description: "" });

  const addPersona = (targetId: string) => {
    const newId = `new-${Date.now()}`;
    setTargets(targets.map((t) =>
      t.id === targetId
        ? { ...t, personae: [...t.personae, { id: newId, targetId, function: "", decisionMakerProfile: "", painPoints: "", category: categoryList[0]?.name || "sales", isNew: true }] }
        : t
    ));
    setNewPersonaIds((prev) => new Set(prev).add(newId));
    setEditingPersonaId(newId);
  };

  const handleSavePersona = async (targetId: string, personaId: string) => {
    const target = targets.find((t) => t.id === targetId);
    const persona = target?.personae.find((p) => p.id === personaId);
    if (!persona) return;

    const isCaseB2C = target?.caseType === "B2C";
    if (!isCaseB2C && !persona.function.trim()) {
      showSaveMessage(t("account.personaFunction") + " requis");
      return;
    }

    const personaSaveData: any = {
      function: persona.function || "-",
      decisionMakerProfile: persona.decisionMakerProfile || undefined,
      painPoints: persona.painPoints || undefined,
      category: persona.category,
      personaType: isCaseB2C ? "B2C" : "B2B",
      csp: persona.csp || undefined,
      ageRange: persona.ageRange || undefined,
      gender: persona.gender || undefined,
      purchaseCriteria: persona.purchaseCriteria || undefined,
      purchasePhase: persona.purchasePhase || undefined,
      productRelationship: persona.productRelationship || undefined,
    };

    setIsSaving(true);
    try {
      if (newPersonaIds.has(personaId)) {
        const created = await accountApi.createPersona(targetId, personaSaveData, accountId);
        setTargets(targets.map((t) =>
          t.id === targetId
            ? { ...t, personae: t.personae.map((p) => p.id === personaId ? { ...p, id: created.id, targetId, isNew: false } : p) }
            : t
        ));
        setNewPersonaIds((prev) => { const next = new Set(prev); next.delete(personaId); return next; });
      } else {
        await accountApi.updatePersona(targetId, personaId, personaSaveData, accountId);
      }
      setEditingPersonaId(null);
      showSaveMessage(t("common.success"));
    } catch (err: any) {
      showSaveMessage(err.message || t("api.updateError"));
    } finally {
      setIsSaving(false);
    }
  };

  // Sauvegarde d'un persona d'un cas depuis le formulaire persona UNIQUE partagé (PersonaForm).
  // Contrairement à handleSavePersona (ancien formulaire réduit qui persistait un sous-ensemble),
  // on transmet ICI TOUS les champs (Réalisme + Psychologie) issus du `form` complet.
  const saveCasePersona = async (targetId: string, personaId: string, form: any) => {
    const isCaseB2C = targets.find((t) => t.id === targetId)?.caseType === "B2C";
    const personaType = form.personaType || (isCaseB2C ? "B2C" : "B2B");
    const isCC = (form.category || "sales") === "customer_care";
    if (!isCaseB2C && !isCC && !(form.function || "").trim()) {
      showSaveMessage(t("account.personaFunction") + " requis");
      return;
    }
    const clean = (v: any) => (typeof v === "string" && v.trim() ? v.trim() : undefined);
    const personaSaveData: any = {
      function: clean(form.function) || "-",
      category: form.category,
      personaType,
      decisionMakerProfile: clean(form.decisionMakerProfile),
      painPoints: clean(form.painPoints),
      csp: clean(form.csp),
      ageRange: clean(form.ageRange),
      gender: clean(form.gender),
      purchaseCriteria: clean(form.purchaseCriteria),
      purchasePhase: clean(form.purchasePhase),
      productRelationship: clean(form.productRelationship),
      contactReason: clean(form.contactReason),
      expressionStyle: clean(form.expressionStyle),
      mainExpectation: clean(form.mainExpectation),
      personalMotivations: clean(form.personalMotivations),
      dailyIrritants: clean(form.dailyIrritants),
      typicalObjections: clean(form.typicalObjections),
      neverDisclose: clean(form.neverDisclose),
      personalityStyle: clean(form.personalityStyle),
      personalityMode: form.personalityMode === "fixed" ? "fixed" : "variable",
      fixedPersonalityStyles: Array.isArray(form.fixedPersonalityStyles) ? form.fixedPersonalityStyles : [],
      maturityLevel: clean(form.maturityLevel),
      internalPressure: clean(form.internalPressure),
    };
    setIsSaving(true);
    try {
      if (newPersonaIds.has(personaId)) {
        const created = await accountApi.createPersona(targetId, personaSaveData, accountId);
        setTargets((prev) => prev.map((t) =>
          t.id === targetId
            ? { ...t, personae: t.personae.map((p) => p.id === personaId ? { ...p, ...form, id: created.id, targetId, isNew: false } : p) }
            : t
        ));
        setNewPersonaIds((prev) => { const next = new Set(prev); next.delete(personaId); return next; });
      } else {
        await accountApi.updatePersona(targetId, personaId, personaSaveData, accountId);
        setTargets((prev) => prev.map((t) =>
          t.id === targetId
            ? { ...t, personae: t.personae.map((p) => p.id === personaId ? { ...p, ...form } : p) }
            : t
        ));
      }
      setEditingPersonaId(null);
      showSaveMessage(t("common.success"));
    } catch (err: any) {
      showSaveMessage(err.message || t("api.updateError"));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeletePersona = async (targetId: string, personaId: string) => {
    if (newPersonaIds.has(personaId)) {
      // New unsaved persona — just remove locally, no confirmation needed
      setNewPersonaIds((prev) => { const next = new Set(prev); next.delete(personaId); return next; });
      setTargets(targets.map((t) =>
        t.id === targetId
          ? { ...t, personae: t.personae.filter((p) => p.id !== personaId) }
          : t
      ));
      if (editingPersonaId === personaId) setEditingPersonaId(null);
      return;
    }
    // Persisted persona — show confirmation
    const target = targets.find((t) => t.id === targetId);
    const persona = target?.personae.find((p) => p.id === personaId);
    if (persona) {
      setDeletingPersona({ targetId, persona });
    }
  };

  const confirmDeletePersona = async () => {
    if (!deletingPersona) return;
    const { targetId, persona } = deletingPersona;
    try {
      await accountApi.deletePersona(targetId, persona.id, accountId);
      setTargets(targets.map((t) =>
        t.id === targetId
          ? { ...t, personae: t.personae.filter((p) => p.id !== persona.id) }
          : t
      ));
      if (editingPersonaId === persona.id) setEditingPersonaId(null);
    } catch (err: any) {
      showSaveMessage(err.message || t("api.deleteTargetError"));
    }
    setDeletingPersona(null);
  };

  const handleCancelPersona = (targetId: string, personaId: string) => {
    if (newPersonaIds.has(personaId)) {
      setNewPersonaIds((prev) => { const next = new Set(prev); next.delete(personaId); return next; });
      setTargets(targets.map((t) =>
        t.id === targetId
          ? { ...t, personae: t.personae.filter((p) => p.id !== personaId) }
          : t
      ));
    }
    setEditingPersonaId(null);
  };

  const updatePersonaField = (targetId: string, personaId: string, field: keyof PersonaLocal, value: string) => {
    setTargets(targets.map((t) =>
      t.id === targetId
        ? { ...t, personae: t.personae.map((p) => p.id === personaId ? { ...p, [field]: value } : p) }
        : t
    ));
  };

  // Collaborator handlers

  // Le wizard s'ouvre depuis DEUX endroits (le bouton « Ajouter » et la modale mobile par
  // licence). Chacun construisait son brouillon dans son coin, et celui de la modale avait
  // déjà oublié `language`, `profileType` et `status` : le champ existait à l'écran mais
  // partait vide. Un seul constructeur, donc.
  const makeCollaboratorDraft = (newId: string): Collaborator => ({
    id: newId,
    civility: "M.",
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    profileType: "",
    status: "created",
    position: "",
    licences: [],
    // Langue d'AFFICHAGE par défaut (email d'activation + interface).
    language: "fr",
    // Langue d'ENTRAÎNEMENT : `null` = « identique à la langue d'affichage », le cas courant.
    spokenLanguage: null,
    // MNG : pré-remplit le manager au créateur (modifiable dans son sous-arbre, cf. L2.9)
    managerId: isManager ? user.id : null,
  });

  const addCollaborator = () => {
    const newId = String(Date.now());
    setCollaborators([...collaborators, makeCollaboratorDraft(newId)]);
    setNewCollabIds((prev) => new Set(prev).add(newId));
    setEditingUserId(newId);
    setCreateWizardStep(1);
    setExpandedUserId(null);
  };

  const deactivateCollaborator = async (id: string) => {
    if (newCollabIds.has(id)) {
      // New unsaved collaborator: just remove from local state
      setNewCollabIds((prev) => { const next = new Set(prev); next.delete(id); return next; });
      setCollaborators((prev) => prev.filter((c) => c.id !== id));
      return;
    }
    try {
      const updated = await accountApi.deactivateCollaborator(id, accountId);
      setCollaborators((prev) => prev.map((c) => c.id === id ? { ...c, status: updated.status } : c));
      showSaveMessage(t("account.deactivate"));
    } catch (err: any) {
      showSaveMessage(err.message || t("api.deactivateCollaboratorError"));
    }
  };

  const reactivateCollaborator = async (id: string) => {
    try {
      const updated = await accountApi.reactivateCollaborator(id, accountId);
      setCollaborators((prev) => prev.map((c) => c.id === id ? { ...c, status: updated.status } : c));
      showSaveMessage(t("account.reactivate"));
      setReactivateUserId(null);
    } catch (err: any) {
      showSaveMessage(err.message || t("api.reactivateCollaboratorError"));
    }
  };

  const [reactivateUserId, setReactivateUserId] = useState<string | null>(null);
  const [resendingLinkId, setResendingLinkId] = useState<string | null>(null);

  const resendMagicLink = async (id: string) => {
    setResendingLinkId(id);
    try {
      await accountApi.resendMagicLink(id, accountId);
      showSaveMessage(t("auth.emailSent"));
    } catch (err: any) {
      showSaveMessage(err.message || t("api.resendLinkError"));
    } finally {
      setResendingLinkId(null);
    }
  };

  // Invitation (bouton « Inviter » d'une ligne, de la vue manager ou de la fiche) — un seul handler.
  const inviteCollaboratorNow = async (id: string) => {
    setResendingLinkId(id);
    try {
      const updated = await accountApi.inviteCollaborator(id, accountId);
      setCollaborators((prev) => prev.map((c) => c.id === id ? { ...c, status: updated.status || "invited" } : c));
      showSaveMessage(t("auth.emailSent"));
    } catch (err: any) {
      showSaveMessage(err.message || t("api.resendLinkError"));
    } finally {
      setResendingLinkId(null);
    }
  };

  /** Droits de l'utilisateur connecté sur UN collaborateur — même règle que le serveur. */
  const collabRightsFor = (collab: Collaborator) => {
    const can = (action: CollaboratorAction) => checkCollaboratorAction({
      actor: { id: user.id, profileType: collabActorProfile },
      target: { id: collab.id, profileType: collab.profileType },
      action,
      // Un MNG ne reçoit que son sous-arbre (lui compris) : tout autre que lui en fait partie.
      inMngSubtree: collab.id !== user.id,
    }) === null;
    return {
      edit: can("edit"),
      manager: can("manager"),
      profile: can("profile"),
      licences: can("licences"),
      invite: can("invite"),
      status: can("status"),
    };
  };

  const renderCollabStatusBadge = (collab: Collaborator) => (
    <>
      <Badge
        variant={collab.status === "disabled" ? "outline" : collab.status === "created" ? "outline" : collab.status === "invited" ? "secondary" : "default"}
        className={`text-xs ${collab.status === "created" ? "border-gray-400 text-gray-500" : collab.status === "disabled" ? "border-red-300 text-red-500" : ""}`}
      >
        {collab.status === "created" ? t("account.userStatusCreated") : collab.status === "disabled" ? t("account.inactive") : collab.status === "invited" ? t("account.userStatusInvited") : t("account.active")}
      </Badge>
      {isAdminMode && collab.profileType && (
        <Badge variant="outline" className="text-xs ml-1">
          {collab.profileType}
        </Badge>
      )}
    </>
  );

  /** Reporte dans la liste ce que le serveur a réellement enregistré. */
  const applySavedCollaborator = (id: string, updated: any) => {
    setCollaborators((prev) => prev.map((c) =>
      c.id === id
        ? {
            ...c,
            civility: updated.civility || "M.",
            firstName: updated.firstName || updated.first_name || "",
            lastName: updated.lastName || updated.last_name || "",
            email: updated.email || "",
            phoneNumber: updated.phoneNumber || updated.phone_number || "",
            position: updated.position || "",
            linkedinUrl: updated.linkedinUrl || updated.linkedin_url || "",
            profilePicture: updated.profilePicture || updated.profile_picture || "",
            managerId: updated.managerId ?? updated.manager_id ?? null,
            profileType: updated.profileType || updated.profile_type || c.profileType,
            language: updated.language || c.language || "fr",
            spokenLanguage: updated.spokenLanguage ?? updated.spoken_language ?? null,
          }
        : c
    ));
  };

  /**
   * Enregistre UN champ de la fiche (double-clic). N'envoie que ce champ : le serveur ne
   * touche pas aux autres. Renvoie le message d'erreur à afficher sous le champ, ou `null`.
   */
  const handleSaveCollaboratorField = async (id: string, field: FicheFieldKey, snapshot: Partial<FicheCollaborator>): Promise<string | null> => {
    const collab = collaborators.find((c) => c.id === id);
    if (!collab) return null;
    try {
      if (field === "profileType") {
        const updated = await accountApi.updateCollaboratorProfile(id, collab.profileType, accountId);
        applySavedCollaborator(id, updated);
      } else {
        const patch = buildCollaboratorPatch({ ...collab, ...snapshot }, collab);
        if (Object.keys(patch).length > 0) {
          const updated = await accountApi.updateCollaborator(id, patch, accountId);
          applySavedCollaborator(id, updated);
        }
      }
      showSaveMessage(t("common.success"));
      return null;
    } catch (err: any) {
      return err?.message || t("api.updateError");
    }
  };

  const updateCollaborator = (id: string, field: keyof Omit<Collaborator, "id" | "licences">, value: string | null) => {
    setCollaborators((prev) =>
      prev.map((c) => (c.id === id ? { ...c, [field]: value } : c))
    );
  };

  // ── Simulateurs d'une licence d'abonnement Starter / Focus (`lib/licence-simulators.ts`) ──
  const choiceForLicence = (licenceType: LicenceType) =>
    licenceSimulatorChoice(licencePoolConfig.find((lp) => lp.type === licenceType));

  const ensureSelectableSimulators = () => {
    if (selectableSimulators) return;
    setSelectableSimulatorsError(null);
    accountApi.getLicenceSimulators(accountId)
      .then(setSelectableSimulators)
      .catch((err: any) => setSelectableSimulatorsError(err?.message || t("common.loadError")));
  };

  // La fiche ET la liste des collaborateurs affichent les simulateurs choisis : les noms sont
  // chargés dès que le compte détient une licence « à choisir » (ou à l'ouverture d'une fiche).
  const hasChoiceLicence = licencePoolConfig.some((lp) => lp.total > 0 && licenceSimulatorChoice(lp).kind === "choose");
  useEffect(() => {
    if (ficheUserId || hasChoiceLicence) ensureSelectableSimulators();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ficheUserId, hasChoiceLicence]);

  const openSimulatorChoice = (choice: { collaboratorId: string; licenceType: LicenceType; mode: "pending" | "assign" | "edit"; max: number; initialIds: string[] }) => {
    ensureSelectableSimulators();
    setSimulatorChoice(choice);
  };

  const confirmSimulatorChoice = async (simulatorIds: string[]): Promise<string | null> => {
    if (!simulatorChoice) return null;
    const { collaboratorId, licenceType, mode } = simulatorChoice;
    const rememberChoice = () => setLicenceSimulatorsByUser((prev) => ({
      ...prev,
      [collaboratorId]: { ...(prev[collaboratorId] || {}), [licenceType]: simulatorIds },
    }));
    try {
      if (mode === "pending") {
        setPendingLicenceSimulators((prev) => ({
          ...prev,
          [collaboratorId]: { ...(prev[collaboratorId] || {}), [licenceType]: simulatorIds },
        }));
      } else {
        const licenceId = licenceNameToId[licenceType];
        if (!licenceId) return t("api.assignLicenceError");
        if (mode === "assign") await accountApi.assignLicence(collaboratorId, licenceId, accountId, simulatorIds);
        else await accountApi.setLicenceSimulators(collaboratorId, licenceId, simulatorIds, accountId);
        rememberChoice();
      }
      if (mode !== "edit") {
        setCollaborators((prev) => prev.map((c) =>
          c.id === collaboratorId && !c.licences.includes(licenceType) ? { ...c, licences: [...c.licences, licenceType] } : c
        ));
      }
      setSimulatorChoice(null);
      return null;
    } catch (err: any) {
      return err?.message || t("api.assignLicenceError");
    }
  };

  /** Pour la fiche : simulateurs choisis d'une licence « à choisir » (sinon `null`). */
  const licenceSimulatorsFor = (collaboratorId: string, licenceType: LicenceType) => {
    const choice = choiceForLicence(licenceType);
    if (choice.kind !== "choose") return null;
    const enregistres = newCollabIds.has(collaboratorId)
      ? pendingLicenceSimulators[collaboratorId]?.[licenceType] ?? null
      : licenceSimulatorsByUser[collaboratorId]?.[licenceType] ?? null;
    // Un choix enregistré avant le 14/10/2026 peut contenir un coach, plus proposé (inclus d'office) :
    // on ne compte que les simulateurs proposés — un choix « coach seul » s'affiche « à choisir ».
    const ids = enregistres && selectableSimulators
      ? enregistres.filter((id) => selectableSimulators.some((sim) => sim.id === id))
      : enregistres;
    const names = ids && selectableSimulators
      ? ids.map((id) => selectableSimulators.find((sim) => sim.id === id)?.name).filter((n): n is string => !!n)
      : null;
    return { max: choice.max, ids, names };
  };

  /** Liste des collaborateurs : simulateurs choisis d'une licence Starter / Focus, en petit sous la licence. */
  const renderLicenceSimulatorsInline = (collaboratorId: string, licenceType: LicenceType) => {
    const info = licenceSimulatorsFor(collaboratorId, licenceType);
    if (!info) return null;
    if (!info.ids || info.ids.length === 0) {
      return <span className="text-[11px] text-[#926B00]" data-testid="liste-licence-simulators">{t("account.licenceSimulators.notChosen", { count: info.max })}</span>;
    }
    const label = info.names ? info.names.join(", ") : t("account.licenceSimulators.chosenCount", { count: info.ids.length });
    return <span className="text-[11px] text-[#5A4BBE] leading-snug" title={label} data-testid="liste-licence-simulators">{label}</span>;
  };

  const toggleCollaboratorLicence = async (collaboratorId: string, licenceType: LicenceType) => {
    // For new (unsaved) collaborators, toggle locally only
    if (newCollabIds.has(collaboratorId)) {
      const collab = collaborators.find((c) => c.id === collaboratorId);
      if (!collab) return;
      const hasLicence = collab.licences.includes(licenceType);
      if (hasLicence) {
        setCollaborators((prev) => prev.map((c) =>
          c.id === collaboratorId ? { ...c, licences: c.licences.filter((l) => l !== licenceType) } : c
        ));
      } else {
        const pool = licencePool.find((lp) => lp.type === licenceType);
        if (pool && pool.used >= pool.total) return;
        // Licence Starter / Focus : ses simulateurs se choisissent d'abord (enregistrés à la création).
        const choice = choiceForLicence(licenceType);
        if (choice.kind === "choose") {
          openSimulatorChoice({ collaboratorId, licenceType, mode: "pending", max: choice.max, initialIds: [] });
          return;
        }
        setCollaborators((prev) => prev.map((c) =>
          c.id === collaboratorId ? { ...c, licences: [...c.licences, licenceType] } : c
        ));
      }
      return;
    }

    const collab = collaborators.find((c) => c.id === collaboratorId);
    if (!collab) return;

    const licenceId = licenceNameToId[licenceType];
    if (!licenceId) return;

    const hasLicence = collab.licences.includes(licenceType);

    try {
      if (hasLicence) {
        // Show confirmation dialog for revoking licence
        const collabName = `${collab.firstName} ${collab.lastName}`;
        setRevokingLicence({ collaboratorId, licenceType, collabName, licenceName: licenceType });
        return;
      } else {
        // Check pool availability
        const pool = licencePool.find((lp) => lp.type === licenceType);
        if (pool && pool.used >= pool.total) return;

        // Licence Starter / Focus : la fenêtre de choix des simulateurs fait l'attribution.
        const choice = choiceForLicence(licenceType);
        if (choice.kind === "choose") {
          openSimulatorChoice({ collaboratorId, licenceType, mode: "assign", max: choice.max, initialIds: [] });
          return;
        }

        await accountApi.assignLicence(collaboratorId, licenceId, accountId);
        setCollaborators((prev) =>
          prev.map((c) =>
            c.id === collaboratorId
              ? { ...c, licences: [...c.licences, licenceType] }
              : c
          )
        );
      }
    } catch (err: any) {
      showSaveMessage(err.message || t("api.updateError"));
    }
  };

  const confirmRevokeLicence = async () => {
    if (!revokingLicence) return;
    const { collaboratorId, licenceType } = revokingLicence;
    const licenceId = licenceNameToId[licenceType];
    if (!licenceId) { setRevokingLicence(null); return; }

    try {
      await accountApi.revokeLicence(collaboratorId, licenceId, accountId);
      // Le choix de simulateurs part avec la licence : une prochaine attribution le refait.
      setLicenceSimulatorsByUser((prev) => {
        if (!prev[collaboratorId]?.[licenceType]) return prev;
        const { [licenceType]: _retire, ...reste } = prev[collaboratorId]!;
        return { ...prev, [collaboratorId]: reste };
      });
      setCollaborators((prev) =>
        prev.map((c) =>
          c.id === collaboratorId
            ? { ...c, licences: c.licences.filter((l) => l !== licenceType) }
            : c
        )
      );
    } catch (err: any) {
      showSaveMessage(err.message || t("api.revokeLicenceError"));
    }
    setRevokingLicence(null);
  };

  const handleSaveUser = async (id: string, andInvite = false) => {
    const collab = collaborators.find((c) => c.id === id);
    if (!collab) return;
    // Étapes secondaires ayant échoué après la création : le collaborateur
    // existe, mais l'opération n'est pas complète — il faut le dire.
    const failedSteps: string[] = [];

    setIsSaving(true);
    try {
      if (newCollabIds.has(id)) {
        // Create new collaborator via API
        const pendingLicences = collab.licences || [];
        const created = await accountApi.createCollaborator({
          civility: collab.civility,
          firstName: collab.firstName,
          lastName: collab.lastName,
          email: collab.email,
          phoneNumber: collab.phoneNumber,
          position: collab.position || undefined,
          profileType: collab.profileType || "BNF",
          managerId: collab.managerId ?? null,
          language: collab.language ?? "fr",
          spokenLanguage: collab.spokenLanguage ?? null,
        }, accountId);

        // Assign pending licences after creation
        const assignedLicences: LicenceType[] = [];
        for (const licenceType of pendingLicences) {
          const licenceId = licenceNameToId[licenceType];
          if (licenceId) {
            try {
              const chosen = pendingLicenceSimulators[id]?.[licenceType];
              await accountApi.assignLicence(created.id, licenceId, accountId, chosen);
              assignedLicences.push(licenceType);
              if (chosen) {
                setLicenceSimulatorsByUser((prev) => ({ ...prev, [created.id]: { ...(prev[created.id] || {}), [licenceType]: chosen } }));
              }
            } catch (e) {
              // Le collaborateur est déjà créé : on n'annule pas tout pour une
              // licence. Mais on ne l'ajoute PAS à `assignedLicences` (sinon
              // l'écran afficherait une licence qui n'existe pas) et on garde
              // trace pour prévenir en fin d'opération.
              console.error(`[collaborateur] licence ${licenceType} non attribuée à ${created.id}:`, e);
              failedSteps.push(t("account.licenceAssignFailed", { licence: licenceType }));
            }
          }
        }

        // Invite if requested
        let finalStatus = created.status || "created";
        if (andInvite) {
          try {
            const invited = await accountApi.inviteCollaborator(created.id, accountId);
            finalStatus = invited.status || "invited";
          } catch (e) {
            // Non bloquant — mais le manager doit le savoir : sans invitation,
            // le collaborateur ne recevra jamais son lien d'activation et
            // n'apparaîtra jamais dans les statistiques.
            console.error(`[collaborateur] invitation non envoyée à ${created.id}:`, e);
            failedSteps.push(t("account.inviteFailed"));
          }
        }

        const mapped: Collaborator = {
          id: created.id,
          civility: created.civility || "M.",
          firstName: created.firstName || created.first_name || "",
          lastName: created.lastName || created.last_name || "",
          email: created.email || "",
          phoneNumber: created.phoneNumber || created.phone_number || "",
          profileType: created.profileType || created.profile_type || "BNF",
          managerId: created.managerId ?? created.manager_id ?? null,
          language: created.language || collab.language || "fr",
          spokenLanguage: created.spokenLanguage ?? collab.spokenLanguage ?? null,
          status: finalStatus,
          position: created.position || "",
          licences: assignedLicences,
        };
        setCollaborators((prev) => prev.map((c) => (c.id === id ? mapped : c)));
        setNewCollabIds((prev) => { const next = new Set(prev); next.delete(id); return next; });
      } else {
        // Update existing collaborator — UNIQUEMENT les champs modifiés depuis l'ouverture de
        // l'édition. Réécrire toute la fiche écrasait des valeurs non touchées (patron de
        // l'incident AUVERGNE) et faisait refuser l'enregistrement d'un MNG pour une clé hors droits.
        const patch = buildCollaboratorPatch(editSnapshot ?? {}, collab);
        if (Object.keys(patch).length > 0) {
          const updated = await accountApi.updateCollaborator(id, patch, accountId);
          applySavedCollaborator(id, updated);
        }

        // Synchronise le profileType si modifié (endpoint séparé pour protection
        // contre l'élévation de privilèges). Le managerId est déjà géré ci-dessus.
        if (editSnapshot && editSnapshot.profileType !== collab.profileType) {
          const updated = await accountApi.updateCollaboratorProfile(id, collab.profileType, accountId);
          applySavedCollaborator(id, updated);
        }
      }
      setEditingUserId(null);
      setCreateWizardStep(1);
      // Une réussite partielle ne doit pas s'annoncer comme un succès : le
      // collaborateur est créé, mais sans sa licence ou sans son invitation.
      showSaveMessage(
        failedSteps.length > 0
          ? `${t("account.collaboratorPartial")} ${failedSteps.join(" · ")}`
          : andInvite ? t("auth.emailSent") : t("common.success"),
      );
    } catch (err: any) {
      showSaveMessage(err.message || t("api.updateError"));
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelUser = () => {
    if (editingUserId && newCollabIds.has(editingUserId)) {
      // Nouveau collab non sauvegardé : on retire complètement le draft
      setCollaborators((prev) => prev.filter((c) => c.id !== editingUserId));
      setNewCollabIds((prev) => { const next = new Set(prev); next.delete(editingUserId); return next; });
    } else if (editingUserId && editSnapshot && editSnapshot.id === editingUserId) {
      // Collab existant édité : on restaure les valeurs d'origine prises au snapshot
      setCollaborators((prev) => prev.map((c) => (c.id === editingUserId ? editSnapshot : c)));
    }
    setEditSnapshot(null);
    setEditingUserId(null);
    setCreateWizardStep(1);
  };

  return (
    <div className={isEmbed ? "" : "space-y-6"}>
      {/* Bouton retour admin */}
      {!isEmbed && isAdminMode && onBackToAdmin && (
        <div className="flex items-center gap-3 mb-2">
          <button
            onClick={onBackToAdmin}
            className="p-1 rounded-md hover:bg-slate-100 transition-colors text-slate-400 hover:text-slate-600"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">
            {t("account.backToAdmin")}
          </span>
        </div>
      )}

      {/* Packs one-shot à configurer (périmètre simulateur) — compte de l'user connecté */}
      {!isAdminMode && !isEmbed && <OneShotPackConfig />}

      {/* Titre de la page — pleine largeur : la colonne de nav de 256 px a disparu
          au profit de la sous-nav en pills (25/07). */}
      {!isEmbed && (
      <div className="flex flex-col gap-2 md:flex-row md:items-end md:gap-4">
        <h1 className="text-2xl font-bold tracking-tight">
          {isAdminMode ? companyData.name || t("account.company") : t("nav.myAccount")}
        </h1>
        {activeTab === "users" && (
          <>
            <h2 className="hidden text-lg font-semibold text-muted-foreground md:block">
              {/* Ce sous-titre coiffe l'onglet COLLABORATEURS. Il affichait `account.licences`,
                  rebaptisé « Abonnements et pack » lors du chantier produits : l'onglet
                  Collaborateurs s'annonçait donc sous le titre d'un autre onglet. Un renommage
                  de libellé qui avait débordé sur un écran voisin. */}
              {isManager ? t("account.myTeam") : t("account.collaborators")}
            </h2>
            {canCreateUsers && (
            <div className="hidden md:ml-auto md:flex">
              <Button data-tour="account-add-collaborator" onClick={addCollaborator} size="sm">
                <Plus className="h-4 w-4 mr-2" />
                {t("account.addCollaborator")}
              </Button>
            </div>
            )}
          </>
        )}
      </div>
      )}

      {/* Message de confirmation */}
      {saveMessage && (
        <div className="fixed top-4 right-4 z-[60] bg-green-500 text-white px-4 py-2 rounded-md shadow-lg flex items-center gap-2">
          <Check className="h-4 w-4" />
          {saveMessage}
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className={isEmbed ? "" : "md:min-h-[calc(100vh-11rem)]"}>
          {/* Sous-nav horizontale en pills (ex-colonne de 256 px, retirée le 25/07 :
              elle coûtait ~22 % de la largeur utile en permanence — cf. `SubTabs`).
              Masquée en mode « Mon profil » (variant=profile) ou « onglet focalisé » (embedTab). */}
          <TabsList className={`mb-5 flex h-auto w-max max-w-full gap-1 overflow-x-auto rounded-full bg-[#F1F1F7] p-1 scrollbar-hide${(isProfileOnly || isEmbed) ? " hidden" : ""}`}>
            {!isAdminMode && (
              <TabsTrigger value="profile" className="shrink-0 rounded-full px-4 py-2">
                <User className="h-4 w-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">{t("account.profile")}</span>
                <span className="sm:hidden">{t("account.profile")}</span>
              </TabsTrigger>
            )}
            {canViewCompany && (
              <TabsTrigger value="company" className="shrink-0 rounded-full px-4 py-2">
                <Building className="h-4 w-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">{t("account.company")}</span>
                <span className="sm:hidden">{t("account.company")}</span>
              </TabsTrigger>
            )}
            {/* Conception (cas/offres/personae/critères/contextes) : déménagée dans
                l'espace encadrant › Concevoir. On ne l'affiche plus dans « Mon compte »
                normal ; on la garde en mode admin « gérer ce compte » (adminAccountId). */}
            {canManageTargets && isAdminMode && (
              <>
              <TabsTrigger value="targets" className="shrink-0 rounded-full px-4 py-2">
                <Target className="h-4 w-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">{t("account.targets")}</span>
                <span className="sm:hidden">{t("account.targets")}</span>
              </TabsTrigger>
              <TabsTrigger value="offers" className="shrink-0 rounded-full px-4 py-2">
                <BookOpen className="h-4 w-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">{t("account.myOffers")}</span>
                <span className="sm:hidden">{t("account.offers")}</span>
              </TabsTrigger>
              <TabsTrigger value="personae" className="shrink-0 rounded-full px-4 py-2">
                <UserCircle className="h-4 w-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">{t("account.personae")}</span>
                <span className="sm:hidden">{t("account.personae")}</span>
              </TabsTrigger>
              <TabsTrigger value="evaluation-criteria" className="shrink-0 rounded-full px-4 py-2">
                <Target className="h-4 w-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">{t("account.evaluationCriteriaTab")}</span>
                <span className="sm:hidden">{t("account.criteriaShort")}</span>
              </TabsTrigger>
              <TabsTrigger value="contexts" className="shrink-0 rounded-full px-4 py-2">
                <Sparkles className="h-4 w-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">{t("account.simulationContextsTab")}</span>
                <span className="sm:hidden">{t("account.contextsShort")}</span>
              </TabsTrigger>
              </>
            )}
            {canManageLicences && (
              <TabsTrigger value="licences" data-tour="account-tab-licences" className="shrink-0 rounded-full px-4 py-2">
                <Package className="h-4 w-4 mr-1 sm:mr-2 shrink-0" />
                <span className="hidden sm:inline">{t("account.licences")}</span>
                <span className="sm:hidden">{t("account.licences")}</span>
              </TabsTrigger>
            )}
            {canManageUsers && (
              <TabsTrigger value="users" data-tour="account-tab-users" className="shrink-0 rounded-full px-4 py-2" onClick={() => { setSelectedAgent(null); setEditingUserId(null); setFicheUserId(null); }}>
                <Users className="h-4 w-4 mr-1 sm:mr-2 shrink-0" />
                <span className="hidden sm:inline">{t("account.team")}</span>
                <span className="sm:hidden">{t("account.team")}</span>
              </TabsTrigger>
            )}
            {/* Challenges → espace encadrant › Animer ; Parcours → Concevoir.
                Masqués dans « Mon compte » normal, conservés en mode admin. */}
            {canManageChallenges && isAdminMode && (
              <TabsTrigger value="challenges" className="shrink-0 rounded-full px-4 py-2">
                <Trophy className="h-4 w-4 mr-1 sm:mr-2 shrink-0" />
                <span className="hidden sm:inline">{t("challenge.challenges")}</span>
                <span className="sm:hidden">{t("challenge.challenges")}</span>
              </TabsTrigger>
            )}
            {canManageChallenges && isAdminMode && (
              <TabsTrigger value="certification-paths" className="shrink-0 rounded-full px-4 py-2">
                <Award className="h-4 w-4 mr-1 sm:mr-2 shrink-0" />
                <span>{t("certPaths.title")}</span>
              </TabsTrigger>
            )}
            {/* Factures : règle partagée `canSeeInvoices` (ORG, RH, GES ; ADM qui gère le compte). */}
            {(isOrganisateur || canSeeInvoices(user.profileType)) && (
              <TabsTrigger value="invoices" className="shrink-0 rounded-full px-4 py-2">
                <FileText className="h-4 w-4 mr-1 sm:mr-2 shrink-0" />
                <span className="hidden sm:inline">{t("account.invoices")}</span>
                <span className="sm:hidden">{t("account.invoices")}</span>
              </TabsTrigger>
            )}
            {isOrganisateur && (
              <TabsTrigger value="rewards" className="shrink-0 rounded-full px-4 py-2">
                <Gift className="h-4 w-4 mr-1 sm:mr-2 shrink-0" />
                <span>{t("bizRewards.tab")}</span>
              </TabsTrigger>
            )}
          </TabsList>

          {/* Contenu */}
          <div className="min-w-0">

        {/* Profile Tab */}
        <TabsContent value="profile" className="md:mt-0">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>{t("account.profile")}</CardTitle>
                {!isEditingProfile && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIsEditingProfile(true)}
                  >
                    <Pencil className="h-4 w-4 mr-2" />
                    <span className="hidden sm:inline">{t("common.edit")}</span>
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Photo de profil - uniquement en mode édition */}
              {isEditingProfile && (
                <div className="mb-6">
                  <Label className="mb-2 block">{t("account.profilePicture")}</Label>
                  <div
                    className="relative h-24 w-24 cursor-pointer group"
                    onClick={() => setIsPhotoDialogOpen(true)}
                  >
                    <Avatar className="h-24 w-24">
                      <AvatarImage src={formData.profilePicture || user.profilePicture} />
                      <AvatarFallback className="text-3xl">
                        {formData.firstName[0]}
                        {formData.lastName[0]}
                      </AvatarFallback>
                    </Avatar>
                    <div className="absolute inset-0 bg-black/50 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <Pencil className="h-6 w-6 text-white" />
                    </div>
                    <div className="absolute bottom-0 right-0 bg-primary rounded-full p-1.5 shadow-md">
                      <Pencil className="h-4 w-4 text-primary-foreground" />
                    </div>
                  </div>
                </div>
              )}

              {/* Fenêtre « Changer la photo » — partagée avec la fiche collaborateur. */}
              <ProfilePhotoDialog
                open={isPhotoDialogOpen}
                currentPhoto={formData.profilePicture || user.profilePicture}
                initials={`${formData.firstName[0] || ""}${formData.lastName[0] || ""}`}
                onCancel={() => setIsPhotoDialogOpen(false)}
                onConfirm={(photo) => {
                  setFormData({ ...formData, profilePicture: photo });
                  setIsPhotoDialogOpen(false);
                }}
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Civilité */}
                <div className="space-y-2">
                  <Label htmlFor="civility">{t("account.civility")}</Label>
                  {isEditingProfile ? (
                    <Select
                      value={formData.civility}
                      onValueChange={(value) =>
                        setFormData({ ...formData, civility: value as "Mme" | "M." })
                      }
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder={t("common.confirm")} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="M.">M.</SelectItem>
                        <SelectItem value="Mme">Mme</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input
                      value={formData.civility}
                      readOnly
                      className="bg-muted cursor-default"
                    />
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="firstName">{t("account.firstName")}</Label>
                  <Input
                    id="firstName"
                    value={formData.firstName}
                    onChange={(e) =>
                      setFormData({ ...formData, firstName: e.target.value })
                    }
                    readOnly={!isEditingProfile}
                    className={!isEditingProfile ? "bg-muted cursor-default" : ""}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lastName">{t("account.lastName")}</Label>
                  <Input
                    id="lastName"
                    value={formData.lastName}
                    onChange={(e) =>
                      setFormData({ ...formData, lastName: e.target.value })
                    }
                    readOnly={!isEditingProfile}
                    className={!isEditingProfile ? "bg-muted cursor-default" : ""}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">{t("account.emailLabel")}</Label>
                  <Input
                    id="email"
                    type="email"
                    autoComplete="email"
                    value={formData.email}
                    onChange={(e) =>
                      setFormData({ ...formData, email: e.target.value })
                    }
                    readOnly={!isEditingProfile}
                    className={!isEditingProfile ? "bg-muted cursor-default" : ""}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">{t("account.phone")}</Label>
                  <Input
                    id="phone"
                    type="tel"
                    autoComplete="tel"
                    value={formData.phoneNumber}
                    onChange={(e) =>
                      setFormData({ ...formData, phoneNumber: e.target.value })
                    }
                    readOnly={!isEditingProfile}
                    className={!isEditingProfile ? "bg-muted cursor-default" : ""}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="linkedinUrl">{t("account.linkedin")}</Label>
                  <Input
                    id="linkedinUrl"
                    type="url"
                    placeholder="https://linkedin.com/in/..."
                    value={formData.linkedinUrl}
                    onChange={(e) =>
                      setFormData({ ...formData, linkedinUrl: e.target.value })
                    }
                    readOnly={!isEditingProfile}
                    className={!isEditingProfile ? "bg-muted cursor-default" : ""}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="experienceLevel">{isFr ? "Niveau d'expérience commerciale" : "Sales experience level"}</Label>
                  <select
                    id="experienceLevel"
                    value={formData.experienceLevel}
                    onChange={(e) => setFormData({ ...formData, experienceLevel: e.target.value })}
                    disabled={!isEditingProfile}
                    className={`w-full rounded-md border px-3 py-2 text-sm ${!isEditingProfile ? "bg-muted cursor-default" : ""}`}
                    style={{ borderColor: "var(--border)", color: "#384872" }}
                  >
                    <option value="debutant">{isFr ? "Débutant" : "Beginner"}</option>
                    <option value="confirme">{isFr ? "Confirmé" : "Confirmed"}</option>
                    <option value="experimente">{isFr ? "Expérimenté" : "Experienced"}</option>
                  </select>
                </div>
              </div>

              {/* Les DEUX langues, par le composant partagé avec la fiche collaborateur : la
                  langue d'entraînement manquait ici, si bien que personne ne pouvait régler
                  la sienne (22/09/2026). La langue d'affichage garde son comportement :
                  elle bascule l'interface tout de suite, à tout moment ; la langue
                  d'entraînement suit le mode édition, comme les autres champs du profil. */}
              <CollaboratorLanguageFields
                language={getCurrentLanguage()}
                spokenLanguage={formData.spokenLanguage}
                spokenLanguageDisabled={!isEditingProfile}
                onChange={async (field, value) => {
                  if (field === "spokenLanguage") {
                    setFormData({ ...formData, spokenLanguage: value });
                    return;
                  }
                  if (!value) return;
                  await changeLanguage(value);
                  if (isEditingProfile) {
                    await authApi.updateProfile({ language: value });
                  }
                }}
              />

              {isEditingProfile && (
                <>
                  <Separator />
                  <div className="flex gap-2 w-full">
                    <Button onClick={handleSaveProfile} className="flex-1">
                      <Check className="h-4 w-4 mr-2" />
                      {t("common.save")}
                    </Button>
                    <Button variant="outline" onClick={handleCancelProfile} className="flex-1">
                      <X className="h-4 w-4 mr-2" />
                      {t("common.cancel")}
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Profil joueur — pseudo + avatar (Challenges V2) */}
          {!isAdminMode && (
            <Card className="mt-4">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Gamepad2 className="h-5 w-5" style={{ color: "#F6C847" }} />
                    <CardTitle>{t("pseudo.title")}</CardTitle>
                  </div>
                  {(user as any).pseudo && (
                    <Button variant="outline" size="sm" onClick={() => setIsPseudoModalOpen(true)}>
                      <Pencil className="h-4 w-4 mr-2" />
                      <span className="hidden sm:inline">{t("pseudo.editProfile")}</span>
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {(user as any).pseudo ? (
                  <div className="flex items-center gap-4">
                    {(user as any).avatarUrl && (
                      <img
                        src={(user as any).avatarUrl}
                        alt="Avatar"
                        className="h-16 w-16 rounded-xl border-2 border-[#F6C847]/30"
                      />
                    )}
                    <div>
                      <p className="font-semibold text-lg" style={{ color: "#384872" }}>{(user as any).pseudo}</p>
                      <p className="text-sm text-muted-foreground">
                        {t("account.pseudoHint")}
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <p className="text-sm text-muted-foreground mb-3">{t("pseudo.noPseudoYet")}</p>
                    <Button onClick={() => setIsPseudoModalOpen(true)} style={{ backgroundColor: "#F6C847", color: "#926B00" }} className="hover:opacity-90">
                      <Gamepad2 className="h-4 w-4 mr-2" />
                      {t("pseudo.createNow")}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Mes badges (Challenges V2) */}
          {!isAdminMode && badgesLoaded && (
            <Card className="mt-4">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Award className="h-5 w-5" style={{ color: "#F6C847" }} />
                  <CardTitle>{t("account.myBadges")}</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                {myBadges.length === 0 ? (
                  <div className="text-center py-4">
                    <Award className="h-8 w-8 mx-auto mb-2 opacity-20" />
                    <p className="text-sm text-muted-foreground">
                      {t("account.noBadgesYet")}
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
                    {myBadges.map((b: any, i: number) => (
                      <div key={`${b.badgeId}-${i}`} className="text-center group" title={b.badgeDescription || b.badgeName}>
                        <div className="w-14 h-14 mx-auto rounded-lg overflow-hidden border-2 border-[#F6C847]/30 group-hover:border-[#F6C847] transition-colors">
                          <img src={b.badgeImageUrl} alt={b.badgeName} className="w-full h-full object-contain" loading="lazy" />
                        </div>
                        <p className="text-xs font-medium mt-1 truncate" style={{ color: "#384872" }}>{b.badgeName}</p>
                        <p className="text-[10px] text-muted-foreground">
                          {new Date(b.earnedAt).toLocaleDateString(i18n.language === "fr" ? "fr-FR" : "en-GB")}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* PseudoModal */}
          <PseudoModal
            open={isPseudoModalOpen}
            onClose={() => setIsPseudoModalOpen(false)}
            onSaved={(pseudo, avatarSeed, avatarUrl) => {
              setIsPseudoModalOpen(false);
              onUpdate({ ...user, pseudo, avatarSeed, avatarUrl } as any);
            }}
            currentPseudo={(user as any).pseudo}
            currentAvatarSeed={(user as any).avatarSeed}
          />
        </TabsContent>

        {/* Company Tab */}
        <TabsContent value="company" className="md:mt-0">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>{t("account.company")}</CardTitle>
              <div className="flex items-center gap-2">
                {(isOrganisateur || isAdminMode) && onOpenCompanyNotifications && !isEditingCompany && (
                  <Button variant="outline" size="sm" onClick={onOpenCompanyNotifications}><Bell className="h-4 w-4" />{t("account.companyNotifications")}</Button>
                )}
                {(isOrganisateur || isAdminMode) && !isEditingCompany && (
                  <Button variant="ghost" size="icon" onClick={() => { setCompanyDataBackup({ ...companyData }); setIsEditingCompany(true); }} title={t("account.edit")} aria-label={t("account.edit")}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Logo entreprise — cliquable pour changer */}
              <div className="flex items-center gap-4">
                <div
                  className={`relative h-16 w-16 rounded-lg border overflow-hidden ${isEditingCompany ? "cursor-pointer group" : ""}`}
                  onClick={() => isEditingCompany && setIsLogoDialogOpen(true)}
                >
                  {companyData.logoUrl ? (
                    <img src={companyData.logoUrl} alt={companyData.name || "Logo"} loading="lazy" className="h-full w-full object-contain" />
                  ) : (
                    <div className="h-full w-full bg-slate-100 flex items-center justify-center text-muted-foreground text-[10px] text-center px-1">
                      {t("account.noLogo")}
                    </div>
                  )}
                  {isEditingCompany && (
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity rounded-lg">
                      <Pencil className="h-5 w-5 text-white" />
                    </div>
                  )}
                </div>
                {isEditingCompany && (
                  <p className="text-xs text-muted-foreground">{t("account.clickLogoToChange")}</p>
                )}
              </div>

              {/* Dialog pour changer le logo */}
              <Dialog open={isLogoDialogOpen} onOpenChange={(open) => !open && handleCancelLogo()}>
                <DialogContent className="sm:max-w-md">
                  <DialogHeader>
                    <DialogTitle>{t("account.companyLogo")}</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div className="flex justify-center">
                      <div className="h-24 w-24 rounded-lg border overflow-hidden">
                        {(tempLogo || companyData.logoUrl) ? (
                          <img src={tempLogo || companyData.logoUrl} alt="Logo" className="h-full w-full object-contain" />
                        ) : (
                          <div className="h-full w-full bg-slate-100 flex items-center justify-center text-muted-foreground text-xs">{t("account.noLogo")}</div>
                        )}
                      </div>
                    </div>
                    <div className="grid grid-cols-1 gap-2">
                      <input type="file" ref={logoFileInputRef} accept="image/*" className="hidden" onChange={handleLogoFileSelect} />
                      <Button variant="outline" className="w-full justify-start" onClick={() => logoFileInputRef.current?.click()}>
                        <Image className="h-4 w-4 mr-2" />
                        {t("account.uploadFromComputer")}
                      </Button>
                      <Separator className="my-1" />
                      <div className="space-y-2">
                        <Label>{t("account.orPasteUrl")}</Label>
                        <div className="flex gap-2">
                          <Input placeholder="https://..." value={logoUrlInput} onChange={(e) => setLogoUrlInput(e.target.value)} className="flex-1" />
                          <Button onClick={handleLogoUrlSubmit} disabled={!logoUrlInput.trim()}>
                            <Link className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                    <Separator />
                    <div className="flex gap-2 w-full">
                      <Button onClick={handleConfirmLogo} className="flex-1" disabled={!tempLogo}>
                        <Check className="h-4 w-4 mr-2" />{t("common.save")}
                      </Button>
                      <Button variant="outline" onClick={handleCancelLogo} className="flex-1">
                        <X className="h-4 w-4 mr-2" />{t("common.cancel")}
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="companyName">{t("account.companyName")}</Label>
                  <Input
                    id="companyName"
                    value={companyData.name || (isAdminMode ? "" : t("common.none"))}
                    readOnly={!isEditingCompany}
                    className={!isEditingCompany ? "bg-muted cursor-default" : ""}
                    onChange={(e) => isEditingCompany && setCompanyData({ ...companyData, name: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="companySiret">{t("account.siret")}</Label>
                  <Input
                    id="companySiret"
                    value={companyData.siret || (isAdminMode ? "" : t("common.none"))}
                    readOnly={!isEditingCompany}
                    className={!isEditingCompany ? "bg-muted cursor-default" : ""}
                    onChange={(e) => isEditingCompany && setCompanyData({ ...companyData, siret: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="companyWebsite">{t("account.website")}</Label>
                  <Input
                    id="companyWebsite"
                    value={companyData.website || (isAdminMode ? "" : t("common.none"))}
                    readOnly={!isEditingCompany}
                    className={!isEditingCompany ? "bg-muted cursor-default" : ""}
                    onChange={(e) => isEditingCompany && setCompanyData({ ...companyData, website: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="companyIndustry">{t("account.industry")}</Label>
                  <Input
                    id="companyIndustry"
                    value={companyData.industry || (isAdminMode ? "" : t("common.none"))}
                    readOnly={!isEditingCompany}
                    className={!isEditingCompany ? "bg-muted cursor-default" : ""}
                    onChange={(e) => isEditingCompany && setCompanyData({ ...companyData, industry: e.target.value })}
                  />
                </div>
              </div>

              {/* Modèles de vente — pilote les cas Kestio proposés à l'entraînement.
                  Multi-choix : on vend souvent de plusieurs manières. */}
              <div className="space-y-2">
                <Label>{t("account.salesModels")}</Label>
                <p className="text-xs text-muted-foreground">{t("account.salesModelsHint")}</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 pt-1">
                  {SALES_MODELS.map((m) => {
                    const coche = (companyData.salesModels || []).includes(m.code);
                    return (
                      <label
                        key={m.code}
                        className={`flex items-start gap-2.5 rounded-lg border p-2.5 transition-colors ${
                          isEditingCompany ? "cursor-pointer hover:border-[#988EF2]" : "cursor-default"
                        } ${coche ? "border-[#988EF2] bg-purple-50/60" : "border-gray-200"}`}
                      >
                        <input
                          type="checkbox"
                          className="mt-0.5 h-4 w-4 shrink-0 accent-[#988EF2]"
                          checked={coche}
                          disabled={!isEditingCompany}
                          onChange={() => {
                            if (!isEditingCompany) return;
                            const actuels: string[] = companyData.salesModels || [];
                            setCompanyData({
                              ...companyData,
                              salesModels: coche ? actuels.filter((c) => c !== m.code) : [...actuels, m.code],
                            });
                          }}
                        />
                        <span className="min-w-0">
                          <span className="block text-sm font-medium">{t(m.labelKey)}</span>
                          <span className="block text-xs text-muted-foreground">{t(m.hintKey)}</span>
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* Registre des messages des managers vers leurs collaborateurs (décision 6.2) :
                  défaut de tous les managers du compte, modifiable message par message. */}
              <div className="space-y-2">
                <Label>{t("account.messageRegister.title")}</Label>
                <p className="text-xs text-muted-foreground">{t("account.messageRegister.hint")}</p>
                <div className="flex flex-wrap gap-2 pt-1" role="radiogroup" aria-label={t("account.messageRegister.title")}>
                  {(["tu", "vous"] as const).map((r) => {
                    const choisi = companyData.messageRegister === r;
                    return (
                      <label key={r} className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm ${isEditingCompany ? "cursor-pointer hover:border-[#988EF2]" : "cursor-default"} ${choisi ? "border-[#988EF2] bg-purple-50/60" : "border-gray-200"}`}>
                        <input
                          type="radio"
                          name="company-message-register"
                          className="h-4 w-4 accent-[#988EF2]"
                          checked={choisi}
                          disabled={!isEditingCompany}
                          onChange={() => isEditingCompany && setCompanyData({ ...companyData, messageRegister: r })}
                        />
                        {t(`account.messageRegister.${r}`)}
                      </label>
                    );
                  })}
                </div>
              </div>

              <div className="space-y-2">
                <Label>{t("account.address")}</Label>
                <Textarea
                  value={companyData.address || (isEditingCompany ? "" : t("common.none"))}
                  readOnly={!isEditingCompany}
                  rows={3}
                  className={!isEditingCompany ? "bg-muted cursor-default resize-none" : "resize-none"}
                  onChange={(e) => isEditingCompany && setCompanyData({ ...companyData, address: e.target.value })}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t("account.postalCode")}</Label>
                  <Input
                    value={companyData.postalCode || (isAdminMode ? "" : t("common.none"))}
                    readOnly={!isEditingCompany}
                    className={!isEditingCompany ? "bg-muted cursor-default" : ""}
                    onChange={(e) => isEditingCompany && setCompanyData({ ...companyData, postalCode: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t("account.city")}</Label>
                  <Input
                    value={companyData.city || (isAdminMode ? "" : t("common.none"))}
                    readOnly={!isEditingCompany}
                    className={!isEditingCompany ? "bg-muted cursor-default" : ""}
                    onChange={(e) => isEditingCompany && setCompanyData({ ...companyData, city: e.target.value })}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>{t("account.description")}</Label>
                <Textarea
                  value={isEditingCompany ? (companyData.description || "") : (pickCompanyDescription(companyData, i18n.language) || t("common.none"))}
                  readOnly={!isEditingCompany}
                  rows={4}
                  className={!isEditingCompany ? "bg-muted cursor-default resize-none" : "resize-none"}
                  onChange={(e) => isEditingCompany && setCompanyData({ ...companyData, description: e.target.value })}
                />
              </div>

              {isEditingCompany && (
                <div className="flex gap-2 pt-2">
                  <Button
                    onClick={async () => {
                      setIsSaving(true);
                      try {
                        // Envoi « dirty » : uniquement les champs réellement modifiés vs le
                        // snapshot pris à l'ouverture de l'édition (companyDataBackup, l.2440).
                        // Empêche un update partiel d'écraser (nuller) un champ NON édité —
                        // ex. incident AUVERGNE où une description existante était renvoyée vide
                        // par un formulaire qui postait tout l'objet. Un champ vidé volontairement
                        // diffère du snapshot → il est bien envoyé (effacement légitime préservé).
                        const base = (companyDataBackup || {}) as Record<string, any>;
                        const dirty: Record<string, any> = {};
                        // Les modèles de vente sont un TABLEAU : deux tableaux de même contenu
                        // ne sont jamais égaux par référence, ils passeraient donc pour modifiés
                        // à chaque enregistrement. On les compare sur leur contenu.
                        const aChange = (a: any, b: any) =>
                          Array.isArray(a) || Array.isArray(b)
                            ? JSON.stringify(a ?? []) !== JSON.stringify(b ?? [])
                            : a !== b;
                        for (const k of Object.keys(companyData)) {
                          if (aChange((companyData as Record<string, any>)[k], base[k])) {
                            dirty[k] = (companyData as Record<string, any>)[k];
                          }
                        }
                        const updatedCompany = await accountApi.updateCompany(dirty as typeof companyData, accountId);
                        // Resynchroniser l'état avec la réponse (préserve description + variantes i18n,
                        // évite qu'un save suivant renvoie une description vidée).
                        if (updatedCompany) {
                          setCompanyData((prev) => ({
                            ...prev,
                            description: updatedCompany.description ?? updatedCompany.descriptionFr ?? "",
                            descriptionFr: updatedCompany.descriptionFr ?? updatedCompany.description_fr ?? "",
                            descriptionEn: updatedCompany.descriptionEn ?? updatedCompany.description_en ?? "",
                          }));
                        }
                        setIsEditingCompany(false);
                        // Registre des messages (décision 6.2) : effet immédiat pour les managers de CE compte
                        // (pas quand un ADM édite la fiche d'un autre compte).
                        if (!adminAccountId && dirty.messageRegister !== undefined) {
                          const reg = dirty.messageRegister === "vous" ? "vous" : "tu";
                          setCompanyMessageRegister(reg);
                          onUpdate({ ...user, companyMessageRegister: reg } as any);
                        }
                        // Update user context so header logo refreshes
                        if (updatedCompany?.logoUrl !== undefined || updatedCompany?.logo_url !== undefined) {
                          onUpdate({ ...user, companyLogoUrl: updatedCompany.logoUrl || updatedCompany.logo_url || null, companyName: updatedCompany.name || null } as any);
                        }
                        showSaveMessage(t("common.success"));
                      } catch (err: any) {
                        showSaveMessage(err.message || t("api.updateError"));
                      } finally {
                        setIsSaving(false);
                      }
                    }}
                    disabled={isSaving}
                    size="sm"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    {t("common.save")}
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => { if (companyDataBackup) setCompanyData(companyDataBackup); setIsEditingCompany(false); }}>
                    {t("common.cancel")}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Targets Tab — Cas de simulation */}
        <TabsContent value="targets" className="md:mt-0">
          <div className="space-y-5">
            {/* Header + intro */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-lg font-semibold">{t("account.targets")}</h2>
                <div className="flex gap-2">
                  <Button data-tour="account-new-case" onClick={() => setShowSimWizard(true)} size="sm" style={{ backgroundColor: "#384872" }}>
                    <Sparkles className="h-4 w-4 mr-2" />
                    {t("account.createCaseGuided")}
                  </Button>
                </div>
              </div>
              {/* Une phrase, pas un bandeau replié : l'écran s'explique seul (Pilot cible, chantier E). */}
              <p className="text-sm text-muted-foreground">{t("account.simulationCasesIntro")}</p>
            </div>

            {/* Onglets « Cas d'équipe » / « Cas personnels » / « Cas génériques » */}
            <div className="flex gap-2 flex-wrap">
              {(["team", "personal", "generic"] as const).map((tab) => (
                <Button
                  key={tab}
                  data-testid={`case-tab-${tab}`}
                  onClick={() => setCaseTab(tab)}
                  size="sm"
                  variant={caseTab === tab ? "default" : "outline"}
                  style={caseTab === tab ? { backgroundColor: "#384872" } : undefined}
                >
                  {t(`account.caseTabs.${tab}`)}
                </Button>
              ))}
            </div>

            {caseTab === "generic" && (
              <div data-testid="generic-cases-intro" className="px-4 py-3 rounded-lg bg-blue-50 border border-blue-100 text-sm text-[#384872] flex items-start gap-2">
                <Info className="h-4 w-4 mt-0.5 shrink-0" />
                <span>{t("account.genericCasesIntro")}</span>
              </div>
            )}

            {filteredTargets.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Building2 className="h-10 w-10 text-muted-foreground/40 mx-auto mb-3" />
                  <p className="text-muted-foreground mb-1">
                    {t(`account.caseTabsEmpty.${caseTab}`)}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {caseTab === "team" ? t("account.addFirstTarget") : t(`account.caseTabsEmptyHint.${caseTab}`)}
                  </p>
                </CardContent>
              </Card>
            ) : (
              filteredTargets.map((target) => {
                const isEditingTarget = editingTargetId === target.id;
                // Gestes proposés = gestes que le serveur accepte (règle partagée, Q1 du 01/10/2026) :
                // un MNG ne modifie que ses cas et n'en supprime aucun.
                const peutModifierCas = canTargetAction(user, "edit", target);
                const peutSupprimerCas = canTargetAction(user, "delete", target);
                // Désactiver / réactiver : même règle que la modification (MNG : ses cas seulement).
                const peutDesactiverCas = canTargetAction(user, "archive", target);
                return (
                  <Card key={target.id} className={`overflow-hidden${target.archivedAt ? " opacity-75" : ""}`}>
                    {/* Card header */}
                    <CardHeader className="pb-3">
                      <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-start gap-2">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <Badge variant={target.caseType === "B2C" ? "secondary" : "default"} className="text-[10px]">{target.caseType || "B2B"}</Badge>
                            {target.archivedAt && (
                              <Badge
                                data-testid="target-archived-badge"
                                className="text-[10px] bg-slate-200 text-slate-700 hover:bg-slate-200 border border-slate-300"
                                title={t("account.archivedHint")}
                              >
                                {t("account.archivedBadge")}
                              </Badge>
                            )}
                            {!(target as any).simulatorId && (
                              <Badge
                                className="text-[10px] bg-amber-100 text-amber-800 hover:bg-amber-100 border border-amber-300"
                                title={t("account.needsUpdateTooltip")}
                              >
                                {t("account.needsUpdate")}
                              </Badge>
                            )}
                            {/* Cas sans offre : l'offre est obligatoire depuis le 30/07, mais
                                des cas plus anciens (ou vidés avant le verrouillage de la
                                modification) n'en ont pas. L'apprenant y démarre alors sa
                                simulation sans savoir ce qu'il est censé vendre, et le bloc
                                « Ce que tu vends » du scénario reste vide. */}
                            {((target as any).offerIds || []).length === 0 && (
                              <Badge
                                className="text-[10px] bg-amber-100 text-amber-800 hover:bg-amber-100 border border-amber-300"
                                title={t("account.missingOfferTooltip")}
                              >
                                {t("account.missingOffer")}
                              </Badge>
                            )}
                            {/* Badge d'origine — uniquement sur les cas personnels (private).
                                Coach Debrief : sourceRealCallSessionId renseigné.
                                Coach Prepa : private sans sourceRealCallSessionId. */}
                            {(target as any).visibility === "private" && (
                              (target as any).sourceRealCallSessionId ? (
                                <Badge
                                  className="text-[10px]"
                                  style={{ backgroundColor: "#988EF2", color: "white" }}
                                  title={isFr ? "Cas créé via Coach Debrief" : "Created from Coach Debrief"}
                                >
                                  🎙️ Coach Debrief
                                </Badge>
                              ) : (
                                <Badge
                                  className="text-[10px]"
                                  style={{ backgroundColor: "#F6C847", color: "#926B00" }}
                                  title={isFr ? "Cas créé via Coach Prepa" : "Created from Coach Prepa"}
                                >
                                  📋 Coach Prepa
                                </Badge>
                              )
                            )}
                          </div>
                          {!isEditingTarget && (
                            <CardTitle className="text-base flex items-center gap-2">
                              {target.caseType === "B2C" ? (
                                <>
                                  <Package className="h-4 w-4 text-muted-foreground shrink-0" />
                                  {/* Titre = nom libre saisi par l'utilisateur, sinon fallback offres */}
                                  {target.displayName || (() => {
                                    const offerNames = offresDuCas(target)
                                      .map((o: any) => pickOfferLabel(o, i18n.language));
                                    return offerNames.length > 0 ? offerNames.join(", ") : t("account.caseB2C");
                                  })()}
                                </>
                              ) : (
                                <>
                                  <Building2 className="h-4 w-4 text-muted-foreground shrink-0" />
                                  {/* Titre = nom libre saisi par l'utilisateur, sinon fallback companySize */}
                                  {target.displayName || localizeCompanySize(target.companySize, i18n.language) || t("account.companySize")}
                                </>
                              )}
                            </CardTitle>
                          )}
                          {!isEditingTarget && target.createdAt && (
                            <p className="text-xs text-muted-foreground mt-1">
                              {isFr ? "Créé le" : "Created on"} {new Date(target.createdAt).toLocaleDateString(isFr ? "fr-FR" : "en-GB")}
                            </p>
                          )}
                          {!isEditingTarget && (targetUsage[target.id]?.campaigns.length || targetUsage[target.id]?.paths.length) ? (
                            <p className="mt-1 flex flex-wrap items-center gap-1 text-xs" style={{ color: "#556479" }}>
                              <span className="font-semibold">{t("account.usedBy")}</span>
                              {targetUsage[target.id]!.campaigns.map((c) => (
                                <span key={c.id} className="rounded-full px-2 py-0.5 text-[11px] font-semibold" style={{ background: "#E7ECF7", color: "#384872" }} title={t("plan.type.campaign")}>{c.title}{c.status === "active" ? " ●" : ""}</span>
                              ))}
                              {targetUsage[target.id]!.paths.map((p) => (
                                <span key={p.id} className="rounded-full px-2 py-0.5 text-[11px] font-semibold" style={{ background: "#EEEAF9", color: "#5A4BBE" }} title={t("plan.type.path")}>{p.title}</span>
                              ))}
                            </p>
                          ) : null}
                          {/* 2026-06-12 — Affichage du focus/axe à travailler pour les
                              cas personnels (private). Coach Debrief = "Axe à travailler"
                              (focus saisi par l'user dans le popup Lancer ma simulation).
                              Coach Prepa = brief auto-généré du RDV ("Préparation [...] avec
                              [contact] chez [entreprise]"). Format compact 2 lignes max,
                              texte complet en title au survol. */}
                          {!isEditingTarget && (target as any).visibility === "private" && (target as any).focusRaw && (
                            (target as any).sourceRealCallSessionId ? (
                              <p
                                className="text-xs italic mt-1 line-clamp-2"
                                style={{ color: "#5A4BBE" }}
                                title={(target as any).focusRaw}
                              >
                                <span className="font-semibold not-italic">
                                  🎯 {isFr ? "Axe à travailler" : "Focus to work on"} :
                                </span>{" "}
                                {(target as any).focusRaw}
                              </p>
                            ) : (
                              <p
                                className="text-xs italic mt-1 line-clamp-2"
                                style={{ color: "#5A4BBE" }}
                                title={(target as any).focusRaw}
                              >
                                <span className="font-semibold not-italic">📋</span>{" "}
                                {(target as any).focusRaw}
                              </p>
                            )
                          )}
                        </div>

                        {/* Simulateur centré (mode lecture) */}
                        <div className="flex flex-col items-center text-center">
                          {!isEditingTarget && target.simulatorId && (() => {
                            const sim = simulatorsList.find((s) => s.id === target.simulatorId);
                            if (!sim) return null;
                            return (
                              <>
                                <div className="flex items-center gap-2 mb-2">
                                  <Sparkles className="h-4 w-4 text-[#384872]" />
                                  <span className="text-sm font-semibold">{t("account.simulator")}</span>
                                </div>
                                <span className="inline-flex items-center rounded-full px-3 py-1 text-xs font-medium" style={{ backgroundColor: "#A6D3F9", color: "#384872" }}>
                                  {sim.name}
                                </span>
                              </>
                            );
                          })()}
                        </div>

                        <div className="flex justify-end gap-2 shrink-0">
                          {!isEditingTarget && peutModifierCas && (
                            <Button variant="ghost" size="icon" className="shrink-0" title={t("account.edit")} aria-label={t("account.edit")} onClick={() => {
                              // Détecter un éventuel secteur "Autre" déjà enregistré : une valeur qui
                              // n'est NI un secteur standard NI le label "Tous secteurs". On pré-remplit
                              // le champ texte libre pour permettre son édition.
                              const known = new Set([t("account.allIndustries"), ...industryOptions]);
                              const custom = parseMultiValue(target.industry).find((v) => !known.has(v));
                              setCustomIndustryEdit(custom || "");
                              setCustomIndustryMode(!!custom);
                              setEditingTargetId(target.id); setAddOfferMode(null); setAddPersonaMode(null);
                            }}>
                              <Pencil className="h-4 w-4" />
                            </Button>
                          )}
                          {isEditingTarget && (
                            <>
                              <Button onClick={() => { handleSaveTarget(target.id); setAddOfferMode(null); setAddPersonaMode(null); }} size="sm">
                                <Check className="h-4 w-4 mr-1" />
                                {t("common.save")}
                              </Button>
                              <Button variant="outline" onClick={() => { handleCancelTarget(); setAddOfferMode(null); setAddPersonaMode(null); }} size="sm">
                                {t("common.cancel")}
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    </CardHeader>

                    <CardContent className="pt-0 space-y-5">

                      {/* ── MODE ÉDITION ── */}
                      {isEditingTarget && (
                        <>
                        {/* Nom libre du cas — éditable inline. Vide → fallback companySize côté affichage. */}
                        <div className="space-y-1.5">
                          <Label className="text-sm font-semibold">
                            {isFr ? "Nom du cas (optionnel)" : "Case name (optional)"}
                          </Label>
                          <Input
                            value={target.displayName ?? ""}
                            onChange={(e) => {
                              const newName = e.target.value;
                              setTargets((prev) => prev.map((t) => t.id === target.id ? { ...t, displayName: newName } : t));
                            }}
                            placeholder={isFr ? "Ex: Cold call PME chimie — relance budget" : "Ex: Cold call SMB chemistry"}
                            maxLength={120}
                          />
                          <p className="text-xs text-muted-foreground">
                            {isFr ? "Sert juste à vous y retrouver dans la liste. Vide → la taille d'entreprise sert d'étiquette." : "Just a UI label. Empty → company size used."}
                          </p>
                        </div>

                        {/* Section Simulateur (associe le cas à un simulateur) */}
                        <div className="space-y-3">
                          <div className="flex items-center gap-2">
                            <Sparkles className="h-4 w-4 text-[#384872]" />
                            <span className="text-sm font-semibold">{t("account.simulator")}</span>
                            {!target.simulatorId && (
                              <Badge className="text-[10px] bg-amber-100 text-amber-800 hover:bg-amber-100 border border-amber-300">
                                {t("account.toSet")}
                              </Badge>
                            )}
                          </div>
                          <Select
                            value={target.simulatorId || undefined}
                            onValueChange={(value) => {
                              if (!value) return;
                              const sim = simulatorsList.find((s) => s.id === value);
                              const types = Array.isArray((sim as any)?.caseTypes) ? (sim as any).caseTypes as string[] : null;
                              // Batch les 2 updates dans un seul setTargets pour éviter une race condition
                              setTargets((prev) =>
                                prev.map((t) => {
                                  if (t.id !== target.id) return t;
                                  // Reset du contexte si on change de simulateur (un contexte est lié à un simulateur)
                                  const updated: TargetProspect = { ...t, simulatorId: value, contextId: null };
                                  if (types && types.length === 1) updated.caseType = types[0]!;
                                  else if (types && !types.includes(t.caseType)) updated.caseType = types[0]!;
                                  return updated;
                                })
                              );
                            }}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder={t("account.selectSimulator")} />
                            </SelectTrigger>
                            <SelectContent>
                              {(() => {
                                // Filtre de compatibilité : caseType + category dérivée des personae
                                const personaeCategories = Array.from(new Set((target.personae || []).map((p: any) => p.category).filter(Boolean)));
                                const requiredCategory = personaeCategories.length === 1 ? personaeCategories[0] : null;
                                const compatible = simulatorsList.filter((s) => {
                                  if (s.simulatorType !== "training") return false;
                                  if (!simulateurUtilisable(s)) return false;
                                  if (requiredCategory && s.category !== requiredCategory) return false;
                                  const types = Array.isArray((s as any).caseTypes) ? (s as any).caseTypes : ["B2B"];
                                  if (target.caseType && !types.includes(target.caseType)) return false;
                                  return true;
                                });
                                if (compatible.length === 0) {
                                  return (
                                    <div className="px-3 py-2 text-xs text-muted-foreground">
                                      {t("account.noCompatibleSimulator")}
                                    </div>
                                  );
                                }
                                return compatible.map((s) => {
                                  const types = Array.isArray((s as any).caseTypes) ? (s as any).caseTypes : ["B2B"];
                                  return (
                                    <SelectItem key={s.id} value={s.id}>
                                      <span className="flex items-center gap-2">
                                        <span>{s.name}</span>
                                        <span className="text-[10px] text-slate-400">({types.join(" / ")})</span>
                                      </span>
                                    </SelectItem>
                                  );
                                });
                              })()}
                            </SelectContent>
                          </Select>
                          {(target.personae || []).length > 0 && (
                            <p className="text-xs text-muted-foreground">
                              {t("account.simulatorListFiltered")}
                            </p>
                          )}
                        </div>

                        {/* Section Contexte (optionnel — lié au simulateur sélectionné) */}
                        {target.simulatorId && (() => {
                          const availableContexts = accountContexts
                            .filter((c) => c.simulatorId === target.simulatorId && c.isActive)
                            .sort((a, b) => (isFr ? a.nameFr : a.nameEn).localeCompare(isFr ? b.nameFr : b.nameEn));
                          return (
                            <div className="space-y-2">
                              <div className="flex items-center gap-2">
                                <Sparkles className="h-4 w-4 text-[#926B00]" />
                                <span className="text-sm font-semibold">{t("account.context")}</span>
                                <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-700">
                                  {t("account.personalizationComingSoon")}
                                </Badge>
                              </div>
                              {availableContexts.length === 0 ? (
                                <p className="text-xs text-muted-foreground italic">
                                  {t("account.noContextAvailable")}
                                </p>
                              ) : (
                                <Select
                                  value={target.contextId || "__none__"}
                                  onValueChange={(value) => {
                                    const newContextId = value === "__none__" ? null : value;
                                    setTargets((prev) => prev.map((t) => t.id === target.id ? { ...t, contextId: newContextId } : t));
                                  }}
                                >
                                  <SelectTrigger>
                                    <SelectValue placeholder={t("account.noSpecificContext")} />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="__none__">
                                      <span className="italic text-muted-foreground">
                                        {t("account.noSpecificContext")}
                                      </span>
                                    </SelectItem>
                                    {availableContexts.map((c) => {
                                      const name = isFr ? (c.nameFr || c.nameEn) : (c.nameEn || c.nameFr);
                                      const description = isFr ? (c.descriptionFr || c.descriptionEn) : (c.descriptionEn || c.descriptionFr);
                                      return (
                                        <SelectItem key={c.id} value={c.id}>
                                          <div className="flex flex-col gap-0.5 py-0.5">
                                            <span className="font-medium">{name}</span>
                                            {description && (
                                              <span className="text-xs text-muted-foreground leading-tight whitespace-normal">
                                                {description}
                                              </span>
                                            )}
                                          </div>
                                        </SelectItem>
                                      );
                                    })}
                                  </SelectContent>
                                </Select>
                              )}
                            </div>
                          );
                        })()}

                        {/* Section Taille/Secteur (B2B uniquement) */}
                        {target.caseType !== "B2C" && (
                          <div className="space-y-3">
                            <div className="flex items-center gap-2">
                              <Building2 className="h-4 w-4 text-[#384872]" />
                              <span className="text-sm font-semibold">{t("account.target")}</span>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                              <div className="space-y-1.5">
                                <Label className="text-xs">{t("account.companySize")}</Label>
                                <Select value={target.companySize || ""} onValueChange={(value) => updateTarget(target.id, "companySize", value)}>
                                  <SelectTrigger><SelectValue placeholder={t("account.companySize")} /></SelectTrigger>
                                  <SelectContent>
                                    {companySizeOptions.map((opt) => (<SelectItem key={opt} value={opt}>{localizeCompanySize(opt, i18n.language)}</SelectItem>))}
                                  </SelectContent>
                                </Select>
                              </div>
                              <div className="space-y-1.5">
                                <Label className="text-xs">{t("account.industry")}</Label>
                                <MultiSelect
                                  getLabel={(opt) => opt === CUSTOM_INDUSTRY ? t("account.industryOther") : localizeIndustry(opt, i18n.language)}
                                  options={[t("account.allIndustries"), ...industryOptions, CUSTOM_INDUSTRY]}
                                  value={(() => {
                                    const known = new Set([t("account.allIndustries"), ...industryOptions]);
                                    // Secteurs standards persistés + sentinelle "Autre" si le mode est actif
                                    // (indépendamment d'une valeur custom déjà saisie ou non).
                                    const standards = parseMultiValue(target.industry).filter((v) => known.has(v));
                                    return customIndustryMode ? [...standards, CUSTOM_INDUSTRY] : standards;
                                  })()}
                                  onChange={(values) => {
                                    const allLabel = t("account.allIndustries");
                                    // Gestion "Tous secteurs" : exclusif — vient d'être coché
                                    if (values.includes(allLabel) && !parseMultiValue(target.industry).includes(allLabel)) {
                                      setCustomIndustryMode(false);
                                      setCustomIndustryEdit("");
                                      updateTarget(target.id, "industry", JSON.stringify([allLabel]));
                                      return;
                                    }
                                    if (values.includes(allLabel) && values.length > 1) {
                                      values = values.filter((v) => v !== allLabel);
                                    }
                                    // Secteurs standards retenus (hors sentinelle)
                                    const standards = values.filter((v) => v !== CUSTOM_INDUSTRY);
                                    if (values.includes(CUSTOM_INDUSTRY)) {
                                      // "Autre" coché : on active le mode et on ajoute la valeur libre si déjà saisie
                                      setCustomIndustryMode(true);
                                      const custom = customIndustryEdit.trim();
                                      updateTarget(target.id, "industry", JSON.stringify(custom ? [...standards, custom] : standards));
                                    } else {
                                      // "Autre" retiré : on désactive le mode et on oublie la valeur libre
                                      setCustomIndustryMode(false);
                                      setCustomIndustryEdit("");
                                      updateTarget(target.id, "industry", JSON.stringify(standards));
                                    }
                                  }}
                                  placeholder={t("account.industry")}
                                />
                                {/* Champ texte libre affiché quand "Autre" est sélectionné */}
                                {customIndustryMode && (
                                  <Input
                                    value={customIndustryEdit}
                                    onChange={(e) => {
                                      const next = e.target.value;
                                      setCustomIndustryEdit(next);
                                      const known = new Set([t("account.allIndustries"), ...industryOptions]);
                                      // Secteurs standards actuellement enregistrés (hors valeur custom)
                                      const standards = parseMultiValue(target.industry).filter((v) => known.has(v));
                                      const trimmed = next.trim();
                                      updateTarget(target.id, "industry", JSON.stringify(trimmed ? [...standards, trimmed] : standards));
                                    }}
                                    placeholder={t("account.industryOtherPlaceholder")}
                                    className="mt-1.5"
                                  />
                                )}
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Section Offres (édition) */}
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Package className="h-4 w-4" style={{ color: "#7CBF94" }} />
                              <span className="text-sm font-semibold">{t("account.offer")}</span>
                            </div>
                            {!addOfferMode?.targetId && (
                              <Button variant="outline" size="sm" onClick={() => setAddOfferMode({ targetId: target.id, mode: "choose" })}>
                                <Package className="h-3 w-3 mr-1" />
                                {t("account.changeOffer")}
                              </Button>
                            )}
                          </div>
                          {/* Offres actuelles */}
                          <div className="flex flex-wrap gap-2">
                            {(target.offerIds || []).map((oid: string) => {
                              const offer = accountOffers.find((o) => o.id === oid);
                              return offer ? (
                                <span key={oid} className="inline-flex items-center rounded-full px-3 py-1 text-xs font-medium" style={{ backgroundColor: "#eef6f1", border: "1px solid #c8e0d0", color: "#2d6a4f" }}>
                                  {pickOfferLabel(offer, i18n.language)}
                                </span>
                              ) : null;
                            })}
                            {(!target.offerIds || target.offerIds.length === 0) && (
                              <p className="text-xs text-muted-foreground">{t("account.noOfferSelected")}</p>
                            )}
                          </div>
                          {/* Sous-vue offre : choix */}
                          {addOfferMode?.targetId === target.id && addOfferMode.mode === "choose" && (
                            <div className="rounded-lg border border-[#7CBF94]/30 bg-green-50/50 p-4 space-y-3">
                              <div className="flex items-center justify-between">
                                <span className="text-sm font-semibold">{t("account.addOfferQuestion")}</span>
                                <button onClick={() => setAddOfferMode(null)} className="text-muted-foreground hover:text-foreground"><X className="h-4 w-4" /></button>
                              </div>
                              <div className="grid grid-cols-1 gap-3">
                                <button onClick={() => setAddOfferMode({ targetId: target.id, mode: "select" })}
                                  className="flex flex-col items-center gap-2 p-4 rounded-xl border-2 border-border hover:border-[#7CBF94] hover:bg-green-50 transition-all text-center">
                                  <Package className="h-6 w-6" style={{ color: "#7CBF94" }} />
                                  <span className="text-sm font-medium">{t("account.existingOffer")}</span>
                                  <span className="text-[11px] text-muted-foreground">{t("account.existingOfferHelp")}</span>
                                </button>
                                <button onClick={() => { setInlineOfferForm({ name: "", description: "" }); setAddOfferMode({ targetId: target.id, mode: "manual" }); }}
                                  className="flex flex-col items-center gap-2 p-4 rounded-xl border-2 border-border hover:border-[#7CBF94] hover:bg-green-50 transition-all text-center">
                                  <Pencil className="h-6 w-6" style={{ color: "#7CBF94" }} />
                                  <span className="text-sm font-medium">{t("account.createManually")}</span>
                                  <span className="text-[11px] text-muted-foreground">{t("account.createOfferManuallyHelp")}</span>
                                </button>
                                <button onClick={() => setAddOfferMode({ targetId: target.id, mode: "ai" })}
                                  className="flex flex-col items-center gap-2 p-4 rounded-xl border-2 border-border hover:border-[#877BF2] hover:bg-purple-50 transition-all text-center">
                                  <span className="text-2xl">🤖</span>
                                  <span className="text-sm font-medium">{t("account.agentAi")}</span>
                                  <span className="text-[11px] text-muted-foreground">{t("account.createOfferAiHelp")}</span>
                                </button>
                              </div>
                            </div>
                          )}
                          {/* Sous-vue offre : sélection */}
                          {addOfferMode?.targetId === target.id && addOfferMode.mode === "select" && (
                            <div className="rounded-lg border border-[#7CBF94]/30 bg-green-50/50 p-3 space-y-2">
                              <div className="flex items-center justify-between">
                                <button onClick={() => setAddOfferMode({ targetId: target.id, mode: "choose" })} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"><ChevronLeft className="h-3 w-3" /> {t("account.back")}</button>
                                <button onClick={() => setAddOfferMode(null)} className="text-muted-foreground hover:text-foreground"><X className="h-4 w-4" /></button>
                              </div>
                              <p className="text-sm font-medium">{t("account.selectExistingOffer")}</p>
                              <div className="flex flex-wrap gap-2">
                                {accountOffers.map((offer: any) => {
                                  const isSelected = (target.offerIds || []).includes(offer.id);
                                  return (
                                    <button key={offer.id} type="button"
                                      className={`px-3 py-1.5 rounded-lg border-2 text-xs text-left transition-all ${isSelected ? "border-[#7CBF94] bg-green-50 font-medium" : "border-border hover:border-[#7CBF94]/50"}`}
                                      onClick={() => { updateTarget(target.id, "offerIds", isSelected ? [] : [offer.id]); }}
                                    >
                                      {pickOfferLabel(offer, i18n.language)}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                          {/* Sous-vue offre : manuelle */}
                          {addOfferMode?.targetId === target.id && addOfferMode.mode === "manual" && (
                            <div className="rounded-lg border border-[#7CBF94]/30 bg-green-50/50 p-3 space-y-2">
                              <div className="flex items-center justify-between">
                                <button onClick={() => setAddOfferMode({ targetId: target.id, mode: "choose" })} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"><ChevronLeft className="h-3 w-3" /> {t("account.back")}</button>
                                <button onClick={() => setAddOfferMode(null)} className="text-muted-foreground hover:text-foreground"><X className="h-4 w-4" /></button>
                              </div>
                              <p className="text-sm font-medium">{t("account.createNewOffer")}</p>
                              <div className="space-y-2">
                                <Input value={inlineOfferForm.name} onChange={(e) => setInlineOfferForm({ ...inlineOfferForm, name: e.target.value })} placeholder={t("account.offerNameInline")} className="text-sm" />
                                <textarea className="w-full min-h-[60px] px-3 py-2 rounded-md border border-input bg-background text-sm resize-y" value={inlineOfferForm.description} onChange={(e) => setInlineOfferForm({ ...inlineOfferForm, description: e.target.value })} placeholder={t("account.offerDescription") + "..."} />
                                <Button size="sm" disabled={!inlineOfferForm.name} onClick={async () => {
                                  try {
                                    const created = await accountApi.createOffer(inlineOfferForm, accountId);
                                    setAccountOffers((prev) => [...prev, created]);
                                    updateTarget(target.id, "offerIds", [...(target.offerIds || []), created.id]);
                                    setInlineOfferForm({ name: "", description: "" });
                                    setAddOfferMode({ targetId: target.id, mode: "select" });
                                  } catch (err: any) { alert(err.message); }
                                }}>{t("account.createAndSelect")}</Button>
                              </div>
                            </div>
                          )}
                          {/* Sous-vue offre : IA */}
                          {addOfferMode?.targetId === target.id && addOfferMode.mode === "ai" && (
                            <div className="rounded-lg border border-[#7CBF94]/30 bg-green-50/50 p-3 space-y-2">
                              <div className="flex items-center justify-between">
                                <button onClick={() => setAddOfferMode({ targetId: target.id, mode: "choose" })} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"><ChevronLeft className="h-3 w-3" /> {t("account.back")}</button>
                                <button onClick={() => setAddOfferMode(null)} className="text-muted-foreground hover:text-foreground"><X className="h-4 w-4" /></button>
                              </div>
                              <div style={{ height: "60vh", minHeight: "520px" }}>
                                <AiChat agentCode="offer_creator" robotColor="#7CBF94" bgColor="#f7faf8"
                                  context={{ companyDescription: pickCompanyDescription(companyData, i18n.language) || "", language: i18n.language }}
                                  onItemCreated={async (_type, data) => { try { const created = await accountApi.createOffer(data, accountId); setAccountOffers((prev) => [...prev, created]); updateTarget(target.id, "offerIds", [...(target.offerIds || []), created.id]); } catch (err: any) { console.error("[AiChat] createOffer failed", err); alert(t("account.offerSaveError") + (err?.message || "")); } }}
                                  onComplete={() => setAddOfferMode({ targetId: target.id, mode: "select" })}
                                  title={t("onboarding.offersTitle")} subtitle={t("onboarding.offersSubtitle")}
                                />
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Section Personae (édition) */}
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <UserCircle className="h-4 w-4" style={{ color: "#877BF2" }} />
                              <span className="text-sm font-semibold">{t("account.personae")}</span>
                            </div>
                            {!addPersonaMode?.targetId && !newTargetIds.has(target.id) && (
                              <Button variant="outline" size="sm" onClick={() => setAddPersonaMode({ targetId: target.id, mode: "choose" })}>
                                <Plus className="h-3 w-3 mr-1" />
                                {t("account.addPersona")}
                              </Button>
                            )}
                          </div>
                          {/* Sous-vue persona : choix */}
                          {addPersonaMode?.targetId === target.id && addPersonaMode.mode === "choose" && (
                            <div className="rounded-lg border border-[#877BF2]/30 bg-purple-50/50 p-4 space-y-3">
                              <div className="flex items-center justify-between">
                                <span className="text-sm font-semibold">{t("account.addPersonaQuestion")}</span>
                                <button onClick={() => setAddPersonaMode(null)} className="text-muted-foreground hover:text-foreground"><X className="h-4 w-4" /></button>
                              </div>
                              <div className="grid grid-cols-1 gap-3">
                                <button onClick={() => setAddPersonaMode({ targetId: target.id, mode: "select" })}
                                  className="flex flex-col items-center gap-2 p-4 rounded-xl border-2 border-border hover:border-[#877BF2] hover:bg-purple-50 transition-all text-center">
                                  <UserCircle className="h-6 w-6" style={{ color: "#877BF2" }} />
                                  <span className="text-sm font-medium">{t("account.existingPersona")}</span>
                                  <span className="text-[11px] text-muted-foreground">{t("account.existingPersonaHelp")}</span>
                                </button>
                                <button onClick={() => { addPersona(target.id); setAddPersonaMode(null); }}
                                  className="flex flex-col items-center gap-2 p-4 rounded-xl border-2 border-border hover:border-[#877BF2] hover:bg-purple-50 transition-all text-center">
                                  <Pencil className="h-6 w-6" style={{ color: "#877BF2" }} />
                                  <span className="text-sm font-medium">{t("account.createManually")}</span>
                                  <span className="text-[11px] text-muted-foreground">{t("account.createPersonaManuallyHelp")}</span>
                                </button>
                                <button onClick={() => setAddPersonaMode({ targetId: target.id, mode: "ai" })}
                                  className="flex flex-col items-center gap-2 p-4 rounded-xl border-2 border-border hover:border-[#877BF2] hover:bg-purple-50 transition-all text-center">
                                  <span className="text-2xl">🤖</span>
                                  <span className="text-sm font-medium">{t("account.agentAi")}</span>
                                  <span className="text-[11px] text-muted-foreground">{t("account.createPersonaAiHelp")}</span>
                                </button>
                              </div>
                            </div>
                          )}
                          {/* Sous-vue persona : sélection */}
                          {addPersonaMode?.targetId === target.id && addPersonaMode.mode === "select" && (
                            <div className="rounded-lg border border-[#877BF2]/30 bg-purple-50/50 p-3 space-y-2">
                              <div className="flex items-center justify-between">
                                <button onClick={() => setAddPersonaMode({ targetId: target.id, mode: "choose" })} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"><ChevronLeft className="h-3 w-3" /> {t("account.back")}</button>
                                <button onClick={() => setAddPersonaMode(null)} className="text-muted-foreground hover:text-foreground"><X className="h-4 w-4" /></button>
                              </div>
                              <p className="text-sm font-medium">{t("account.selectExistingPersona")}</p>
                              <div className="flex flex-wrap gap-2">
                                {(() => {
                                  // Exclut les personae déjà rattachées à CE cas (relation M<->N).
                                  const alreadyLinked = new Set(target.personae.map((p: any) => p.id));
                                  const filtered = accountPersonae.filter((p: any) => (p.personaType || p.persona_type || "B2B") === target.caseType && !alreadyLinked.has(p.id));
                                  if (filtered.length === 0) return <p className="text-xs text-muted-foreground">{t("account.noPersonaTypeAvailable", { type: target.caseType })}</p>;
                                  return filtered.map((p: any) => {
                                    const label = getPersonaLabel(p);
                                    const subtitle = getPersonaSubtitle(p);
                                    return (
                                      <button key={p.id} type="button" className="px-3 py-2 rounded-lg border-2 border-border hover:border-[#877BF2] text-xs text-left transition-all max-w-[280px]"
                                        onClick={async () => {
                                          try {
                                            // Relation M<->N : on LIE la persona existante au cas, sans la dupliquer.
                                            await accountApi.linkPersonaToTarget(target.id, p.id, accountId);
                                            setTargets(targets.map((t) => t.id === target.id ? { ...t, personae: [...t.personae, mapPersonaFromApi(p, target.id) as any] } : t));
                                            showSaveMessage(t("account.personaAddedToast"));
                                          } catch (err: any) { alert(err.message); }
                                        }}
                                      >
                                        <div className="flex items-center gap-1.5">
                                          <PersonaBadges persona={p} size="small" />
                                          <span className="font-medium">{label}</span>
                                        </div>
                                        {subtitle && <p className="text-[10px] text-muted-foreground mt-0.5 line-clamp-2">{subtitle}</p>}
                                      </button>
                                    );
                                  });
                                })()}
                              </div>
                            </div>
                          )}
                          {/* Sous-vue persona : IA */}
                          {addPersonaMode?.targetId === target.id && addPersonaMode.mode === "ai" && (
                            <div className="rounded-lg border border-[#877BF2]/30 bg-purple-50/50 p-3 space-y-2">
                              <div className="flex items-center justify-between">
                                <button onClick={() => setAddPersonaMode({ targetId: target.id, mode: "choose" })} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"><ChevronLeft className="h-3 w-3" /> {t("account.back")}</button>
                                <button onClick={() => setAddPersonaMode(null)} className="text-muted-foreground hover:text-foreground"><X className="h-4 w-4" /></button>
                              </div>
                              <div style={{ height: "60vh", minHeight: "520px" }}>
                                <AiChat agentCode="persona_creator" robotColor="#877BF2" bgColor="#f8f5ff"
                                  context={{ companyDescription: pickCompanyDescription(companyData, i18n.language) || "", offers: accountOffers.map((o: any) => `${o.name}: ${o.description}`).join("\n"), language: i18n.language }}
                                  onItemCreated={async (_type, data) => { try { const created = await accountApi.createAccountPersona(data, accountId); setAccountPersonae((prev) => [...prev, created]); /* Relation M<->N : on lie la persona fraîchement créée au cas, sans la dupliquer. */ await accountApi.linkPersonaToTarget(target.id, created.id, accountId); setTargets(targets.map((t) => t.id === target.id ? { ...t, personae: [...t.personae, { id: created.id, targetId: target.id, function: data.function || data.csp || "-", decisionMakerProfile: data.decisionMakerProfile || "", painPoints: data.painPoints || "", category: data.category || "sales", personaType: data.personaType || "B2B", csp: data.csp || "", ageRange: data.ageRange || "", gender: data.gender || "", purchaseCriteria: data.purchaseCriteria || "", purchasePhase: data.purchasePhase || "", productRelationship: data.productRelationship || "" }] } : t)); } catch (err: any) { console.error("[AiChat] createAccountPersona failed", err); alert(t("account.personaSaveError") + (err?.message || "")); } }}
                                  onComplete={() => setAddPersonaMode({ targetId: target.id, mode: "select" })}
                                  title={t("onboarding.personaeTitle")} subtitle={t("onboarding.personaeSubtitle")}
                                />
                              </div>
                            </div>
                          )}
                        </div>
                        </>
                      )}

                      {/* ── MODE LECTURE ── */}
                      {!isEditingTarget && (
                        <>
                        {/* Cas générique : le commercial joue une entreprise fictive fournie par Kestio. */}
                        {target.kind === "socle" && pickFrEn(target.userCompanyNameFr, target.userCompanyNameEn, i18n.language) && (
                          <div data-testid="generic-case-seller" className="rounded-xl border border-slate-200 p-4 space-y-1">
                            <div className="flex items-center gap-2">
                              <Building2 className="h-4 w-4 text-[#5A4BBE]" />
                              <span className="text-sm font-semibold">
                                {t("account.genericCaseSeller", { name: pickFrEn(target.userCompanyNameFr, target.userCompanyNameEn, i18n.language) })}
                              </span>
                            </div>
                            {pickFrEn(target.userCompanyProfileFr, target.userCompanyProfileEn, i18n.language) && (
                              <p className="text-sm text-slate-700">{pickFrEn(target.userCompanyProfileFr, target.userCompanyProfileEn, i18n.language)}</p>
                            )}
                          </div>
                        )}
                        {/* Row 1 : Offre + Persona côte à côte (Option A — single persona en grand format) */}
                        {((target.offerIds || []).length > 0 || (target.personae || []).length === 1) && (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {/* Offre(s) */}
                            {(target.offerIds || []).length > 0 && (
                              <div className="rounded-xl border border-slate-200 p-4 space-y-2">
                                <div className="flex items-center gap-2">
                                  <Package className="h-4 w-4 text-[#5A4BBE]" />
                                  <span className="text-sm font-semibold">{t("account.offer")}</span>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                  {offresDuCas(target).map((offer: any) => {
                                    return (
                                      <span key={offer.id} className="inline-flex items-center rounded-full px-3 py-1 text-xs font-medium" style={{ backgroundColor: "#988EF2", color: "#FFFFFF" }}>
                                        {pickOfferLabel(offer, i18n.language)}
                                      </span>
                                    );
                                  })}
                                </div>
                                {(() => {
                                  const descriptions = offresDuCas(target)
                                    .filter((o: any) => pickOfferDescription(o, i18n.language))
                                    .map((o: any) => pickOfferDescription(o, i18n.language) as string);
                                  if (descriptions.length === 0) return null;
                                  return (
                                    <div className="text-sm text-slate-700 whitespace-pre-line">
                                      {descriptions.join("\n\n")}
                                    </div>
                                  );
                                })()}
                              </div>
                            )}

                            {/* Persona — grand format UNIQUEMENT si 1 seul persona.
                                Pour ≥2 personae, on n'affiche QUE les vignettes en bas
                                (section "Personae") — pas de bloc redondant "voir détails ci-dessous". */}
                            {(target.personae || []).length === 1 && (
                              <div className="rounded-xl border border-slate-200 p-4 space-y-2">
                                <div className="flex items-center gap-2">
                                  <UserCircle className="h-4 w-4 text-[#384872]" />
                                  <span className="text-sm font-semibold">{isFr ? "Persona" : "Persona"}</span>
                                </div>
                                {target.personae.length === 1 ? (() => {
                                  const p: any = target.personae[0]!;
                                  const isCC = (p.category || "sales") === "customer_care";
                                  const isB2C = target.caseType === "B2C";
                                  const mainLabel = isCC
                                    ? (isB2C ? (localizePersonaOption(p.contactReason, i18n.language) || "SAV") : (getPersonaLabel(p) || "SAV"))
                                    : (getPersonaLabel(p) || "Persona");
                                  return (
                                    <>
                                      <div className="flex flex-wrap items-center gap-2">
                                        <span className="inline-flex items-center rounded-full px-3 py-1 text-xs font-medium" style={{ backgroundColor: "#384872", color: "#FFFFFF" }}>
                                          {mainLabel}
                                        </span>
                                        <span className="text-[10px] font-medium px-2 py-0.5 rounded-full" style={{ backgroundColor: getCategoryColor(p.category), color: getCategoryTextColor(p.category) }}>
                                          {getCategoryLabel(p.category)}
                                        </span>
                                      </div>
                                      {p.decisionMakerProfile && (
                                        <div className="text-sm text-slate-700">
                                          <span className="font-semibold block mb-1">{isFr ? "Profil décideur" : "Decision maker profile"}</span>
                                          <span>{pickDecisionMaker(p)}</span>
                                        </div>
                                      )}
                                      {p.painPoints && (
                                        <div className="text-sm text-slate-700">
                                          <span className="font-semibold block mb-1">{isFr ? "Problématiques adressées" : "Pain points addressed"}</span>
                                          <span className="whitespace-pre-line">{pickPersonaField(p, "painPoints", i18n.language)}</span>
                                        </div>
                                      )}
                                    </>
                                  );
                                })() : (
                                  <p className="text-xs text-muted-foreground italic">
                                    {isFr
                                      ? `${target.personae.length} personae — voir détails ci-dessous`
                                      : `${target.personae.length} personae — see details below`}
                                  </p>
                                )}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Row 2 : Contexte (pleine largeur) */}
                        {target.contextId && (() => {
                          const ctx = accountContexts.find((c) => c.id === target.contextId);
                          if (!ctx) return null;
                          const label = isFr ? (ctx.nameFr || ctx.nameEn) : (ctx.nameEn || ctx.nameFr);
                          const description = isFr ? (ctx.descriptionFr || ctx.descriptionEn) : (ctx.descriptionEn || ctx.descriptionFr);
                          return (
                            <div className="rounded-xl border border-slate-200 p-4 space-y-2">
                              <div className="flex items-center gap-2">
                                <Sparkles className="h-4 w-4 text-[#926B00]" />
                                <span className="text-sm font-semibold">{isFr ? "Contexte" : "Context"}</span>
                              </div>
                              <span className="inline-flex items-center rounded-full px-3 py-1 text-xs font-medium" style={{ backgroundColor: "#F6C847", color: "#926B00" }}>
                                {label}
                              </span>
                              {description && (
                                <div className="text-sm text-slate-700 whitespace-pre-line">
                                  {description}
                                </div>
                              )}
                            </div>
                          );
                        })()}

                        {/* Row 3 : Secteur d'activité + Taille d'entreprise (B2B uniquement, badges côte à côte) */}
                        {target.caseType !== "B2C" && (parseMultiValue(target.industry).length > 0 || target.companySize) && (
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {parseMultiValue(target.industry).length > 0 && (
                              <div>
                                <div className="flex items-center gap-2 mb-2">
                                  <Factory className="h-4 w-4 text-[#384872]" />
                                  <span className="text-sm font-semibold">{t("account.industry")}</span>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                  {parseMultiValue(target.industry).map((v) => (
                                    <span key={v} className="inline-flex items-center rounded-full px-3 py-1 text-xs font-medium" style={{ backgroundColor: "#eef6f1", border: "1px solid #c8e0d0", color: "#2d6a4f" }}>
                                      {localizeIndustry(v, i18n.language)}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                            {target.companySize && (
                              <div>
                                <div className="flex items-center gap-2 mb-2">
                                  <Building2 className="h-4 w-4 text-[#384872]" />
                                  <span className="text-sm font-semibold">{t("account.companySize")}</span>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                  <span className="inline-flex items-center rounded-full px-3 py-1 text-xs font-medium" style={{ backgroundColor: "#eef6f1", border: "1px solid #c8e0d0", color: "#2d6a4f" }}>
                                    {localizeCompanySize(target.companySize, i18n.language)}
                                  </span>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                        </>
                      )}

                      {/* Personae — cartes (lecture + édition inline).
                          En mode lecture avec 1 seul persona, on cache cette section :
                          le persona est déjà affiché en grand dans la Row 1 (Offre+Persona). */}
                      {(isEditingTarget || target.personae.length !== 1) && (
                      <div>
                        {!isEditingTarget && (
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <UserCircle className="h-4 w-4 text-[#384872]" />
                            <span className="text-sm font-semibold">{t("account.personae")}</span>
                          </div>
                        </div>
                        )}

                        {target.personae.length === 0 ? (
                          <p className="text-sm text-muted-foreground py-4 text-center">{t("account.noPersonae")}</p>
                        ) : (
                          <>
                            {/* Persona cards grid */}
                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
                              {target.personae.map((persona) => {
                                const isEditingP = editingPersonaId === persona.id;
                                const isExpanded = expandedPersonaId === persona.id;
                                if (isEditingP) return null; // rendered below
                                const peutRetirer = target.personae.length > 1 && peutModifierCas;
                                return (
                                  // Conteneur relatif : la croix « Retirer » est posée SUR la carte,
                                  // visible sans avoir à déplier la persona (une action qu'il faut
                                  // découvrir en cliquant est une action que personne ne trouve).
                                  <div key={persona.id} className="relative">
                                  <button
                                    onClick={() => setExpandedPersonaId(isExpanded ? null : persona.id)}
                                    className={`w-full rounded-xl p-3 pr-8 text-left transition-all hover:opacity-90 ${isExpanded ? "ring-2 ring-offset-1 ring-[#384872]" : ""}`}
                                    style={{ backgroundColor: target.caseType === "B2C" ? "#7CBF94" : "#384872", minHeight: "80px" }}
                                  >
                                    {(() => {
                                      const isCC = (persona.category || "sales") === "customer_care";
                                      const isB2C = target.caseType === "B2C";
                                      if (isCC) {
                                        return (
                                          <>
                                            <span className="text-white text-xs font-semibold leading-tight line-clamp-2">{isB2C ? (localizePersonaOption(persona.contactReason, i18n.language) || "SAV") : (getPersonaLabel(persona) || "SAV")}</span>
                                            <span className="block text-white/80 text-[10px] mt-0.5">{[localizePersonaOption(persona.expressionStyle, i18n.language), localizePersonaOption(persona.mainExpectation, i18n.language)].filter(Boolean).join(" • ")}</span>
                                          </>
                                        );
                                      }
                                      if (isB2C) {
                                        return (
                                          <>
                                            <span className="text-white text-xs font-semibold leading-tight line-clamp-2">{getPersonaLabel(persona)}</span>
                                            {persona.ageRange && <span className="block text-white/80 text-[10px] mt-0.5">{persona.ageRange}</span>}
                                          </>
                                        );
                                      }
                                      return <span className="text-white text-xs font-semibold leading-tight line-clamp-3">{getPersonaLabel(persona)}</span>;
                                    })()}
                                    <span className="block mt-1.5 text-[10px] font-medium" style={{ color: getCategoryColor(persona.category) }}>
                                      {getCategoryLabel(persona.category)}
                                    </span>
                                  </button>
                                  {peutRetirer && (
                                    <button
                                      type="button"
                                      title={t("account.unlinkPersona")}
                                      aria-label={`${t("account.unlinkPersona")} : ${getPersonaLabel(persona)}`}
                                      // Zone tactile étendue à ~44px (recommandation d'accessibilité
                                      // mobile) via un pseudo-élément : le rond visuel reste discret
                                      // sur la carte, mais la cible réelle au doigt est confortable.
                                      className="absolute top-1 right-1 h-7 w-7 rounded-full bg-white/90 text-slate-700 hover:bg-white hover:text-red-600 flex items-center justify-center shadow-sm transition-colors before:absolute before:content-[''] before:-inset-2"
                                      onClick={async (e) => {
                                        e.stopPropagation();
                                        try {
                                          await accountApi.unlinkPersonaFromTarget(target.id, persona.id, accountId);
                                          setExpandedPersonaId(null);
                                          setTargets((prev) => prev.map((t) => t.id === target.id
                                            ? { ...t, personae: t.personae.filter((x: any) => x.id !== persona.id) }
                                            : t));
                                          showSaveMessage(t("account.personaUnlinkedToast"));
                                        } catch (err: any) { alert(err.message); }
                                      }}
                                    >
                                      <X className="h-3.5 w-3.5" />
                                    </button>
                                  )}
                                  </div>
                                );
                              })}
                            </div>

                            {/* Expanded persona details */}
                            {expandedPersonaId && target.personae.some(p => p.id === expandedPersonaId) && (() => {
                              const persona = target.personae.find(p => p.id === expandedPersonaId)!;
                              return (
                                <div className="mt-3 rounded-xl border border-[#384872]/20 bg-slate-50 p-4 space-y-3">
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                      <span className="font-semibold text-sm">{getPersonaLabel(persona)}</span>
                                      <span className="text-[10px] font-medium px-2 py-0.5 rounded-full" style={{ backgroundColor: getCategoryColor(persona.category), color: getCategoryTextColor(persona.category) }}>
                                        {getCategoryLabel(persona.category)}
                                      </span>
                                    </div>
                                    <div className="flex items-center gap-4">
                                      <div className="flex gap-1">
                                        {/* Modifier la persona = modifier TOUS ses cas (D1 du 01/10/2026) :
                                            pas de crayon pour un MNG si elle sert aussi au cas d'un autre. */}
                                        {peutModifierCas && (persona as any).viewerCanEdit === false && (
                                          <span data-testid="persona-shared-readonly" className="text-[11px] text-muted-foreground self-center">{t("account.personaSharedReadOnly")}</span>
                                        )}
                                        {peutModifierCas && (persona as any).viewerCanEdit !== false && (
                                        <Button variant="ghost" size="icon" className="h-7 w-7" title={t("account.edit")} aria-label={t("account.edit")} onClick={() => { setExpandedPersonaId(null); setEditingPersonaId(persona.id); }}>
                                          <Pencil className="h-3.5 w-3.5" />
                                        </Button>
                                        )}
                                        <Button variant="ghost" size="icon" className="h-7 w-7 text-green-600 hover:text-green-700 hover:bg-green-50" title={t("account.close")} aria-label={t("account.close")} onClick={() => setExpandedPersonaId(null)}>
                                          <X className="h-3.5 w-3.5" />
                                        </Button>
                                      </div>
                                      {/* Retirer ≠ Supprimer : une persona peut servir dans PLUSIEURS cas
                                          (relation M<->N). « Retirer » la détache de CE cas seulement ;
                                          « Supprimer » l'efface partout. Sans ce bouton, on ne pouvait
                                          qu'ajouter des personae à un cas, jamais en enlever. */}
                                      {peutModifierCas && (<>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-7 px-2 text-xs text-amber-700 hover:bg-amber-50 disabled:opacity-40"
                                        title={target.personae.length <= 1 ? t("account.unlinkPersonaLastTooltip") : t("account.unlinkPersona")}
                                        aria-label={t("account.unlinkPersona")}
                                        disabled={target.personae.length <= 1}
                                        onClick={async () => {
                                          try {
                                            await accountApi.unlinkPersonaFromTarget(target.id, persona.id, accountId);
                                            setExpandedPersonaId(null);
                                            setTargets((prev) => prev.map((t) => t.id === target.id
                                              ? { ...t, personae: t.personae.filter((p: any) => p.id !== persona.id) }
                                              : t));
                                            showSaveMessage(t("account.personaUnlinkedToast"));
                                          } catch (err: any) { alert(err.message); }
                                        }}
                                      >
                                        <X className="h-3.5 w-3.5 mr-1" />{t("account.unlinkPersona")}
                                      </Button>
                                      <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:bg-red-50" title={t("account.delete")} aria-label={t("account.delete")} onClick={() => { setExpandedPersonaId(null); handleDeletePersona(target.id, persona.id); }}>
                                        <Trash2 className="h-3.5 w-3.5" />
                                      </Button>
                                      </>)}
                                    </div>
                                  </div>
                                  <PersonaDetailView persona={persona} isFr={i18n.language === "fr"} />
                                </div>
                              );
                            })()}

                            {/* Editing form — shown inline below the grid */}
                            {/* Formulaire d'édition — composant persona UNIQUE partagé (PersonaUtils) */}
                            {target.personae.filter(p => editingPersonaId === p.id).map((persona) => (
                              <div key={persona.id} className="mt-3">
                                <PersonaForm
                                  persona={persona}
                                  caseType={target.caseType as "B2B" | "B2C" | undefined}
                                  category={persona.category as "sales" | "customer_care" | undefined}
                                  onSave={(form) => saveCasePersona(target.id, persona.id, form)}
                                  onCancel={() => handleCancelPersona(target.id, persona.id)}
                                />
                              </div>
                            ))}
                          </>
                        )}
                      </div>
                      )}

                      {target.archivedAt && (
                        <p className="text-xs text-muted-foreground">{t("account.archivedHint")}</p>
                      )}

                      {/* Désactiver / Réactiver — propriétaire MNG, ORG, ADM, COO (Q1 du 01/10/2026) */}
                      {!newTargetIds.has(target.id) && peutDesactiverCas && (
                        <div className="pt-1 flex justify-end">
                          {target.archivedAt ? (
                            <Button data-testid="target-unarchive" variant="outline" size="sm" className="text-xs" onClick={() => setTargetArchived(target, false)}>
                              <RotateCcw className="h-3 w-3 mr-1" />
                              {t("account.unarchiveTarget")}
                            </Button>
                          ) : (
                            <Button data-testid="target-archive" variant="ghost" size="sm" className="text-xs" onClick={() => setArchivingTarget(target)}>
                              <Square className="h-3 w-3 mr-1" />
                              {t("account.archiveTarget")}
                            </Button>
                          )}
                        </div>
                      )}

                      {/* Supprimer le cas — jamais proposé à un MNG (Q1 du 01/10/2026) */}
                      {!newTargetIds.has(target.id) && peutSupprimerCas && (
                        <div className="pt-1 flex justify-end">
                          <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive text-xs" onClick={() => removeTarget(target.id)}>
                            <X className="h-3 w-3 mr-1" />
                            {t("account.deleteTarget")}
                          </Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })
            )}
          </div>
        </TabsContent>

        {/* Licences Tab */}
        <TabsContent value="licences" className="md:mt-0">
          <div data-tour="account-licences" className="mb-6 px-4 py-3 rounded-lg bg-blue-50 border border-blue-100 text-sm text-[#384872] flex items-start gap-2">
            <Info className="h-4 w-4 mt-0.5 shrink-0" />
            <span>{t("account.licencesIntro")}</span>
          </div>

          {/* ============ SUBSCRIPTION LICENCES ============ */}
          {/* `|| showBuyModal` : la fenêtre d'achat est rendue dans ce bloc. Sans cette
              condition, arriver par `?buy=1` sur un compte sans carte affichable (lignes
              d'abonnement jamais abouties) n'ouvrait RIEN, en silence. */}
          {!isManager && (cartesAbonnement(subscriptionData?.subscriptions).length > 0 || showBuyModal) && (
            <div className="mb-6 space-y-4">
              {cartesAbonnement(subscriptionData.subscriptions)
                .map((sub: any) => {
                  const tierLabel = sub.planTier === "starter" ? t("account.tierStarter") : sub.planTier === "focus" ? t("account.tierFocus") : sub.planTier === "premium" ? t("account.tierPremium") : sub.planTier;
                  const tierColor = sub.planTier === "starter" ? "#7CBF94" : sub.planTier === "focus" ? "#877BF2" : "#384872";
                  const seats = sub.seats || [];
                  const available = sub.quantity - seats.length;
                  const maxSim = sub.maxSimulators === 0 ? "tous" : sub.maxSimulators;
                  const simLabel = sub.maxSimulators === 0 ? t("account.allSimulators") : sub.maxSimulators === 1 ? t("account.oneSimulator") : t("account.manySimulators", { count: sub.maxSimulators });

                  return (
                    <Card key={sub.id} className="border-l-4" style={{ borderLeftColor: tierColor }}>
                      <CardContent className="!py-4">
                        {/* Header */}
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div className="h-10 w-10 rounded-full flex items-center justify-center text-white text-sm font-bold" style={{ backgroundColor: tierColor }}>
                              {seats.length}/{sub.quantity}
                            </div>
                            <div>
                              <div className="flex items-center gap-2 flex-wrap">
                                <h3 className="font-semibold text-base" style={{ fontFamily: "Sora, sans-serif" }}>
                                  {t("account.licenceTitle", { tier: tierLabel })}
                                </h3>
                                <SubscriptionStateBadge access={sub.access} />
                              </div>
                              <p className="text-xs text-muted-foreground">
                                {simLabel} • {sub.billingInterval === "yearly" ? t("account.billingYearly") : sub.billingInterval === "quarterly" ? t("account.billingQuarterly") : t("account.billingMonthly")} • {available} {t("account.available")}{available > 1 ? "s" : ""}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {isOrganisateur && abonnementOuvert(sub) && available > 0 && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setAssignSeatModal({ subscriptionId: sub.id, planTier: sub.planTier, maxSimulators: sub.maxSimulators });
                                  setAssignSeatUserId("");
                                  setAssignSeatSimulatorIds([]);
                                }}
                              >
                                {t("account.attribute")}
                              </Button>
                            )}
                            {/* Abonnement clos : le seul chemin de réabonnement était la fenêtre
                                de blocage d'une session. On le propose ici aussi. */}
                            {peutAcheter && !abonnementOuvert(sub) && (
                              <Button size="sm" onClick={() => setShowBuyModal(true)}>
                                {t("quota.ctaSubscribe")}
                              </Button>
                            )}
                            {/* Ajouter des sièges sur ce même abonnement (modale d'achat) */}
                            {peutAcheter && abonnementOuvert(sub) && (
                              <Button size="sm" variant="ghost" onClick={() => setShowBuyModal(true)}>
                                + {t("account.addLicences")}
                              </Button>
                            )}
                          </div>
                        </div>

                        {/* Seats list */}
                        {seats.length > 0 ? (
                          <div className="space-y-2">
                            {seats.map((seat: any) => {
                              const seatSims = (seat.simulatorIds || [])
                                .map((simId: string) => subscriptionData.simulators?.find((s: any) => s.id === simId))
                                .filter(Boolean);
                              return (
                                <div key={seat.id} className="flex items-center justify-between py-2 px-3 rounded-lg bg-muted/50">
                                  <div className="flex items-center gap-3">
                                    <div className="h-8 w-8 rounded-full bg-slate-200 flex items-center justify-center text-xs font-bold text-slate-600">
                                      {seat.userName?.split(" ").map((n: string) => n[0]).join("").substring(0, 2).toUpperCase()}
                                    </div>
                                    <div>
                                      <span className="text-sm font-medium">{seat.userName}</span>
                                      {seatSims.length > 0 && (
                                        <div className="flex gap-1 mt-0.5">
                                          {seatSims.map((sim: any) => (
                                            <span key={sim.id} className="text-xs px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">{sim.name}</span>
                                          ))}
                                        </div>
                                      )}
                                      {seatSims.length === 0 && (
                                        <p className="text-xs text-orange-500">{t("account.noSimulatorChosen")}</p>
                                      )}
                                    </div>
                                  </div>
                                  {/* Licence définitive — pas de modification ni retrait */}
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <p className="text-sm text-muted-foreground italic">{t("account.noLicenceAttributed")}</p>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}

              {/* L'achat de nouveaux sièges passe par « + Ajouter des licences » sur
                  chaque carte d'abonnement ; la découverte d'autres formules par le
                  bouton landing en bas d'onglet. */}

              {/* Modale achat nouvelles licences */}
              {showBuyModal && (() => {
                // Group licences by tier from DB
                const allLicences = licencePoolConfig as any[];
                const tiers = ["starter", "focus", "premium"];
                const tierLabels: Record<string, string> = { starter: t("account.tierStarter"), focus: t("account.tierFocus"), premium: t("account.tierPremium") };
                const tierColors: Record<string, string> = { starter: "#7CBF94", focus: "#877BF2", premium: "#384872" };
                const tierSimDesc: Record<string, string> = { starter: t("account.tierStarterDesc"), focus: t("account.tierFocusDesc"), premium: t("account.tierPremiumDesc") };

                // Find matching licence for current selection
                const selectedTier = getTierFromPlanCode(buyPlan) || "premium";
                const matchingLicence = allLicences.find((l: any) =>
                  l.tier === selectedTier && l.billingInterval === buyInterval
                );
                const priceHt = matchingLicence?.priceHt ? (matchingLicence.priceHt / 100) : null;

                return (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" role="dialog" aria-modal="true" onClick={() => setShowBuyModal(false)}>
                  <Card className="w-full max-w-md mx-4" onClick={(e) => e.stopPropagation()}>
                    <CardHeader>
                      <CardTitle className="text-lg">{t("account.newLicences")}</CardTitle>
                      <CardDescription>{t("account.newLicencesDesc")}</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {/* Plan */}
                      <div className="space-y-2">
                        <Label>{t("account.licenceType")}</Label>
                        <div className="grid grid-cols-3 gap-2">
                          {tiers.map((tier) => {
                            const isSelected = selectedTier === tier;
                            const tierLicence = allLicences.find((l: any) => l.tier === tier && l.billingInterval === buyInterval);
                            const price = tierLicence?.priceHt ? (tierLicence.priceHt / 100) : null;
                            return (
                              <button
                                key={tier}
                                type="button"
                                className={`p-3 rounded-lg border-2 text-center transition-all ${isSelected ? "border-blue-500 bg-blue-50" : "border-border hover:border-blue-300"}`}
                                onClick={() => setBuyPlan(getSubscriptionPlanCode(tier as PlanTier, buyInterval))}
                              >
                                <div className="h-8 w-8 rounded-full mx-auto mb-1 flex items-center justify-center text-white text-xs font-bold" style={{ backgroundColor: tierColors[tier] }}>
                                  {tier[0].toUpperCase()}
                                </div>
                                <p className="text-sm font-semibold">{tierLabels[tier]}</p>
                                <p className="text-xs text-muted-foreground">{tierSimDesc[tier]}</p>
                                {price && <p className="text-sm font-bold mt-1" style={{ color: tierColors[tier] }}>{price} €<span className="text-xs font-normal text-muted-foreground">{t("account.perMonthHt")}</span></p>}
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      {/* Engagement */}
                      <div className="space-y-2">
                        <Label>{t("account.engagement")}</Label>
                        <div className="grid grid-cols-2 gap-2">
                          {(["monthly", "yearly"] as const).map((interval) => {
                            const isSelected = buyInterval === interval;
                            return (
                              <button
                                key={interval}
                                type="button"
                                className={`p-3 rounded-lg border-2 text-center transition-all ${isSelected ? "border-blue-500 bg-blue-50" : "border-border hover:border-blue-300"}`}
                                onClick={() => {
                                  setBuyInterval(interval);
                                  setBuyPlan(getSubscriptionPlanCode(getTierFromPlanCode(buyPlan) || "premium", interval));
                                }}
                              >
                                <p className="text-sm font-semibold">{interval === "monthly" ? t("account.monthly") : t("account.yearly")}</p>
                                <p className="text-xs text-muted-foreground">{interval === "monthly" ? t("account.noCommitment") : t("account.preferentialRate")}</p>
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      {/* Quantité */}
                      <div className="space-y-2">
                        <Label>{t("account.licenceCount")}</Label>
                        <div className="flex items-center gap-3">
                          <Button type="button" variant="outline" size="sm" className="h-10 w-10 text-lg font-bold"
                            onClick={() => setBuyQuantity((q) => Math.max(1, q - 1))}>−</Button>
                          <span className="text-2xl font-bold w-12 text-center">{buyQuantity}</span>
                          <Button type="button" variant="outline" size="sm" className="h-10 w-10 text-lg font-bold"
                            onClick={() => setBuyQuantity((q) => q + 1)}>+</Button>
                        </div>
                        {priceHt && (
                          <p className="text-sm text-muted-foreground">
                            <Trans i18nKey="account.totalHt" values={{ value: (priceHt * buyQuantity).toFixed(2) }} components={{ strong: <strong /> }} />
                            {buyInterval === "yearly" && <span>{t("account.yearlyTotal", { value: (priceHt * buyQuantity * 12).toFixed(2) })}</span>}
                          </p>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="flex justify-end gap-2 pt-2">
                        <Button variant="outline" onClick={() => setShowBuyModal(false)}>{t("common.cancel")}</Button>
                        <Button
                          disabled={buyLoading}
                          onClick={async () => {
                            setBuyLoading(true);
                            try {
                              // Carte enregistrée → débit direct sans re-saisie ; sinon redirection.
                              const result = await accountApi.buyAgain(buyPlan, buyQuantity);
                              if (result.status === "paid") {
                                alert(t("account.buyAgainPaid"));
                                window.location.reload();
                              } else if (result.status === "redirect" && result.url) {
                                window.location.href = result.url;
                              } else {
                                alert(t("account.buyAgainPending"));
                              }
                            } catch (err: any) {
                              alert(err.message);
                            } finally {
                              setBuyLoading(false);
                            }
                          }}
                        >
                          {buyLoading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> {t("account.redirecting")}</> : t("account.goToPayment")}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </div>
                );
              })()}

              {/* Assign Seat Modal */}
              {assignSeatModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" role="dialog" aria-modal="true" onClick={() => setAssignSeatModal(null)}>
                  <Card className="w-full max-w-md mx-4" onClick={(e) => e.stopPropagation()}>
                    <CardHeader>
                      <CardTitle className="text-lg">{t("account.attributeLicence")}</CardTitle>
                      <CardDescription>
                        {assignSeatModal.maxSimulators === 0
                          ? t("account.allSimulatorsIncluded")
                          : t("account.chooseUpToSimulators", { count: assignSeatModal.maxSimulators })}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {/* User select */}
                      <div className="space-y-2">
                        <Label>{t("account.collaborator")}</Label>
                        <select
                          className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                          value={assignSeatUserId}
                          onChange={(e) => setAssignSeatUserId(e.target.value)}
                        >
                          <option value="">{t("account.selectPlaceholder")}</option>
                          {collaborators
                            .filter((c) => c.status !== "disabled")
                            .map((c) => (
                              <option key={c.id} value={c.id}>{c.firstName} {c.lastName}</option>
                            ))}
                        </select>
                      </div>

                      {/* Simulator selection */}
                      {assignSeatModal.maxSimulators !== 0 && (
                        <div className="space-y-2">
                          <Label>{t("account.simulator")}{assignSeatModal.maxSimulators > 1 ? "s" : ""}</Label>
                          <p className="text-xs text-muted-foreground">{t("account.coachesIncluded")}</p>
                          <div className="grid grid-cols-2 gap-2">
                            {(subscriptionData?.simulators || []).map((sim: any) => {
                              const isSelected = assignSeatSimulatorIds.includes(sim.id);
                              const maxReached = !isSelected && assignSeatSimulatorIds.length >= assignSeatModal!.maxSimulators;
                              return (
                                <button
                                  key={sim.id}
                                  type="button"
                                  className={`text-left p-2 rounded-lg border-2 transition-all text-sm ${
                                    isSelected
                                      ? "border-blue-500 bg-blue-50"
                                      : maxReached
                                        ? "border-gray-100 bg-gray-50 opacity-40 cursor-not-allowed"
                                        : "border-border hover:border-blue-300"
                                  }`}
                                  disabled={maxReached}
                                  onClick={() => {
                                    if (isSelected) {
                                      setAssignSeatSimulatorIds((ids) => ids.filter((id) => id !== sim.id));
                                    } else {
                                      setAssignSeatSimulatorIds((ids) => [...ids, sim.id]);
                                    }
                                  }}
                                >
                                  <div className="flex items-center gap-2">
                                    {sim.image && <img src={sim.image} alt="" loading="lazy" className="h-6 w-6 rounded-full" />}
                                    <span className="font-medium">{sim.name}</span>
                                  </div>
                                  <span className="text-xs text-muted-foreground">{sim.type === "coach" ? t("account.coach") : t("account.simulator")}</span>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Actions */}
                      <div className="flex justify-end gap-2 pt-2">
                        <Button variant="outline" onClick={() => setAssignSeatModal(null)}>{t("common.cancel")}</Button>
                        <Button
                          disabled={!assignSeatUserId || (assignSeatModal.maxSimulators !== 0 && assignSeatSimulatorIds.length === 0)}
                          onClick={async () => {
                            try {
                              const simIds = assignSeatModal.maxSimulators === 0
                                ? (subscriptionData?.simulators || []).map((s: any) => s.id)
                                : assignSeatSimulatorIds;
                              await accountApi.assignSeat(assignSeatModal.subscriptionId, assignSeatUserId, simIds);
                              const data = await accountApi.getSubscriptions(accountId);
                              setSubscriptionData(data);
                              setAssignSeatModal(null);
                            } catch (err: any) {
                              alert(err.message);
                            }
                          }}
                        >
                          {assignSeatUserId && subscriptionData?.subscriptions?.some((s: any) =>
                            s.seats?.some((seat: any) => seat.userId === assignSeatUserId && s.id === assignSeatModal.subscriptionId)
                          ) ? t("account.modify") : t("account.assign")}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}
            </div>
          )}

          {/* ============ LICENCES VIA GRANTS (ancien modèle + packs one-shot) ============ */}
          {/* Les licences posées hors subscription Zoho (grants : trimestriel ancien modèle,
              packs one-shot, licences custom ADM) n'apparaissaient nulle part dans cet onglet
              → onglet vide alors que le compte a des licences actives. On les liste ici. */}
          {!isManager && licencePool.filter((lp) => lp.total > 0).length > 0 && (
            <div className="mb-6 space-y-3">
              {licencePool.filter((lp) => lp.total > 0).map((lp) => {
                const available = lp.total - lp.used;
                const isOneShot = !!(lp as any).isOneShot;
                // Packs one-shot rattachés à ce produit (1 grant = 1 pack, chacun son pool de simulateurs).
                const matchingPacks = isOneShot ? oneShotPacks.filter((p) => p.licenceCode === lp.code) : [];
                const simName = (id: string) => oneShotSims.find((s) => s.id === id)?.name || id;
                return (
                  <Card key={lp.type} className="border-l-4" style={{ borderLeftColor: lp.color }}>
                    <CardContent className="!py-4 space-y-3">
                      <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 rounded-full flex items-center justify-center text-white text-sm font-bold" style={{ backgroundColor: lp.color }}>
                            {lp.used}/{lp.total}
                          </div>
                          <div>
                            <h3 className="font-semibold text-base" style={{ fontFamily: "Sora, sans-serif" }}>{lp.label}</h3>
                            <p className="text-xs text-muted-foreground">
                              {isOneShot
                                ? t("account.packCount", { count: matchingPacks.length || lp.total })
                                : `${available} ${t("account.available")}${available > 1 ? "s" : ""}`}
                            </p>
                          </div>
                        </div>
                        {/* Rachat du même produit — toujours visible (anticiper l'épuisement) */}
                        {peutAcheter && (lp as any).zohoPlanCode && (
                          <div className="flex items-center gap-2">
                            <Input
                              type="number"
                              min={1}
                              max={99}
                              value={buyAgainQty[lp.code] || 1}
                              onChange={(e) => setBuyAgainQty((prev) => ({ ...prev, [lp.code]: Math.max(1, parseInt(e.target.value) || 1) }))}
                              className="w-16 h-8"
                            />
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={buyAgainLoading === lp.code}
                              onClick={() => handleBuyAgain(lp as any)}
                            >
                              {isOneShot ? t("account.buyAgain") : t("account.addLicences")}
                            </Button>
                          </div>
                        )}
                      </div>

                      {/* Détail par pack one-shot : crédits + simulateurs choisis (ou bouton Configurer) */}
                      {isOneShot && matchingPacks.map((pack) => (
                        <div key={pack.id} className="rounded-lg border bg-slate-50/60 px-3 py-2">
                          <div className="flex items-center justify-between gap-2 flex-wrap">
                            <span className="text-sm font-medium" style={{ color: "#384872" }}>
                              {t("account.packCredits", { count: pack.rateLimitSessions ?? 0 })}
                              {pack.expiresAt ? ` · ${t("account.packExpires", { date: new Date(pack.expiresAt).toLocaleDateString(i18n.language) })}` : ""}
                            </span>
                            {pack.needsConfig && isOrganisateur && (
                              <Button size="sm" variant="outline" onClick={() => setConfigurePacksOpen(true)}>
                                {t("account.packConfigureBtn")}
                              </Button>
                            )}
                          </div>
                          <div className="mt-1 text-xs text-muted-foreground">
                            {pack.isPremium ? (
                              t("account.packAllSimulators")
                            ) : pack.simulatorIds.length > 0 ? (
                              <span className="flex flex-wrap gap-1.5 mt-1">
                                {pack.simulatorIds.map((sid) => (
                                  <span key={sid} className="inline-block rounded-full bg-white border px-2 py-0.5 text-[11px]" style={{ color: "#384872" }}>
                                    {simName(sid)}
                                  </span>
                                ))}
                              </span>
                            ) : (
                              <span style={{ color: "#C0423F" }}>{t("account.packNoSimulator")}</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}

          {/* Découvrir les autres formules (upgrade abo, pack → abo, etc.) → landing */}
          {!isManager && peutAcheter && (
            <div className="flex justify-center mt-2">
              <Button
                variant="outline"
                className="gap-2"
                onClick={() => window.open("https://bizcoach.kestio.com/#pricing", "_blank", "noopener,noreferrer")}
              >
                <ShoppingCart className="h-4 w-4" /> {t("account.discoverOffers")}
              </Button>
            </div>
          )}

          {/* Modale de choix des simulateurs d'un pack — ouverte via « Configurer » ou après un achat (Lot D). */}
          <Dialog open={configurePacksOpen} onOpenChange={(open) => {
            setConfigurePacksOpen(open);
            if (!open) {
              packsApi.getOneShot(accountId).then((d) => { setOneShotPacks(d.packs || []); setOneShotSims(d.availableSimulators || []); }).catch(() => {});
            }
          }}>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>{t("account.packConfigureTitle")}</DialogTitle>
              </DialogHeader>
              <OneShotPackConfig onAllConfigured={() => {
                setConfigurePacksOpen(false);
                // Rafraîchir l'affichage par pack (Radix n'appelle pas onOpenChange sur fermeture programmatique).
                packsApi.getOneShot(accountId).then((d) => { setOneShotPacks(d.packs || []); setOneShotSims(d.availableSimulators || []); }).catch(() => {});
              }} />
            </DialogContent>
          </Dialog>

        </TabsContent>

        {/* Users Tab */}
        <TabsContent value="users" className="md:mt-0">

          {/* ============ VUE MANAGER : LISTE HIÉRARCHIQUE ============ */}
          {isManager && (() => {
            const orderedCollabs = buildHierarchicalOrder(collaborators, user.id);
            return (
              <div className="space-y-3">
                {orderedCollabs.length === 0 ? (
                  <p className="text-sm text-muted-foreground italic text-center py-8">{t("account.noCollaboratorsAttached")}</p>
                ) : (
                  orderedCollabs.map((collab) => {
                    const isMgr = collab.profileType === "MNG" || collab.profileType === "ORG";
                    return (
                  <div
                    key={collab.id}
                    style={{ marginLeft: `${collab._depth * 32}px` }}
                    role="button"
                    tabIndex={0}
                    aria-label={t("account.fiche.open", { name: `${collab.firstName} ${collab.lastName}` })}
                    onClick={() => setFicheUserId(collab.id)}
                    onKeyDown={(e) => { if (e.key === "Enter" && e.target === e.currentTarget) setFicheUserId(collab.id); }}
                    className={`flex items-center justify-between py-3 px-4 rounded-lg border bg-card transition-colors cursor-pointer hover:bg-muted/30 ${
                      isMgr ? "border-l-4 border-l-[#877BF2] bg-[#877BF2]/5" : ""
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`h-9 w-9 rounded-full flex items-center justify-center text-sm font-bold ${
                        isMgr ? "bg-[#877BF2]/20 text-[#5A4BBE]" : "bg-slate-200 text-slate-600"
                      }`}>
                        {collab.firstName[0]}{collab.lastName[0]}
                      </div>
                      <div>
                        <p className="text-sm font-medium flex items-center gap-2">
                          {collab.firstName} {collab.lastName}
                          {isMgr && (
                            <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-[#877BF2] text-[#5A4BBE]">
                              {collab.profileType === "MNG" ? t("account.manager") : t("account.organisateur")}
                            </Badge>
                          )}
                        </p>
                        <p className="text-xs text-muted-foreground">{collab.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                      <Badge variant={collab.status === "active" ? "default" : "secondary"} className="text-xs">
                        {collab.status === "active" ? t("account.userStatusActive") : collab.status === "invited" ? t("account.userStatusInvited") : collab.status === "disabled" ? t("account.userStatusDisabled") : t("account.userStatusCreated")}
                      </Badge>
                      {collab.status === "created" && collab.id !== user.id && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs"
                          disabled={resendingLinkId === collab.id}
                          onClick={() => inviteCollaboratorNow(collab.id)}
                        >
                          {t("account.invite")}
                        </Button>
                      )}
                    </div>
                  </div>
                    );
                  })
                )}
              </div>
            );
          })()}

          {/* ============ VUE MOBILE ============ */}
          {!isManager && <div className="md:hidden">
            {subscriptionData?.subscriptions?.some((s: any) => abonnementOuvert(s)) ? (
              /* Vue subscription : liste collaborateurs simple */
              <div className="space-y-3">
                <h3 className="font-semibold text-base">{t("account.collaboratorsHeading")}</h3>
                {collaborators.map((collab) => (
                  <div
                    key={collab.id}
                    role="button"
                    tabIndex={0}
                    aria-label={t("account.fiche.open", { name: `${collab.firstName} ${collab.lastName}` })}
                    onClick={() => setFicheUserId(collab.id)}
                    onKeyDown={(e) => { if (e.key === "Enter" && e.target === e.currentTarget) setFicheUserId(collab.id); }}
                    className="flex items-center justify-between py-2.5 px-3 rounded-lg border bg-card cursor-pointer hover:bg-muted/30"
                  >
                    <div>
                      <p className="text-sm font-medium">{collab.firstName} {collab.lastName}</p>
                      <p className="text-xs text-muted-foreground">{collab.email}</p>
                    </div>
                    <Badge variant={collab.status === "active" ? "default" : "secondary"} className="text-xs">
                      {collab.status === "active" ? t("account.userStatusActive") : collab.status === "invited" ? t("account.userStatusInvited") : collab.status === "disabled" ? t("account.userStatusDisabled") : t("account.userStatusCreated")}
                    </Badge>
                  </div>
                ))}
              </div>
            ) : selectedAgent === null ? (
              /* Vue 1 : Grille des cartes agents (ancien système) */
              <div className="grid grid-cols-2 gap-3">
                {licencePool.filter((lp) => lp.total > 0).map((lp) => {
                  const available = lp.total - lp.used;
                  return (
                    <button
                      key={lp.type}
                      type="button"
                      className="text-left"
                      onClick={() => {
                        setSelectedAgent(lp.type);
                        setMobileUserFilter("active");
                      }}
                    >
                      <Card className="!py-4 !gap-0 hover:shadow-md transition-shadow">
                        <CardContent className="!py-0 flex flex-col items-center gap-2">
                          <div
                            className="h-12 w-12 rounded-full flex items-center justify-center text-white text-sm font-bold"
                            style={{ backgroundColor: lp.color }}
                          >
                            {lp.used}/{lp.total}
                          </div>
                          <span className="text-sm font-medium">{lp.label}</span>
                          <span className="text-xs text-muted-foreground">
                            {t("account.availableCount", { count: available })}
                          </span>
                        </CardContent>
                      </Card>
                    </button>
                  );
                })}
              </div>
            ) : (
              /* Vue 2 : Drill-down liste utilisateurs */
              (() => {
                const agentPool = licencePool.find((lp) => lp.type === selectedAgent)!;
                const savedCollabsMobile = collaborators.filter((c) => !newCollabIds.has(c.id));
                const activeCollabs = savedCollabsMobile.filter((c) => c.licences.includes(selectedAgent));
                const inactiveCollabs = savedCollabsMobile.filter((c) => !c.licences.includes(selectedAgent));
                const filteredCollabs = mobileUserFilter === "active" ? activeCollabs : inactiveCollabs;

                return (
                  <div className="space-y-4">
                    {/* Header retour */}
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-2 flex-1">
                        <span className="font-semibold text-lg">{agentPool.label}</span>
                        <span
                          className="inline-block h-3 w-3 rounded-full"
                          style={{ backgroundColor: agentPool.color }}
                        />
                      </div>
                      <span
                        className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold text-white"
                        style={{ backgroundColor: agentPool.color }}
                      >
                        {agentPool.used}/{agentPool.total}
                      </span>
                    </div>

                    {/* Filtre pilule */}
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setMobileUserFilter("active")}
                        className={`flex-1 py-2 rounded-full text-sm font-medium transition-colors ${
                          mobileUserFilter === "active"
                            ? "bg-primary text-primary-foreground"
                            : "bg-muted text-muted-foreground"
                        }`}
                      >
                        {t("account.active")}s ({activeCollabs.length})
                      </button>
                      <button
                        type="button"
                        onClick={() => setMobileUserFilter("inactive")}
                        className={`flex-1 py-2 rounded-full text-sm font-medium transition-colors ${
                          mobileUserFilter === "inactive"
                            ? "bg-primary text-primary-foreground"
                            : "bg-muted text-muted-foreground"
                        }`}
                      >
                        {t("account.inactive")}s ({inactiveCollabs.length})
                      </button>
                    </div>

                    {/* Liste des collaborateurs */}
                    <div className="space-y-2">
                      {filteredCollabs.length === 0 ? (
                        <Card>
                          <CardContent className="py-6 text-center text-muted-foreground text-sm">
                            {t("account.noCollaborators")} {mobileUserFilter === "active" ? t("account.active").toLowerCase() : t("account.inactive").toLowerCase()} pour {agentPool.label}.
                          </CardContent>
                        </Card>
                      ) : (
                        filteredCollabs.map((collab, index) => {
                          const hasLicence = collab.licences.includes(selectedAgent);
                          const poolExhausted = !hasLicence && agentPool.used >= agentPool.total;
                          const displayName = collab.firstName && collab.lastName
                            ? `${collab.civility} ${collab.firstName} ${collab.lastName}`
                            : t("account.anonymousUser", { n: collaborators.indexOf(collab) + 1 });

                          return (
                            <Card
                              key={collab.id}
                              className="!py-0 !gap-0 cursor-pointer hover:bg-muted/30"
                              onClick={() => setFicheUserId(collab.id)}
                            >
                              <CardContent className="!py-3">
                                <div className="flex items-center justify-between gap-2">
                                  <span className="flex min-w-0 flex-1 flex-col">
                                    <span className="text-sm font-medium truncate">{displayName}</span>
                                    {hasLicence && renderLicenceSimulatorsInline(collab.id, selectedAgent)}
                                  </span>
                                  {/* Les actions de la carte ne doivent pas ouvrir la fiche. */}
                                  <div className="flex items-center gap-2 shrink-0" onClick={(e) => e.stopPropagation()}>
                                    <Switch
                                      checked={hasLicence}
                                      onCheckedChange={() => toggleCollaboratorLicence(collab.id, selectedAgent)}
                                      disabled={poolExhausted || !collabRightsFor(collab).licences}
                                    />
                                    {!newCollabIds.has(collab.id) && collab.status === "created" && collabRightsFor(collab).invite && (
                                      <button
                                        type="button"
                                        onClick={() => inviteCollaboratorNow(collab.id)}
                                        disabled={resendingLinkId === collab.id}
                                        className="p-1 rounded-md hover:bg-muted"
                                        title={t("account.invite")}
                                      >
                                        <Send className={`h-4 w-4 ${resendingLinkId === collab.id ? "text-blue-400 animate-pulse" : "text-blue-500"}`} />
                                      </button>
                                    )}
                                    {!newCollabIds.has(collab.id) && (collab.status === "invited" || collab.status === "active") && collabRightsFor(collab).invite && (
                                      <button
                                        type="button"
                                        onClick={() => resendMagicLink(collab.id)}
                                        disabled={resendingLinkId === collab.id}
                                        className="p-1 rounded-md hover:bg-muted"
                                        title={t("account.resendLink")}
                                      >
                                        <Mail className={`h-4 w-4 ${resendingLinkId === collab.id ? "text-muted-foreground animate-pulse" : "text-muted-foreground"}`} />
                                      </button>
                                    )}
                                    {/* Modifier, voir comme, désactiver… : dans la fiche (toucher la carte). */}
                                    <ChevronRight className="h-4 w-4 text-[#384872]/50" aria-hidden="true" />
                                  </div>
                                </div>

                              </CardContent>
                            </Card>
                          );
                        })
                      )}
                    </div>

                    {/* Bouton Ajouter */}
                    {canCreateUsers && (
                    <Button
                      onClick={() => setShowMobileAddPicker(true)}
                      size="sm"
                      className="w-full"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      {t("account.addCollaborator")}
                    </Button>
                    )}

                    {/* Dialog de sélection : existant ou nouveau */}
                    {(() => {
                      const eligibleCollabs = collaborators.filter(
                        (c) => !c.licences.includes(selectedAgent)
                      );
                      return (
                        <Dialog open={showMobileAddPicker} onOpenChange={setShowMobileAddPicker}>
                          <DialogContent className="sm:max-w-md">
                            <DialogHeader>
                              <DialogTitle>{t("account.addCollaborator")}</DialogTitle>
                              <DialogDescription>
                                {t("account.selectOrCreate")}
                              </DialogDescription>
                            </DialogHeader>
                            <div className="space-y-3 max-h-64 overflow-y-auto">
                              {eligibleCollabs.length > 0 ? (
                                eligibleCollabs.map((collab, idx) => {
                                  const displayName = collab.firstName && collab.lastName
                                    ? `${collab.civility} ${collab.firstName} ${collab.lastName}`
                                    : t("account.anonymousUser", { n: collaborators.indexOf(collab) + 1 });
                                  return (
                                    <button
                                      key={collab.id}
                                      type="button"
                                      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left hover:bg-muted transition-colors"
                                      onClick={() => {
                                        toggleCollaboratorLicence(collab.id, selectedAgent);
                                        setShowMobileAddPicker(false);
                                        setMobileUserFilter("active");
                                      }}
                                    >
                                      <User className="h-4 w-4 text-muted-foreground shrink-0" />
                                      <div className="flex-1 min-w-0">
                                        <div className="text-sm font-medium truncate">{displayName}</div>
                                        {collab.position && (
                                          <div className="text-xs text-muted-foreground truncate">{collab.position}</div>
                                        )}
                                      </div>
                                    </button>
                                  );
                                })
                              ) : (
                                <p className="text-sm text-muted-foreground text-center py-2">
                                  {t("account.allHaveLicence")}
                                </p>
                              )}
                            </div>
                            <Separator />
                            <Button
                              variant="outline"
                              className="w-full"
                              onClick={() => {
                                const newId = String(Date.now());
                                setCollaborators([...collaborators, makeCollaboratorDraft(newId)]);
                                setNewCollabIds((prev) => new Set(prev).add(newId));
                                setEditingUserId(newId);
                                setCreateWizardStep(1);
                                setMobileUserFilter("active");
                                setShowMobileAddPicker(false);
                              }}
                            >
                              <Plus className="h-4 w-4 mr-2" />
                              {t("account.createNewUser")}
                            </Button>
                          </DialogContent>
                        </Dialog>
                      );
                    })()}
                  </div>
                );
              })()
            )}
          </div>}

          {/* ============ VUE DESKTOP ============ */}
          {!isManager && <div className="hidden md:block">
            <div className="space-y-6">
              {/* Section 1 — Cartes licences (masqué si subscriptions) */}
              {!subscriptionData?.subscriptions?.some((s: any) => abonnementOuvert(s)) && (
              <div className="grid grid-cols-4 gap-3">
                {licencePool.filter((lp) => lp.total > 0).map((lp) => {
                  const available = lp.total - lp.used;
                  const isActive = desktopAgentFilter === lp.type;
                  return (
                    <button
                      key={lp.type}
                      type="button"
                      className="text-left"
                      onClick={() => setDesktopAgentFilter(isActive ? null : lp.type)}
                    >
                      <Card className={`!py-3 !gap-0 transition-shadow cursor-pointer ${isActive ? "shadow-md" : "hover:shadow-sm"}`} style={isActive ? { boxShadow: `0 0 0 2px ${lp.color}` } : undefined}>
                        <CardContent className="!py-0 flex flex-col items-center gap-2">
                          <div
                            className="h-10 w-10 rounded-full flex items-center justify-center text-white text-sm font-bold"
                            style={{ backgroundColor: lp.color }}
                          >
                            {lp.used}/{lp.total}
                          </div>
                          <span className="text-sm font-medium">{lp.label}</span>
                          <span className="text-xs text-muted-foreground">
                            {t("account.availableCount", { count: available })}
                          </span>
                        </CardContent>
                      </Card>
                    </button>
                  );
                })}
              </div>
              )}

              {/* Section 2 — Tableau collaborateurs */}
              {(() => {
                // Exclure les nouveaux collabs non sauvegardés (gérés via modale)
                const savedCollabs = collaborators.filter((c) => !newCollabIds.has(c.id));
                let desktopFilteredCollabs = desktopAgentFilter
                  ? savedCollabs.filter((c) => c.licences.includes(desktopAgentFilter))
                  : [...savedCollabs];
                const filterLabel = desktopAgentFilter
                  ? licencePoolConfig.find((lp) => lp.type === desktopAgentFilter)?.label
                  : null;

                // Filter by status
                if (desktopStatusFilter === "active") {
                  desktopFilteredCollabs = desktopFilteredCollabs.filter((c) => c.status !== "disabled");
                } else if (desktopStatusFilter === "disabled") {
                  desktopFilteredCollabs = desktopFilteredCollabs.filter((c) => c.status === "disabled");
                }

                // Sort
                desktopFilteredCollabs.sort((a, b) => {
                  const valA = (a[desktopSortBy] || "").toLowerCase();
                  const valB = (b[desktopSortBy] || "").toLowerCase();
                  const cmp = valA.localeCompare(valB, "fr");
                  return desktopSortDir === "asc" ? cmp : -cmp;
                });

                const handleSort = (col: "lastName" | "firstName") => {
                  if (desktopSortBy === col) {
                    setDesktopSortDir(desktopSortDir === "asc" ? "desc" : "asc");
                  } else {
                    setDesktopSortBy(col);
                    setDesktopSortDir("asc");
                  }
                };

                return (
              <div className="space-y-4">
                <div className="flex items-center gap-3 flex-wrap">
                  <h2 className="text-lg font-semibold">{t("account.collaborators")}</h2>
                  {/* Filtre par statut */}
                  <div className="flex gap-1 ml-auto">
                    {(["all", "active", "disabled"] as const).map((f) => (
                      <button
                        key={f}
                        type="button"
                        onClick={() => setDesktopStatusFilter(f)}
                        className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                          desktopStatusFilter === f
                            ? "bg-primary text-primary-foreground"
                            : "bg-muted text-muted-foreground hover:text-foreground"
                        }`}
                      >
                        {f === "all" ? t("common.all") : f === "active" ? t("account.active") : t("account.inactive")}
                      </button>
                    ))}
                  </div>
                  {filterLabel && (
                    <>
                      <Badge variant="secondary" className="text-xs">
                        {t("account.filter")}: {filterLabel}
                      </Badge>
                      <button
                        type="button"
                        onClick={() => setDesktopAgentFilter(null)}
                        className="text-xs text-muted-foreground hover:text-foreground underline"
                      >
                        {t("account.reset")}
                      </button>
                    </>
                  )}
                </div>

                {desktopFilteredCollabs.length === 0 ? (
                  <Card>
                    <CardContent className="py-8 text-center text-muted-foreground">
                      {desktopAgentFilter
                        ? `${t("account.noCollaboratorsWith")} ${filterLabel}.`
                        : t("account.noCollaboratorsYet")
                      }
                    </CardContent>
                  </Card>
                ) : (
                  <Card className="!py-0 !gap-0 overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="min-w-full text-sm">
                        <thead>
                          <tr className="border-b bg-muted/50">
                            <th
                              className="text-left font-medium px-4 py-3 relative cursor-pointer select-none hover:bg-muted/80"
                              style={{ minWidth: collabColWidths["nom"] ? collabColWidths["nom"] + "px" : undefined }}
                              onClick={() => handleSort("lastName")}
                            >
                              <span className="flex items-center gap-1">
                                {t("account.lastName")}
                                {desktopSortBy === "lastName" && (
                                  <span className="text-xs">{desktopSortDir === "asc" ? "↑" : "↓"}</span>
                                )}
                              </span>
                              <div onMouseDown={(e) => { e.stopPropagation(); handleCollabResizeStart("nom", e); }} className="absolute right-0 top-0 h-full w-1 cursor-col-resize hover:bg-primary/30" />
                            </th>
                            <th
                              className="text-left font-medium px-4 py-3 relative cursor-pointer select-none hover:bg-muted/80"
                              style={{ minWidth: collabColWidths["prenom"] ? collabColWidths["prenom"] + "px" : undefined }}
                              onClick={() => handleSort("firstName")}
                            >
                              <span className="flex items-center gap-1">
                                {t("account.firstName")}
                                {desktopSortBy === "firstName" && (
                                  <span className="text-xs">{desktopSortDir === "asc" ? "↑" : "↓"}</span>
                                )}
                              </span>
                              <div onMouseDown={(e) => { e.stopPropagation(); handleCollabResizeStart("prenom", e); }} className="absolute right-0 top-0 h-full w-1 cursor-col-resize hover:bg-primary/30" />
                            </th>
                            {[
                              { key: "email", label: t("account.emailLabel"), align: "text-left" },
                              { key: "manager", label: t("account.manager"), align: "text-left" },
                              { key: "statut", label: t("admin.status"), align: "text-center" },
                              { key: "licences", label: t("account.fiche.simulators"), align: "text-left" },
                              { key: "actions", label: t("account.actions"), align: "text-right", extra: "w-32 sticky right-0 z-20 bg-muted" },
                            ].map(({ key, label, align, extra }) => (
                              <th
                                key={key}
                                className={`${align} font-medium px-4 py-3 relative ${extra || ""}`}
                                style={{ minWidth: collabColWidths[key] ? collabColWidths[key] + "px" : undefined }}
                              >
                                {label}
                                <div
                                  onMouseDown={(e) => handleCollabResizeStart(key, e)}
                                  className="absolute right-0 top-0 h-full w-1 cursor-col-resize hover:bg-primary/30"
                                />
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {desktopFilteredCollabs.map((collab, index) => {
                            const isOpen = ficheUserId === collab.id;
                            const rights = collabRightsFor(collab);

                            return (
                              <React.Fragment key={collab.id}>
                                {/* Ligne tableau — un clic ouvre la fiche (popup) */}
                                <tr
                                  className={`border-b last:border-b-0 cursor-pointer ${isOpen ? "bg-muted/30" : "hover:bg-muted/20"} ${collab.status === "disabled" ? "opacity-50" : ""}`}
                                  tabIndex={0}
                                  aria-label={t("account.fiche.open", { name: `${collab.firstName} ${collab.lastName}` })}
                                  onClick={() => setFicheUserId(collab.id)}
                                  onKeyDown={(e) => { if (e.key === "Enter" && e.target === e.currentTarget) setFicheUserId(collab.id); }}
                                  data-testid="collaborator-row"
                                >
                                  <td className="px-4 py-3 font-medium truncate overflow-hidden" title={collab.lastName || ""}>{collab.lastName || t("common.none")}</td>
                                  <td className="px-4 py-3 truncate overflow-hidden" title={collab.firstName || ""}>{collab.firstName || t("common.none")}</td>
                                  <td className="px-4 py-3 text-muted-foreground truncate overflow-hidden" title={collab.email || t("common.none")}>{collab.email || t("common.none")}</td>
                                  <td className="px-4 py-3 text-muted-foreground truncate overflow-hidden">
                                    {(() => {
                                      if (!collab.managerId) return <span className="text-xs">—</span>;
                                      const mgr = collaborators.find((c) => c.id === collab.managerId);
                                      if (!mgr) return <span className="text-xs">—</span>;
                                      const fullName = `${mgr.firstName} ${mgr.lastName}`.trim();
                                      return <span title={fullName}>{fullName}</span>;
                                    })()}
                                  </td>
                                  <td className="px-4 py-3 text-center">
                                    {renderCollabStatusBadge(collab)}
                                  </td>
                                  <td className="px-4 py-3">
                                    <div className="flex flex-wrap gap-1">
                                      {collab.licences.length > 0 ? collab.licences.map((lic) => {
                                        const pool = licencePoolConfig.find((lp) => lp.type === lic);
                                        return (
                                          <span key={lic} className="inline-flex flex-col items-start gap-0.5">
                                            <span
                                              className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium text-white"
                                              style={{ backgroundColor: pool?.color || "#6B7280" }}
                                            >
                                              {pool?.label || lic}
                                            </span>
                                            {/* Licence Starter / Focus : ses simulateurs, sans ouvrir la fiche. */}
                                            {renderLicenceSimulatorsInline(collab.id, lic)}
                                          </span>
                                        );
                                      }) : (
                                        <span className="text-muted-foreground text-xs">{t("common.none")}</span>
                                      )}
                                    </div>
                                  </td>
                                  {/* Les actions ne doivent pas ouvrir la fiche : React fait remonter jusqu'à la
                                      ligne les clics des menus et dialogues Radix, même rendus hors du tableau. */}
                                  <td className="px-4 py-3 sticky right-0 z-10 bg-card cursor-default" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                                    <div className="flex justify-end gap-1">
                                      {/* Emplacement « invitation / renvoi de lien » — largeur fixe (icône w-8)
                                          ou placeholder, pour aligner les colonnes d'actions entre les lignes. */}
                                      {!newCollabIds.has(collab.id) && collab.status === "created" && rights.invite ? (
                                        <Button
                                          variant="ghost"
                                          size="icon"
                                          className="h-8 w-8 text-blue-600 hover:text-blue-700"
                                          title={t("account.invite")}
                                          aria-label={t("account.invite")}
                                          onClick={() => inviteCollaboratorNow(collab.id)}
                                          disabled={resendingLinkId === collab.id}
                                        >
                                          <Send className={`h-4 w-4 ${resendingLinkId === collab.id ? "animate-pulse" : ""}`} />
                                        </Button>
                                      ) : !newCollabIds.has(collab.id) && (collab.status === "invited" || collab.status === "active") && rights.invite ? (
                                        <Button
                                          variant="ghost"
                                          size="icon"
                                          className="h-8 w-8"
                                          onClick={() => resendMagicLink(collab.id)}
                                          disabled={resendingLinkId === collab.id}
                                          title={t("account.resendLink")}
                                          aria-label={t("account.resendLink")}
                                        >
                                          <Mail className={`h-4 w-4 ${resendingLinkId === collab.id ? "animate-pulse" : ""}`} />
                                        </Button>
                                      ) : (
                                        <span className="inline-block h-8 w-8" aria-hidden="true" />
                                      )}
                                      {/* Le reste des actions (modifier, voir comme, contexte, désactiver…) vit dans
                                          la fiche : la ligne n'en garde que l'invitation, action contextuelle rapide.
                                          Le chevron dit que la ligne s'ouvre. */}
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-8 w-8 text-[#384872]/60 hover:text-[#384872]"
                                        aria-label={t("account.fiche.open", { name: `${collab.firstName} ${collab.lastName}` })}
                                        title={t("account.fiche.openShort")}
                                        onClick={() => setFicheUserId(collab.id)}
                                      >
                                        <ChevronRight className="h-4 w-4" />
                                      </Button>
                                    </div>
                                  </td>
                                </tr>

                              </React.Fragment>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </Card>
                )}
              </div>
                );
              })()}
            </div>
          </div>}

          {/* Dialog de confirmation de désactivation (commun mobile/desktop) */}
          <Dialog open={deleteUserId !== null} onOpenChange={(open) => !open && setDeleteUserId(null)}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>{t("account.confirmDeactivate")}</DialogTitle>
                <DialogDescription>
                  {t("account.deactivateWarning")}
                </DialogDescription>
              </DialogHeader>
              <div className="flex gap-2 w-full pt-4">
                <Button
                  variant="destructive"
                  className="flex-1"
                  onClick={() => {
                    if (deleteUserId) {
                      // Une édition en cours sur ce collaborateur est abandonnée (valeurs restaurées).
                      if (editingUserId === deleteUserId) handleCancelUser();
                      if (expandedUserId === deleteUserId) setExpandedUserId(null);
                      deactivateCollaborator(deleteUserId);
                      setDeleteUserId(null);
                    }
                  }}
                >
                  {t("account.deactivate")}
                </Button>
                <Button variant="outline" className="flex-1" onClick={() => setDeleteUserId(null)}>
                  {t("common.cancel")}
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Dialog de confirmation de réactivation */}
          <Dialog open={reactivateUserId !== null} onOpenChange={(open) => !open && setReactivateUserId(null)}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>{t("account.confirmReactivate")}</DialogTitle>
                <DialogDescription>
                  {t("account.reactivateWarning")}
                </DialogDescription>
              </DialogHeader>
              <div className="flex gap-2 w-full pt-4">
                <Button
                  className="flex-1"
                  onClick={() => {
                    if (reactivateUserId) {
                      reactivateCollaborator(reactivateUserId);
                    }
                  }}
                >
                  {t("account.reactivate")}
                </Button>
                <Button variant="outline" className="flex-1" onClick={() => setReactivateUserId(null)}>
                  {t("common.cancel")}
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Choix des simulateurs d'une licence Starter / Focus (à l'attribution ou depuis la fiche). */}
          {simulatorChoice && (() => {
            const collab = collaborators.find((c) => c.id === simulatorChoice.collaboratorId);
            const lp = licencePoolConfig.find((l) => l.type === simulatorChoice.licenceType);
            return (
              <LicenceSimulatorsDialog
                open
                licenceLabel={lp?.label || simulatorChoice.licenceType}
                collaboratorName={collab ? `${collab.firstName} ${collab.lastName}`.trim() : ""}
                max={simulatorChoice.max}
                simulators={selectableSimulators}
                loadError={selectableSimulatorsError}
                initialIds={simulatorChoice.initialIds}
                onCancel={() => setSimulatorChoice(null)}
                onConfirm={confirmSimulatorChoice}
              />
            );
          })()}

          {/* Fiche collaborateur (popup) — rendue HORS du tableau : ses clics remonteraient sinon
              jusqu'à la ligne. Commune aux vues bureau, mobile et manager. */}
          {(() => {
            const ficheCollab = ficheUserId ? collaborators.find((c) => c.id === ficheUserId) : null;
            if (!ficheCollab || newCollabIds.has(ficheCollab.id)) return null;
            const rights = collabRightsFor(ficheCollab);
            const isEditingFiche = editingUserId === ficheCollab.id;
            const dirty = isEditingFiche && !!editSnapshot && (
              Object.keys(buildCollaboratorPatch(editSnapshot, ficheCollab)).length > 0
              || editSnapshot.profileType !== ficheCollab.profileType
            );
            return (
              <CollaboratorFicheDialog
                collab={ficheCollab}
                managerCandidates={collaborators.filter(
                  (c) => (c.profileType === "MNG" || c.profileType === "ORG") && c.id !== ficheCollab.id && !newCollabIds.has(c.id),
                )}
                licencePool={licencePool}
                rights={rights}
                profileOptions={assignableProfiles(collabActorProfile)}
                statusBadge={renderCollabStatusBadge(ficheCollab)}
                actions={
                  <CollaboratorRowActions
                    collab={ficheCollab}
                    candidates={collaborators}
                    accountId={accountId}
                    variant="toolbar"
                    canImpersonate={user.profileType === "ADM" && ficheCollab.id !== user.id}
                    // Le responsable se modifie dans la fiche même : pas de second chemin.
                    canChangeManager={false}
                    canManageProgram={rights.edit && ficheCollab.id !== user.id && ficheCollab.status !== "disabled"}
                    canToggleStatus={rights.status}
                    onImpersonate={() => {
                      sessionStorage.setItem("bizcoach_impersonate_user_id", ficheCollab.id);
                      window.location.reload();
                    }}
                    onDeactivate={() => setDeleteUserId(ficheCollab.id)}
                    onReactivate={() => setReactivateUserId(ficheCollab.id)}
                  />
                }
                editing={isEditingFiche}
                dirty={dirty}
                saving={isSaving}
                resendingLink={resendingLinkId === ficheCollab.id}
                onClose={() => setFicheUserId(null)}
                onStartEdit={() => setEditingUserId(ficheCollab.id)}
                onSave={() => handleSaveUser(ficheCollab.id)}
                onCancelEdit={handleCancelUser}
                onChange={(prop, value) => updateCollaborator(ficheCollab.id, prop, value)}
                onSaveField={(field, snapshot) => handleSaveCollaboratorField(ficheCollab.id, field, snapshot)}
                onToggleLicence={(type) => toggleCollaboratorLicence(ficheCollab.id, type)}
                licenceSimulators={(type) => licenceSimulatorsFor(ficheCollab.id, type)}
                onEditLicenceSimulators={(type) => {
                  const info = licenceSimulatorsFor(ficheCollab.id, type);
                  if (!info) return;
                  openSimulatorChoice({
                    collaboratorId: ficheCollab.id,
                    licenceType: type,
                    mode: newCollabIds.has(ficheCollab.id) ? "pending" : "edit",
                    max: info.max,
                    initialIds: info.ids ?? [],
                  });
                }}
                onInvite={() => inviteCollaboratorNow(ficheCollab.id)}
                onResendLink={() => resendMagicLink(ficheCollab.id)}
                onSavePhoto={async (photo) => {
                  try {
                    const updated = await accountApi.updateCollaborator(ficheCollab.id, { profilePicture: photo }, accountId);
                    applySavedCollaborator(ficheCollab.id, updated);
                    showSaveMessage(t("common.success"));
                    return null;
                  } catch (err: any) {
                    return err?.message || t("api.updateError");
                  }
                }}
              />
            );
          })()}

          {/* Dialog wizard de création d'un nouveau collaborateur */}
          {(() => {
            const newCollab = editingUserId && newCollabIds.has(editingUserId)
              ? collaborators.find((c) => c.id === editingUserId)
              : null;
            if (!newCollab) return null;

            const step1Valid = newCollab.firstName.trim() && newCollab.lastName.trim() && newCollab.email.trim();
            const step2Valid = !!newCollab.profileType;

            const profileOptions = [
              { value: "ORG", label: t("account.organisateur"), description: t("account.profileOrgDesc"), icon: Settings },
              { value: "MNG", label: t("account.manager"), description: t("account.profileMngDesc"), icon: Briefcase },
              { value: "RH", label: t("account.rhManagement"), description: t("account.profileRhDesc"), icon: Users },
              { value: "BNF", label: t("account.user"), description: t("account.profileBnfDesc"), icon: User },
            // Seuls les profils que le créateur a le droit de donner à la création (même règle que le serveur).
            ].filter((o) => creatableProfiles(collabActorProfile).includes(o.value));

            const stepTitles = [
              t("account.addCollaborator"),
              t("account.stepProfile"),
              t("account.licences"),
            ];
            const stepDescriptions = [
              t("account.fillCollaboratorInfo"),
              t("account.chooseProfile"),
              t("account.attributeLicencesHelp"),
            ];

            const stepLabels = [t("account.stepInformations"), t("account.stepProfile"), t("account.stepLicences")];

            return (
              <Dialog open={true} onOpenChange={(open) => { if (!open) handleCancelUser(); }}>
                <DialogContent className="sm:max-w-3xl p-0 gap-0 overflow-hidden">
                  {/* En-tête avec fond coloré */}
                  <div className="bg-gradient-to-r from-[#384872] to-[#877BF2] px-5 py-5 sm:px-8 sm:py-6 text-white">
                    <DialogHeader>
                      <DialogTitle className="text-white text-xl">{stepTitles[createWizardStep - 1]}</DialogTitle>
                      <DialogDescription className="text-white/80 text-base mt-1">
                        {stepDescriptions[createWizardStep - 1]}
                      </DialogDescription>
                    </DialogHeader>

                    {/* Barre de progression */}
                    <div className="flex items-center gap-3 mt-6">
                      {[1, 2, 3].map((step) => (
                        <React.Fragment key={step}>
                          <div className="flex items-center gap-2">
                            <div className={`flex items-center justify-center h-9 w-9 rounded-full text-sm font-semibold transition-all ${
                              createWizardStep === step
                                ? "bg-white text-[#384872] shadow-lg"
                                : createWizardStep > step
                                  ? "bg-white/30 text-white"
                                  : "bg-white/15 text-white/60"
                            }`}>
                              {createWizardStep > step ? <Check className="h-4 w-4" /> : step}
                            </div>
                            <span className={`text-sm font-medium hidden sm:inline ${
                              createWizardStep === step ? "text-white" : "text-white/60"
                            }`}>{stepLabels[step - 1]}</span>
                          </div>
                          {step < 3 && (
                            <div className={`flex-1 h-0.5 rounded-full transition-colors ${
                              createWizardStep > step ? "bg-white/50" : "bg-white/15"
                            }`} />
                          )}
                        </React.Fragment>
                      ))}
                    </div>
                  </div>

                  {/* Contenu */}
                  <div className="px-5 py-5 sm:px-8 sm:py-6">

                  {/* Étape 1 : Informations */}
                  {createWizardStep === 1 && (
                    <div className="space-y-5">
                      <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
                        <div className="space-y-2">
                          <Label className="text-sm font-medium">{t("account.civilityRequired")} <span className="text-red-500">*</span></Label>
                          <Select
                            value={newCollab.civility}
                            onValueChange={(value) => updateCollaborator(newCollab.id, "civility", value)}
                          >
                            <SelectTrigger className="w-full h-11 text-base">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="M." className="text-base">M.</SelectItem>
                              <SelectItem value="Mme" className="text-base">Mme</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-sm font-medium">{t("account.firstNameRequired")} <span className="text-red-500">*</span></Label>
                          <Input
                            className="h-11 text-base"
                            value={newCollab.firstName}
                            onChange={(e) => updateCollaborator(newCollab.id, "firstName", e.target.value)}
                            placeholder={t("account.firstNamePlaceholder")}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-sm font-medium">{t("account.lastNameRequired")} <span className="text-red-500">*</span></Label>
                          <Input
                            className="h-11 text-base"
                            value={newCollab.lastName}
                            onChange={(e) => updateCollaborator(newCollab.id, "lastName", e.target.value)}
                            placeholder={t("account.lastNamePlaceholder")}
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                        <div className="space-y-2">
                          <Label className="text-sm font-medium">{t("account.emailRequired")} <span className="text-red-500">*</span></Label>
                          <Input
                            className="h-11 text-base"
                            type="email"
                            autoComplete="email"
                            value={newCollab.email}
                            onChange={(e) => updateCollaborator(newCollab.id, "email", e.target.value)}
                            placeholder={t("account.emailPlaceholder")}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-sm font-medium">{t("account.phoneLabel")}</Label>
                          <Input
                            className="h-11 text-base"
                            type="tel"
                            autoComplete="tel"
                            value={newCollab.phoneNumber}
                            onChange={(e) => updateCollaborator(newCollab.id, "phoneNumber", e.target.value)}
                            placeholder={t("account.phonePlaceholder")}
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm font-medium">{t("account.positionLabel")}</Label>
                        <Input
                          className="h-11 text-base"
                          value={newCollab.position}
                          onChange={(e) => updateCollaborator(newCollab.id, "position", e.target.value)}
                          placeholder={t("account.positionPlaceholder")}
                        />
                      </div>
                      <CollaboratorLanguageFields
                        language={newCollab.language}
                        spokenLanguage={newCollab.spokenLanguage}
                        onChange={(field, value) => updateCollaborator(newCollab.id, field, value)}
                        inputClassName="h-11 text-base"
                      />
                      <div className="space-y-2">
                        <Label className="text-sm font-medium">
                          {t("account.manager_optional")} <span className="text-gray-400 font-normal">{t("account.optional")}</span>
                        </Label>
                        <Select
                          value={newCollab.managerId ?? "__none__"}
                          onValueChange={(value) => updateCollaborator(newCollab.id, "managerId", value === "__none__" ? null : value)}
                        >
                          <SelectTrigger className="w-full h-11 text-base">
                            <SelectValue placeholder={t("account.noManager")} />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none__" className="text-base text-muted-foreground">{t("account.noManager")}</SelectItem>
                            {collaborators
                              .filter((c) =>
                                (c.profileType === "ORG" || c.profileType === "MNG") &&
                                c.id !== newCollab.id &&
                                !newCollabIds.has(c.id) &&
                                c.status !== "disabled"
                              )
                              .map((c) => (
                                <SelectItem key={c.id} value={c.id} className="text-base">
                                  {c.firstName} {c.lastName} <span className="text-muted-foreground">({c.profileType})</span>
                                </SelectItem>
                              ))}
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground">
                          {t("account.managerHelp")}
                        </p>
                      </div>
                      <div className="flex gap-3 pt-4">
                        <Button
                          onClick={() => setCreateWizardStep(2)}
                          className="flex-1 h-11 text-base"
                          disabled={!step1Valid}
                        >
                          {t("account.next")}
                          <ChevronRight className="h-5 w-5 ml-2" />
                        </Button>
                        <Button variant="outline" onClick={handleCancelUser} className="flex-1 h-11 text-base">
                          {t("common.cancel")}
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Étape 2 : Profil */}
                  {createWizardStep === 2 && (
                    <div className="space-y-5">
                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        {profileOptions.map((profile) => {
                          const isSelected = newCollab.profileType === profile.value;
                          const Icon = profile.icon;
                          return (
                            <button
                              key={profile.value}
                              type="button"
                              onClick={() => {
                                setCollaborators((prev) => prev.map((c) => c.id === newCollab.id ? { ...c, profileType: profile.value } : c));
                              }}
                              className={`flex items-start gap-4 p-5 rounded-xl border-2 text-left transition-all ${
                                isSelected
                                  ? "border-[#877BF2] bg-[#877BF2]/5 shadow-md"
                                  : "border-border hover:border-[#877BF2]/40 hover:bg-gray-50"
                              }`}
                            >
                              <div className={`flex items-center justify-center h-12 w-12 rounded-xl shrink-0 transition-colors ${
                                isSelected ? "bg-[#877BF2] text-white" : "bg-gray-100 text-gray-500"
                              }`}>
                                <Icon className="h-6 w-6" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <span className="font-semibold text-base">{profile.label}</span>
                                  <Badge variant="outline" className="text-[10px] px-1.5 py-0">{profile.value}</Badge>
                                </div>
                                <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">{profile.description}</p>
                              </div>
                              {isSelected && (
                                <div className="flex items-center justify-center h-6 w-6 rounded-full bg-[#877BF2] shrink-0 mt-0.5">
                                  <Check className="h-4 w-4 text-white" />
                                </div>
                              )}
                            </button>
                          );
                        })}
                      </div>
                      <div className="flex gap-3 pt-4">
                        <Button variant="outline" onClick={() => setCreateWizardStep(1)} className="flex-1 h-11 text-base">
                          <ArrowLeft className="h-5 w-5 mr-2" />
                          {t("account.previous")}
                        </Button>
                        <Button
                          onClick={() => setCreateWizardStep(3)}
                          className="flex-1 h-11 text-base"
                          disabled={!step2Valid}
                        >
                          {t("account.attributeLicences")}
                          <ChevronRight className="h-5 w-5 ml-2" />
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Étape 3 : Licences (affichées par simulateur/coach) */}
                  {createWizardStep === 3 && (() => {
                    // Ordre d'affichage souhaité
                    const simOrder = ["Prospector", "Discovery", "Pitch", "MEDDIC", "Negociator", "Negotiator", "Satisfactor"];
                    // Coachs : rang tolérant au renommage du nom (cf. lib/coach-kind) — Prepa, puis Debrief.
                    const coachRank = (name: string) => {
                      const k = detectCoachKind(name);
                      return k === "prepa" ? 0 : k === "debrief" ? 1 : 2;
                    };

                    // Séparer et trier les simulateurs actifs (exclure les internes type ADMIN)
                    const activeSimulators = simulatorsList
                      .filter((s: SimulatorType) => simulateurUtilisable(s))
                      .filter((s: SimulatorType) => s.simulatorType === "training")
                      .sort((a: SimulatorType, b: SimulatorType) => {
                        const ia = simOrder.findIndex((n) => n.toLowerCase() === a.name.toLowerCase());
                        const ib = simOrder.findIndex((n) => n.toLowerCase() === b.name.toLowerCase());
                        return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
                      });
                    const activeCoaches = simulatorsList
                      .filter((s: SimulatorType) => s.isActive !== false)
                      .filter((s: SimulatorType) => s.simulatorType === "coach")
                      .sort((a: SimulatorType, b: SimulatorType) => coachRank(a.name) - coachRank(b.name));

                    // Matcher un simulateur à sa licence dans le pool
                    const findLicence = (sim: SimulatorType) => {
                      const normalized = sim.name.toUpperCase().replace(/\s+/g, "_");
                      return licencePool.find((lp) =>
                        lp.code.toUpperCase() === normalized
                        || lp.label.toUpperCase() === sim.name.toUpperCase()
                        || lp.type.toUpperCase() === normalized
                        || lp.code.toUpperCase() === sim.name.toUpperCase()
                      );
                    };

                    const renderSimItem = (sim: SimulatorType) => {
                      const lp = findLicence(sim);
                      const isChecked = lp ? newCollab.licences.includes(lp.type) : false;
                      const available = lp ? lp.total - lp.used : 0;
                      const poolExhausted = lp ? lp.used >= lp.total : true;
                      const disabled = (!lp || poolExhausted) && !isChecked;
                      return (
                        <button
                          key={sim.id}
                          type="button"
                          disabled={disabled}
                          onClick={() => lp && toggleCollaboratorLicence(newCollab.id, lp.type)}
                          className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg border-2 text-left transition-all ${
                            disabled
                              ? "opacity-40 cursor-not-allowed border-border"
                              : isChecked
                                ? "border-[#877BF2] bg-[#877BF2]/5 shadow-sm"
                                : "border-border hover:border-[#877BF2]/40 hover:bg-gray-50"
                          }`}
                        >
                          <div
                            className={`flex items-center justify-center h-5 w-5 rounded border-2 shrink-0 transition-colors ${
                              isChecked
                                ? "border-[#877BF2] bg-[#877BF2]"
                                : "border-border"
                            }`}
                          >
                            {isChecked && <Check className="h-3 w-3 text-white" />}
                          </div>
                          <div
                            className="h-3.5 w-3.5 rounded-full shrink-0"
                            style={{ backgroundColor: sim.color || "#6B7280" }}
                          />
                          <span className="font-medium text-sm flex-1">{sim.name}</span>
                          {lp ? (
                            <Badge
                              variant={poolExhausted ? "destructive" : "secondary"}
                              className="text-[11px]"
                            >
                              {poolExhausted ? t("account.exhausted") : `${available} ${t("account.available_short")}`}
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-[11px]">
                              {t("account.notSubscribed")}
                            </Badge>
                          )}
                        </button>
                      );
                    };

                    return (
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                        {/* Colonne Simulateurs */}
                        <div className="space-y-2">
                          <div className="flex items-center gap-2 mb-3">
                            <Target className="h-4 w-4 text-[#384872]" />
                            <span className="text-sm font-semibold text-[#384872] uppercase tracking-wide">{t("account.simulatorsHeading")}</span>
                          </div>
                          <div className="space-y-2">
                            {activeSimulators.map(renderSimItem)}
                          </div>
                        </div>
                        {/* Colonne Coachs */}
                        <div className="space-y-2">
                          <div className="flex items-center gap-2 mb-3">
                            <BookOpen className="h-4 w-4 text-[#877BF2]" />
                            <span className="text-sm font-semibold text-[#877BF2] uppercase tracking-wide">{t("account.coachesHeading")}</span>
                          </div>
                          <div className="space-y-2">
                            {activeCoaches.length > 0 ? activeCoaches.map(renderSimItem) : (
                              <p className="text-sm text-muted-foreground italic py-2">{t("account.noCoachAvailable")}</p>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-col gap-3 pt-3 sm:flex-row">
                        <Button variant="outline" onClick={() => setCreateWizardStep(2)} className="h-11 text-sm">
                          <ArrowLeft className="h-4 w-4 mr-2" />
                          {t("account.previous")}
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => handleSaveUser(newCollab.id, false)}
                          className="flex-1 h-11 text-sm"
                          disabled={isSaving}
                        >
                          <Check className="h-4 w-4 mr-2" />
                          {t("account.create")}
                        </Button>
                        <Button
                          onClick={() => handleSaveUser(newCollab.id, true)}
                          className="flex-1 h-11 text-sm bg-[#877BF2] hover:bg-[#7668d9]"
                          disabled={isSaving}
                        >
                          <Send className="h-4 w-4 mr-2" />
                          {t("account.createAndInvite")}
                        </Button>
                      </div>
                    </div>
                    );
                  })()}

                  </div>
                </DialogContent>
              </Dialog>
            );
          })()}
        </TabsContent>

        {/* Récompenses (BizCoins) — réglages entreprise + catalogue (Étape 9) */}
        <TabsContent value="rewards" className="md:mt-0">
          <BizRewardsCard />
        </TabsContent>

        {/* Challenges Tab */}
        <TabsContent value="certification-paths" className="md:mt-0">
          <CertificationPathsPage user={user} mode="manager" />
        </TabsContent>

        <TabsContent value="challenges" className="md:mt-0">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>{t("challenge.challenges")}</CardTitle>
                <Button
                  size="sm"
                  data-tour="challenge-create"
                  onClick={() => {
                    resetChallengeForm();
                    setIsChallengeDialogOpen(true);
                  }}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  {t("challenge.create")}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {challengesList.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Trophy className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p className="font-medium">{t("challenge.noChallenges")}</p>
                  <p className="text-sm mt-1">{t("challenge.noChallengesDesc")}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {challengesList.map((ch) => {
                    const sim = simulatorsList.find(s => s.id === ch.simulatorId);
                    const statusColors: Record<string, string> = {
                      draft: "bg-gray-100 text-gray-700",
                      active: "bg-green-100 text-green-700",
                      paused: "bg-yellow-100 text-yellow-800",
                      ended: "bg-blue-100 text-blue-700",
                    };
                    const statusLabels: Record<string, string> = {
                      draft: t("challenge.statusDraft"),
                      active: t("challenge.statusActive"),
                      paused: t("challenge.statusPaused"),
                      ended: t("challenge.statusEnded"),
                    };
                    // Libellé de l'objectif : helper partagé (objectifs du simulateur, critères via
                    // `pickScoringLabel`, objectifs communs) — plus de ternaire de langue recopié.
                    const objectiveText = (key: string) => challengeObjectiveLabel(key, sim?.scoringCriteria || [], i18n.language, t);
                    return (
                      <div
                        key={ch.id}
                        className="border rounded-lg p-4 hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex items-start justify-between">
                          <div
                            className="flex-1 min-w-0 cursor-pointer"
                            onClick={() => {
                              if (ch.status === "active" || ch.status === "paused" || ch.status === "ended") {
                                // Open read-only dialog (same as edit button)
                                setEditingChallenge(ch);
                                challengesApi.getChallenge(ch.id, accountId).then(async (full: any) => {
                                  const loadedTeams = (full.teams || []).map((t: any) => ({ id: t.id, name: t.name, color: t.color || TEAM_PALETTE[0] }));
                                  setChallengeForm(hydrateChallengeForm(full, loadedTeams));
                                  if (full.teamAssignmentMode) {
                                    setManagingParticipants(full.participants || []);
                                    const asgn: Record<string, string | null> = {};
                                    for (const p of (full.participants || [])) { asgn[p.id] = p.teamId || null; }
                                    setTeamAssignments(asgn);
                                  }
                                }).catch(() => {
                                  setChallengeForm(hydrateChallengeForm(ch, []));
                                });
                                setIsChallengeDialogOpen(true);
                              } else if (ch.status === "draft") {
                                setEditingChallenge(ch);
                                challengesApi.getChallenge(ch.id, accountId).then(async (full: any) => {
                                  const loadedTeams = (full.teams || []).map((t: any) => ({ id: t.id, name: t.name, color: t.color || TEAM_PALETTE[0] }));
                                  setChallengeForm(hydrateChallengeForm(full, loadedTeams));
                                  // Load eligible collaborators (filtered by licence)
                                  setLoadingDraftCollabs(true);
                                  try {
                                    const eligible = await challengesApi.getEligibleUsers(full.simulatorId, accountId);
                                    setDraftCollaborators(proposables(eligible, (full.teamPreAssignments || []).map((pa: any) => pa.userId)));
                                  } catch { setDraftCollaborators([]); }
                                  finally { setLoadingDraftCollabs(false); }

                                  if (full.teamAssignmentMode && loadedTeams.length > 0) {
                                    // Restore team pre-assignments
                                    if (Array.isArray(full.teamPreAssignments) && full.teamPreAssignments.length > 0) {
                                      const teamIdToIndex = new Map(loadedTeams.map((t: any, i: number) => [t.id, i]));
                                      const restored: Record<string, number | null> = {};
                                      for (const pa of full.teamPreAssignments) { const i = teamIdToIndex.get(pa.teamId); if (i !== undefined) restored[pa.userId] = i; }
                                      setDraftTeamAssignments(restored);
                                    }
                                  } else if (!full.teamAssignmentMode) {
                                    // Restore selected participants (without teams)
                                    if (Array.isArray(full.teamPreAssignments) && full.teamPreAssignments.length > 0) {
                                      const restored = new Set<string>(full.teamPreAssignments.map((pa: any) => pa.userId as string));
                                      setDraftSelectedParticipants(restored);
                                    }
                                  }
                                }).catch(() => {
                                  setChallengeForm(hydrateChallengeForm(ch, []));
                                });
                                setIsChallengeDialogOpen(true);
                              }
                            }}
                          >
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-semibold text-base truncate">{ch.title}</h3>
                              <Badge className={`text-xs ${statusColors[ch.status] || ""}`}>
                                {statusLabels[ch.status] || ch.status}
                              </Badge>
                            </div>
                            {ch.description && (
                              <p className="text-sm text-gray-500 mb-2 line-clamp-2">{ch.description}</p>
                            )}
                            <div className="flex flex-wrap gap-3 text-xs text-gray-500">
                              {sim && (
                                <span className="flex items-center gap-1">
                                  {sim.icon && sim.icon.startsWith("data:") ? (
                                    <img src={sim.icon} alt="" loading="lazy" className="h-4 w-4 object-contain" />
                                  ) : sim.icon ? (
                                    <span>{sim.icon}</span>
                                  ) : null}
                                  {sim.name}
                                </span>
                              )}
                              <span>{objectiveText(ch.primaryObjective)}</span>
                              <span>{ch.minSessions}–{ch.maxSessions} {t("challenge.sessions")}</span>
                              {(ch.startDate || ch.endDate) && (
                                <span className="flex items-center gap-1">
                                  <Calendar className="h-3 w-3" />
                                  {ch.startDate ? new Date(ch.startDate).toLocaleDateString(i18n.language === "fr" ? "fr-FR" : "en-GB") : "?"} → {ch.endDate ? new Date(ch.endDate).toLocaleDateString(i18n.language === "fr" ? "fr-FR" : "en-GB") : "?"}
                                </span>
                              )}
                              <span>
                                {ch.status === "draft" && Array.isArray(ch.teamPreAssignments) && ch.teamPreAssignments.length > 0
                                  ? `${ch.teamPreAssignments.length} ${t("challenge.preAssigned").toLowerCase()}`
                                  : `${ch.participantsCount || 0} ${t("challenge.participants").toLowerCase()}`}
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center gap-1 ml-2 shrink-0">
                            {ch.status === "draft" && (
                              <>
                                {challengePermissions(user, ch).edit && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="gap-1.5"
                                  title={t("common.edit")}
                                  onClick={() => {
                                    setEditingChallenge(ch);
                                    // Load full challenge details for teams
                                    challengesApi.getChallenge(ch.id, accountId).then(async (full: any) => {
                                      const loadedTeams = (full.teams || []).map((t: any) => ({ id: t.id, name: t.name, color: t.color || TEAM_PALETTE[0] }));
                                      setChallengeForm(hydrateChallengeForm(full, loadedTeams));
                                      // Load participants for team assignment (active challenges)
                                      if ((ch.status === "active" || ch.status === "paused") && full.teamAssignmentMode) {
                                        setManagingParticipants(full.participants || []);
                                        const asgn: Record<string, string | null> = {};
                                        for (const p of (full.participants || [])) {
                                          asgn[p.id] = p.teamId || null;
                                        }
                                        setTeamAssignments(asgn);
                                      }
                                      // Draft: auto-load eligible collaborators + restore pre-assignments
                                      if (ch.status === "draft") {
                                        // Load eligible collaborators (filtered by licence)
                                        setLoadingDraftCollabs(true);
                                        try {
                                          const eligible = await challengesApi.getEligibleUsers(full.simulatorId, accountId);
                                          setDraftCollaborators(proposables(eligible, (full.teamPreAssignments || []).map((pa: any) => pa.userId)));
                                        } catch { setDraftCollaborators([]); }
                                        finally { setLoadingDraftCollabs(false); }

                                        if (full.teamAssignmentMode && loadedTeams.length > 0) {
                                          // Restore team pre-assignments
                                          if (Array.isArray(full.teamPreAssignments) && full.teamPreAssignments.length > 0) {
                                            const teamIdToIndex = new Map(loadedTeams.map((t: any, idx: number) => [t.id, idx]));
                                            const restored: Record<string, number | null> = {};
                                            for (const pa of full.teamPreAssignments) {
                                              const idx = teamIdToIndex.get(pa.teamId);
                                              if (idx !== undefined) restored[pa.userId] = idx;
                                            }
                                            setDraftTeamAssignments(restored);
                                          }
                                        } else if (!full.teamAssignmentMode) {
                                          // Restore selected participants (without teams)
                                          if (Array.isArray(full.teamPreAssignments) && full.teamPreAssignments.length > 0) {
                                            const restored = new Set<string>(full.teamPreAssignments.map((pa: any) => pa.userId as string));
                                            setDraftSelectedParticipants(restored);
                                          }
                                        }
                                      }
                                    }).catch(() => {
                                      setChallengeForm(hydrateChallengeForm(ch, []));
                                    });
                                    setIsChallengeDialogOpen(true);
                                  }}
                                >
                                  <Pencil className="h-4 w-4" />
                                  <span className="hidden md:inline">{t("common.edit")}</span>
                                </Button>
                                )}
                                {ch.status === "draft" && ch.awaitsCampaignDates && (
                                  <span className="text-xs" style={{ color: "#926B00" }}>{t("challenge.awaitsCampaignDates")}</span>
                                )}
                                {challengePermissions(user, ch).activate && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-green-600 hover:text-green-700 gap-1.5"
                                  title={t("challenge.activate")}
                                  onClick={() => setConfirmAction({ type: "activate", challenge: ch })}
                                >
                                  <Play className="h-4 w-4" />
                                  <span className="hidden md:inline">{t("challenge.activate")}</span>
                                </Button>
                                )}
                                {challengePermissions(user, ch).delete && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-red-500 hover:text-red-600 gap-1.5"
                                  title={t("common.delete")}
                                  onClick={() => setConfirmAction({ type: "delete", challenge: ch })}
                                >
                                  <Trash2 className="h-4 w-4" />
                                  <span className="hidden md:inline">{t("common.delete")}</span>
                                </Button>
                                )}
                              </>
                            )}
                            {(ch.status === "active" || ch.status === "paused") && (
                              <>
                                {challengePermissions(user, ch).teams && (
                                <Button
                                    variant="outline"
                                    size="sm"
                                    title={t("challenge.manageTeams")}
                                    className="text-purple-600 hover:text-purple-700 border-purple-200 hover:border-purple-300 hover:bg-purple-50 gap-1.5"
                                    onClick={async () => {
                                      try {
                                        const full = await challengesApi.getChallenge(ch.id, accountId);
                                        setManagingChallenge(full);
                                        setManagingTeams(full.teams || []);
                                        setManagingParticipants(full.participants || []);
                                        const assignments: Record<string, string | null> = {};
                                        for (const p of (full.participants || [])) {
                                          assignments[p.id] = p.teamId || null;
                                        }
                                        setTeamAssignments(assignments);
                                        setShowAddParticipants(false);
                                        setShowNewTeamInput(false);
                                        setIsTeamManagementOpen(true);
                                      } catch (err: any) {
                                        alert(challengeErrorMessage(err, t));
                                      }
                                    }}
                                  >
                                    <Users className="h-4 w-4" />
                                    <span className="hidden sm:inline">{t("challenge.manageTeams")}</span>
                                  </Button>
                                )}
                                {challengePermissions(user, ch).edit && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="gap-1.5"
                                  title={t("common.edit")}
                                  onClick={() => {
                                    setEditingChallenge(ch);
                                    // Load full challenge details for teams
                                    challengesApi.getChallenge(ch.id, accountId).then((full: any) => {
                                      setChallengeForm(hydrateChallengeForm(full));
                                      // Load participants for team assignment (active challenges)
                                      if ((ch.status === "active" || ch.status === "paused") && full.teamAssignmentMode) {
                                        setManagingParticipants(full.participants || []);
                                        const asgn: Record<string, string | null> = {};
                                        for (const p of (full.participants || [])) {
                                          asgn[p.id] = p.teamId || null;
                                        }
                                        setTeamAssignments(asgn);
                                      }
                                    }).catch(() => {
                                      setChallengeForm(hydrateChallengeForm(ch, []));
                                    });
                                    setIsChallengeDialogOpen(true);
                                  }}
                                >
                                  <Pencil className="h-4 w-4" />
                                  <span className="hidden md:inline">{t("common.edit")}</span>
                                </Button>
                                )}
                                {challengePermissions(user, ch).pause && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-yellow-600 hover:text-yellow-700 gap-1.5"
                                  title={t("challenge.pause")}
                                  onClick={() => setConfirmAction({ type: "pause", challenge: ch })}
                                >
                                  <RotateCcw className="h-4 w-4" />
                                  <span className="hidden md:inline">{t("challenge.pause")}</span>
                                </Button>
                                )}
                                {challengePermissions(user, ch).resume && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-green-600 hover:text-green-700 gap-1.5"
                                  title={t("challenge.resume")}
                                  onClick={() => setConfirmAction({ type: "resume", challenge: ch })}
                                >
                                  <Play className="h-4 w-4" />
                                  <span className="hidden md:inline">{t("challenge.resume")}</span>
                                </Button>
                                )}
                                {/* Date de fin passée pendant la pause : pas de « Reprendre » ; on prolonge d'abord via « Modifier » (30/09/2026). */}
                                {resumeBlockedByEndDate(user, ch) && (
                                  <span className="text-xs self-center" style={{ color: "#926B00" }}>{t("challenge.resumeNeedsNewEndDate")}</span>
                                )}
                                {challengePermissions(user, ch).end && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-orange-500 hover:text-orange-600 gap-1.5"
                                  title={t("challenge.end")}
                                  onClick={() => setConfirmAction({ type: "end", challenge: ch })}
                                >
                                  <Square className="h-4 w-4" />
                                  <span className="hidden md:inline">{t("challenge.end")}</span>
                                </Button>
                                )}
                              </>
                            )}
                            {(ch.status === "active" || ch.status === "paused" || ch.status === "ended") && onNavigateToChallenge && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-blue-600 hover:text-blue-700 gap-1.5"
                                title={t("challenge.viewLeaderboard")}
                                onClick={() => onNavigateToChallenge(ch.id)}
                              >
                                <Eye className="h-4 w-4" />
                                <span className="hidden md:inline">{t("challenge.viewLeaderboard")}</span>
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Challenge Create/Edit Modal (plain HTML — no Radix focus trap for mobile date picker compat) */}
          {isChallengeDialogOpen && (
            <>
              <div className="fixed inset-0 z-50 bg-black/80" onClick={() => { setIsChallengeDialogOpen(false); resetChallengeForm(); }} />
              <div data-tour="challenge-dialog" role="dialog" aria-modal="true" aria-label={editingChallenge ? t("challenge.edit") : t("challenge.create")} className="fixed left-[50%] top-[50%] z-50 translate-x-[-50%] translate-y-[-50%] max-w-4xl w-[95vw] sm:w-[90vw] max-h-[85vh] overflow-y-auto bg-background rounded-lg border shadow-lg p-6" style={{ resize: "both", overflow: "auto", minWidth: "340px", minHeight: "300px" }}>
                <button
                  className="absolute right-4 top-4 rounded-sm opacity-70 hover:opacity-100 transition-opacity"
                  onClick={() => { setIsChallengeDialogOpen(false); resetChallengeForm(); }}
                >
                  <X className="h-4 w-4" />
                </button>
                <h2 className="text-lg font-semibold leading-none tracking-tight mb-1">
                  {editingChallenge ? t("challenge.edit") : t("challenge.create")}
                </h2>
                <div className="space-y-4 mt-4">
                  <div>
                    <Label className="mb-1.5 block">{t("challenge.titleField")} *</Label>
                    <Input
                      value={challengeForm.title}
                      onChange={(e) => setChallengeForm({ ...challengeForm, title: e.target.value })}
                      placeholder={t("challenge.titlePlaceholder")}
                    />
                  </div>
                  <div>
                    <Label className="mb-1.5 block">{t("challenge.description")}</Label>
                    <Textarea
                      value={challengeForm.description}
                      onChange={(e) => setChallengeForm({ ...challengeForm, description: e.target.value })}
                      placeholder={t("challenge.descriptionPlaceholder")}
                      rows={3}
                    />
                  </div>

                  {/* V2 — Type de challenge (masqué ici, les challenges inter se créent depuis Admin > Challenges inter) */}
                  {false && user.profileType === "ADM" && (
                    <div className="space-y-3 p-4 rounded-lg border-2 border-[#F6C847]/30 bg-yellow-50/30">
                      <div>
                        <Label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                          {t("account.challengeType")}
                        </Label>
                        <div className="flex gap-2">
                          <Button size="sm" variant={challengeForm.scope === "intra" ? "default" : "outline"}
                            className={challengeForm.scope === "intra" ? "bg-[#384872] text-white" : ""}
                            onClick={() => setChallengeForm({ ...challengeForm, scope: "intra" })}>
                            {t("account.intraCompany")}
                          </Button>
                          <Button size="sm" variant={challengeForm.scope === "inter_public" ? "default" : "outline"}
                            className={challengeForm.scope === "inter_public" ? "bg-[#F6C847] text-[#926B00]" : ""}
                            onClick={() => setChallengeForm({ ...challengeForm, scope: "inter_public" })}>
                            {t("account.interPublic")}
                          </Button>
                          <Button size="sm" variant={challengeForm.scope === "inter_cross_clients" ? "default" : "outline"}
                            className={challengeForm.scope === "inter_cross_clients" ? "bg-[#F6C847] text-[#926B00]" : ""}
                            onClick={() => setChallengeForm({ ...challengeForm, scope: "inter_cross_clients" })}>
                            {t("account.interSelectedClients")}
                          </Button>
                        </div>
                      </div>

                      {/* Société fictive (inter uniquement) */}
                      {challengeForm.scope !== "intra" && (
                        <div className="space-y-2">
                          <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                            {t("account.fictiveCompany")}
                          </Label>
                          <Input value={challengeForm.fictiveCompanyName}
                            onChange={(e) => setChallengeForm({ ...challengeForm, fictiveCompanyName: e.target.value })}
                            placeholder={t("account.fictiveCompanyName")} />
                          <Textarea value={challengeForm.fictiveCompanyDescription}
                            onChange={(e) => setChallengeForm({ ...challengeForm, fictiveCompanyDescription: e.target.value })}
                            placeholder={t("account.fictiveCompanyDescription")} rows={2} />
                        </div>
                      )}

                      {/* Cross clients — sélection des comptes */}
                      {challengeForm.scope === "inter_cross_clients" && (
                        <div>
                          <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1 block">
                            {t("account.allowedClients")}
                          </Label>
                          <p className="text-[10px] text-muted-foreground mb-1">
                            {t("account.allowedClientsHelp")}
                          </p>
                          {/* TODO Lot 4 — multi-select des clientAccounts (à implémenter avec la liste des comptes) */}
                          <Input value={challengeForm.allowedAccountIds.join(", ")}
                            onChange={(e) => setChallengeForm({ ...challengeForm, allowedAccountIds: e.target.value.split(",").map(s => s.trim()).filter(Boolean) })}
                            placeholder={t("account.allowedClientsPlaceholder")} />
                        </div>
                      )}

                      {/* Offres locales (inter) */}
                      {challengeForm.scope !== "intra" && (
                        <div>
                          <div className="flex items-center justify-between">
                            <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                              {t("account.challengeOffers")} ({challengeForm.offers.length}/3)
                            </Label>
                            {challengeForm.offers.length < 3 && (
                              <Button size="sm" variant="outline" onClick={() => setChallengeForm({ ...challengeForm, offers: [...challengeForm.offers, { name: "", description: "" }] })}>
                                <Plus className="h-3 w-3 mr-1" />{t("account.addAction")}
                              </Button>
                            )}
                          </div>
                          {challengeForm.offers.map((offer, i) => (
                            <div key={i} className="flex gap-2 mt-2">
                              <Input className="flex-1" value={offer.name}
                                onChange={(e) => { const upd = [...challengeForm.offers]; upd[i] = { ...upd[i], name: e.target.value }; setChallengeForm({ ...challengeForm, offers: upd }); }}
                                placeholder={t("account.offerNumber", { n: i + 1 })} />
                              <Input className="flex-1" value={offer.description}
                                onChange={(e) => { const upd = [...challengeForm.offers]; upd[i] = { ...upd[i], description: e.target.value }; setChallengeForm({ ...challengeForm, offers: upd }); }}
                                placeholder={t("account.descriptionLabel")} />
                              <Button size="sm" variant="ghost" onClick={() => setChallengeForm({ ...challengeForm, offers: challengeForm.offers.filter((_, j) => j !== i) })}>
                                <X className="h-3 w-3" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Récompense (inter) */}
                      {challengeForm.scope !== "intra" && (
                        <div className="space-y-2">
                          <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                            {t("account.reward")}
                          </Label>
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                            <Input value={challengeForm.giftName}
                              onChange={(e) => setChallengeForm({ ...challengeForm, giftName: e.target.value })}
                              placeholder={t("account.giftName")} />
                            <Input value={challengeForm.giftDescription}
                              onChange={(e) => setChallengeForm({ ...challengeForm, giftDescription: e.target.value })}
                              placeholder={t("account.descriptionLabel")} />
                            <Input value={challengeForm.giftPhotoUrl}
                              onChange={(e) => setChallengeForm({ ...challengeForm, giftPhotoUrl: e.target.value })}
                              placeholder={t("account.giftPhotoUrl")} />
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="mb-1.5 block">{t("challenge.simulator")} *</Label>
                      <Select
                        value={challengeForm.simulatorId}
                        onValueChange={async (v) => {
                          // Reset objectives when simulator changes (they depend on it)
                          setChallengeForm({ ...challengeForm, simulatorId: v, primaryObjective: "", secondaryObjective: "" });
                          // Load eligible collaborators (filtered by licence for this simulator)
                          setDraftCollaborators([]);
                          setDraftTeamAssignments({});
                          setLoadingDraftCollabs(true);
                          try {
                            const eligible = proposables(await challengesApi.getEligibleUsers(v, accountId), [...draftSelectedParticipants]);
                            setDraftCollaborators(eligible);
                            // La sélection est conservée, restreinte aux éligibles pour ce
                            // simulateur : la vider ferait perdre le pré-remplissage du
                            // cockpit, et obligerait à tout recocher au moindre changement.
                            const eligibleIds = new Set(eligible.map((u: any) => u.id));
                            setDraftSelectedParticipants((prev) => {
                              const kept = new Set([...prev].filter((id) => eligibleIds.has(id)));
                              // Première ouverture sans pré-sélection : tout le monde est
                              // coché. Un challenge d'équipe concerne l'équipe par défaut ;
                              // décocher trois personnes est plus rapide que d'en cocher dix.
                              return prev.size === 0 ? new Set(eligibleIds) : kept;
                            });
                          } catch { setDraftCollaborators([]); }
                          finally { setLoadingDraftCollabs(false); }
                        }}
                      >
                        <SelectTrigger><SelectValue placeholder={t("challenge.selectSimulator")} /></SelectTrigger>
                        <SelectContent>
                          {simulatorsList.filter(s => s.simulatorType === "training").map((sim) => (
                            <SelectItem key={sim.id} value={sim.id}>
                              <span className="flex items-center gap-2">
                                {sim.icon && sim.icon.startsWith("data:") ? (
                                  <img src={sim.icon} alt="" loading="lazy" className="h-5 w-5 object-contain shrink-0" />
                                ) : sim.icon ? (
                                  <span>{sim.icon}</span>
                                ) : null}
                                {sim.name}
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="mb-1.5 block">{t("challenge.challengeType")} *</Label>
                      <Select
                        value={challengeForm.challengeType}
                        onValueChange={(v) => setChallengeForm({ ...challengeForm, challengeType: v })}
                      >
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="mandatory">{t("challenge.mandatory")}</SelectItem>
                          <SelectItem value="optional">{t("challenge.optional")}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="mb-1.5 block">{t("challenge.startDate")} *</Label>
                      <input
                        type="date"
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                        value={challengeForm.startDate}
                        onChange={(e) => setChallengeForm({ ...challengeForm, startDate: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label className="mb-1.5 block">{t("challenge.endDate")} *</Label>
                      <input
                        type="date"
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                        value={challengeForm.endDate}
                        onChange={(e) => setChallengeForm({ ...challengeForm, endDate: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="mb-1.5 flex items-center gap-1.5">
                        {t("challenge.minSessions")}
                        <span className="relative group">
                          <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                          <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 px-3 py-2 text-xs text-white bg-slate-800 rounded-lg shadow-lg opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-opacity z-50">
                            {t("challenge.minSessionsHelp")}
                          </span>
                        </span>
                      </Label>
                      <Input
                        type="text"
                        inputMode="numeric"
                        value={challengeForm.minSessions}
                        onChange={(e) => { const v = e.target.value.replace(/\D/g, ""); setChallengeForm({ ...challengeForm, minSessions: v ? parseInt(v) : 0 }); }}
                      />
                    </div>
                    <div>
                      <Label className="mb-1.5 flex items-center gap-1.5">
                        {t("challenge.maxSessions")} *
                        <span className="relative group">
                          <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                          <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 px-3 py-2 text-xs text-white bg-slate-800 rounded-lg shadow-lg opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-opacity z-50">
                            {t("challenge.maxSessionsHelp")}
                          </span>
                        </span>
                      </Label>
                      <Input
                        type="text"
                        inputMode="numeric"
                        value={challengeForm.maxSessions}
                        onChange={(e) => { const v = e.target.value.replace(/\D/g, ""); setChallengeForm({ ...challengeForm, maxSessions: v ? parseInt(v) : 0 }); }}
                      />
                    </div>
                  </div>
                  <div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="mb-1.5 flex items-center gap-1.5">
                          {t("challenge.orangeThreshold")}
                          <span className="relative group">
                            <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                            <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 px-3 py-2 text-xs text-white bg-slate-800 rounded-lg shadow-lg opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-opacity z-50">
                              {t("challenge.thresholdHelp")}
                            </span>
                          </span>
                        </Label>
                        <Input
                          type="text"
                          inputMode="numeric"
                          value={challengeForm.orangeThresholdMinutes}
                          onChange={(e) => { const v = e.target.value.replace(/\D/g, ""); setChallengeForm({ ...challengeForm, orangeThresholdMinutes: v }); }}
                          placeholder="—"
                        />
                      </div>
                      <div>
                        <Label className="mb-1.5 flex items-center gap-1.5">
                          {t("challenge.redThreshold")}
                          <span className="relative group">
                            <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                            <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 px-3 py-2 text-xs text-white bg-slate-800 rounded-lg shadow-lg opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-opacity z-50">
                              {t("challenge.thresholdHelp")}
                            </span>
                          </span>
                        </Label>
                        <Input
                          type="text"
                          inputMode="numeric"
                          value={challengeForm.redThresholdMinutes}
                          onChange={(e) => { const v = e.target.value.replace(/\D/g, ""); setChallengeForm({ ...challengeForm, redThresholdMinutes: v }); }}
                          placeholder="—"
                        />
                      </div>
                    </div>
                  </div>
                  {/* Niveau de difficulté */}
                  <div className="space-y-1">
                    <Label className="mb-1.5 block">{t("account.difficultyLevel")}</Label>
                    <Select value={challengeForm.difficulty || ""} onValueChange={(v) => setChallengeForm({ ...challengeForm, difficulty: v === "_none" ? "" : v })}>
                      <SelectTrigger><SelectValue placeholder={t("account.difficultyFree")} /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="_none">{t("account.difficultyFree")}</SelectItem>
                        <SelectItem value="easy">{t("account.difficultyEasy")}</SelectItem>
                        <SelectItem value="medium">{t("account.difficultyMedium")}</SelectItem>
                        <SelectItem value="hard">{t("account.difficultyHard")}</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-[10px] text-muted-foreground">
                      {t("account.difficultyHint")}
                    </p>
                  </div>
                  {(() => {
                    // Objectifs : composant PARTAGÉ avec l'écran Challenges de Pilot (3 familles :
                    // objectifs du simulateur, critères de score, objectifs communs — décision 4.1).
                    const selectedSim = simulatorsList.find(s => s.id === challengeForm.simulatorId);
                    return (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <Label className="mb-1.5 flex items-center gap-1.5">
                            {t("challenge.primaryObjective")} *
                            <span className="relative group">
                              <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                              <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 px-3 py-2 text-xs text-white bg-slate-800 rounded-lg shadow-lg opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-opacity z-50">
                                {t("challenge.primaryObjectiveHelp")}
                              </span>
                            </span>
                          </Label>
                          <ChallengeObjectiveSelect
                            value={challengeForm.primaryObjective}
                            onChange={(v) => setChallengeForm({ ...challengeForm, primaryObjective: v })}
                            simulatorName={selectedSim?.name}
                            criteria={selectedSim?.scoringCriteria || []}
                            disabled={!challengeForm.simulatorId}
                            placeholder={challengeForm.simulatorId ? t("challenge.selectObjective") : t("challenge.selectSimulator")}
                            ariaLabel={t("challenge.primaryObjective")}
                          />
                        </div>
                        <div>
                          <Label className="mb-1.5 flex items-center gap-1.5">
                            {t("challenge.secondaryObjective")}
                            <span className="relative group">
                              <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                              <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 px-3 py-2 text-xs text-white bg-slate-800 rounded-lg shadow-lg opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-opacity z-50">
                                {t("challenge.secondaryObjectiveHelp")}
                              </span>
                            </span>
                          </Label>
                          <ChallengeObjectiveSelect
                            value={challengeForm.secondaryObjective}
                            onChange={(v) => setChallengeForm({ ...challengeForm, secondaryObjective: v })}
                            simulatorName={selectedSim?.name}
                            criteria={selectedSim?.scoringCriteria || []}
                            exclude={challengeForm.primaryObjective}
                            allowNone
                            disabled={!challengeForm.simulatorId}
                            placeholder={t("common.none")}
                            ariaLabel={t("challenge.secondaryObjective")}
                          />
                        </div>
                      </div>
                    );
                  })()}
                  {/* Teams section — draft: full edit with inline member assignment */}
                  {(!editingChallenge || editingChallenge.status === "draft") && (
                    <div className="border rounded-lg p-4 space-y-3">
                      <Label className="text-sm font-semibold">{t("challenge.teamsSection")}</Label>
                      <div>
                        <Label className="text-xs text-gray-500">{t("challenge.teamAssignmentMode")}</Label>
                        <Select
                          value={challengeForm.teamAssignmentMode || "_none"}
                          onValueChange={async (v) => {
                            const mode = v === "_none" ? "" : v;
                            setChallengeForm({ ...challengeForm, teamAssignmentMode: mode, teams: mode ? challengeForm.teams : [] });
                            // Reset assignments when switching modes
                            if (mode) {
                              setDraftSelectedParticipants(new Set());
                            } else {
                              setDraftTeamAssignments({});
                            }
                            // Load eligible collaborators if not already loaded
                            if (draftCollaborators.length === 0 && !loadingDraftCollabs && challengeForm.simulatorId) {
                              setLoadingDraftCollabs(true);
                              try {
                                const eligible = await challengesApi.getEligibleUsers(challengeForm.simulatorId, accountId);
                                setDraftCollaborators(proposables(eligible, [...draftSelectedParticipants, ...Object.keys(draftTeamAssignments)]));
                              } catch { setDraftCollaborators([]); }
                              finally { setLoadingDraftCollabs(false); }
                            }
                          }}
                        >
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="_none">{t("challenge.noTeams")}</SelectItem>
                            <SelectItem value="org_assigned">{t("challenge.orgAssigned")}</SelectItem>
                            <SelectItem value="participant_choice">{t("challenge.participantChoice")}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      {challengeForm.teamAssignmentMode && (
                        <div className="space-y-3">
                          {challengeForm.teams.map((team, idx) => {
                            const teamMembers = draftCollaborators.filter(c => draftTeamAssignments[c.id] === idx);
                            const unassigned = draftCollaborators.filter(c => draftTeamAssignments[c.id] === null || draftTeamAssignments[c.id] === undefined);
                            return (
                              <div key={idx} className="border rounded-lg p-3 space-y-2" style={{ borderLeftColor: team.color, borderLeftWidth: "4px" }}>
                                {/* Team header: color + name + delete */}
                                <div className="flex items-center gap-2">
                                  <button
                                    type="button"
                                    className="w-6 h-6 rounded-full border-2 border-white shadow shrink-0"
                                    style={{ backgroundColor: team.color }}
                                    title={t("challenge.teamColor")}
                                    onClick={() => {
                                      const currentIdx = TEAM_PALETTE.indexOf(team.color);
                                      const nextColor = TEAM_PALETTE[(currentIdx + 1) % TEAM_PALETTE.length];
                                      const updated = [...challengeForm.teams];
                                      updated[idx] = { ...team, color: nextColor };
                                      setChallengeForm({ ...challengeForm, teams: updated });
                                    }}
                                  />
                                  <Input
                                    value={team.name}
                                    onChange={(e) => {
                                      const updated = [...challengeForm.teams];
                                      updated[idx] = { ...team, name: e.target.value };
                                      setChallengeForm({ ...challengeForm, teams: updated });
                                    }}
                                    placeholder={t("challenge.teamName")}
                                    className="flex-1 h-8 text-sm"
                                  />
                                  <button
                                    type="button"
                                    className="text-red-400 hover:text-red-600 p-1"
                                    onClick={() => {
                                      // Remove team and unassign its members
                                      const newAssignments = { ...draftTeamAssignments };
                                      for (const c of teamMembers) {
                                        delete newAssignments[c.id];
                                      }
                                      // Shift down assignments for teams after this one
                                      for (const [userId, tIdx] of Object.entries(newAssignments)) {
                                        if (tIdx !== null && tIdx !== undefined && tIdx > idx) {
                                          newAssignments[userId] = tIdx - 1;
                                        }
                                      }
                                      setDraftTeamAssignments(newAssignments);
                                      const updated = challengeForm.teams.filter((_, i) => i !== idx);
                                      setChallengeForm({ ...challengeForm, teams: updated });
                                    }}
                                  >
                                    <X className="h-4 w-4" />
                                  </button>
                                </div>
                                {/* Team members as pills */}
                                {teamMembers.length > 0 && (
                                  <div className="flex flex-wrap gap-1.5">
                                    {teamMembers.map(c => (
                                      <span
                                        key={c.id}
                                        className="inline-flex items-center gap-1 pl-2.5 pr-1 py-1 rounded-full text-xs font-medium text-white"
                                        style={{ backgroundColor: team.color || "#384872" }}
                                      >
                                        {c.firstName} {c.lastName}
                                        <button
                                          type="button"
                                          className="ml-0.5 p-0.5 rounded-full hover:bg-white/30"
                                          onClick={() => {
                                            const newAssignments = { ...draftTeamAssignments };
                                            delete newAssignments[c.id];
                                            setDraftTeamAssignments(newAssignments);
                                          }}
                                        >
                                          <X className="h-3 w-3" />
                                        </button>
                                      </span>
                                    ))}
                                  </div>
                                )}
                                {/* Add member dropdown — only unassigned participants */}
                                {!loadingDraftCollabs && unassigned.length > 0 && (
                                  <select
                                    className="text-xs border rounded px-2 py-1.5 bg-background text-gray-500 w-full"
                                    value=""
                                    onChange={(e) => {
                                      if (e.target.value) {
                                        setDraftTeamAssignments({
                                          ...draftTeamAssignments,
                                          [e.target.value]: idx,
                                        });
                                        e.target.value = "";
                                      }
                                    }}
                                  >
                                    <option value="">+ {t("challenge.addParticipant")}...</option>
                                    {unassigned.map(c => (
                                      <option key={c.id} value={c.id}>
                                        {c.firstName} {c.lastName}
                                      </option>
                                    ))}
                                  </select>
                                )}
                                {loadingDraftCollabs && (
                                  <div className="flex items-center gap-2 text-xs text-gray-400 py-1">
                                    <Loader2 className="h-3 w-3 animate-spin" />
                                    {t("common.loading")}
                                  </div>
                                )}
                                {/* Aucun éligible : distinguer « compte vide » de « personne n'a
                                    accès à ce simulateur ». Afficher « Aucun collaborateur dans ce
                                    compte » alors qu'il y en a est trompeur — c'est ce qui a fait
                                    croire à un client que l'écran d'affectation n'existait pas
                                    (incident du 31/07/2026). */}
                                {!loadingDraftCollabs && draftCollaborators.length === 0 && (
                                  <p className="text-xs text-gray-400 italic py-1">
                                    {collaborators.length > 0
                                      ? t("challenge.noEligibleCollaborators")
                                      : t("challenge.noCollaborators")}
                                  </p>
                                )}
                                {/* Message when all are assigned (no unassigned left) */}
                                {!loadingDraftCollabs && draftCollaborators.length > 0 && unassigned.length === 0 && teamMembers.length === 0 && (
                                  <p className="text-xs text-gray-400 italic py-1">+ {t("challenge.addParticipant")}...</p>
                                )}
                              </div>
                            );
                          })}
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const nextColor = TEAM_PALETTE[challengeForm.teams.length % TEAM_PALETTE.length];
                              setChallengeForm({
                                ...challengeForm,
                                teams: [...challengeForm.teams, { name: "", color: nextColor }],
                              });
                            }}
                          >
                            <Plus className="h-4 w-4 mr-1" />
                            {t("challenge.addTeam")}
                          </Button>
                          {/* Show unassigned count */}
                          {draftCollaborators.length > 0 && (() => {
                            const unassignedAll = draftCollaborators.filter(c => draftTeamAssignments[c.id] === null || draftTeamAssignments[c.id] === undefined);
                            return unassignedAll.length > 0 ? (
                              <p className="text-xs text-gray-400 italic">
                                {t("challenge.unassigned")}: {unassignedAll.map(c => `${c.firstName} ${c.lastName}`).join(", ")}
                              </p>
                            ) : (
                              <p className="text-xs text-green-600 italic">
                                ✓ {t("challenge.allAssigned")}
                              </p>
                            );
                          })()}
                        </div>
                      )}
                      {/* Participants without teams — direct selection */}
                      {!challengeForm.teamAssignmentMode && challengeForm.simulatorId && (
                        <div className="space-y-2">
                          <Label className="text-xs text-gray-500">{t("challenge.participants")}</Label>
                          {loadingDraftCollabs && (
                            <div className="flex items-center gap-2 text-xs text-gray-400 py-1">
                              <Loader2 className="h-3 w-3 animate-spin" />
                              {t("common.loading")}
                            </div>
                          )}
                          {!loadingDraftCollabs && draftCollaborators.length === 0 && (
                            <p className="text-xs text-gray-400 italic">{t("challenge.noCollaborators")}</p>
                          )}
                          {!loadingDraftCollabs && draftCollaborators.length > 0 && (
                            <>
                              <div className="flex flex-wrap gap-1.5">
                                {draftCollaborators.map(c => {
                                  const isSelected = draftSelectedParticipants.has(c.id);
                                  return (
                                    <button
                                      key={c.id}
                                      type="button"
                                      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                                        isSelected
                                          ? "bg-[#384872] text-white border-[#384872]"
                                          : "bg-white text-gray-600 border-border hover:border-[#384872] hover:text-[#384872]"
                                      }`}
                                      onClick={() => {
                                        const next = new Set(draftSelectedParticipants);
                                        if (isSelected) next.delete(c.id); else next.add(c.id);
                                        setDraftSelectedParticipants(next);
                                      }}
                                    >
                                      {c.firstName} {c.lastName}
                                      {isSelected && <X className="h-3 w-3 ml-0.5" />}
                                    </button>
                                  );
                                })}
                              </div>
                              <div className="flex gap-2">
                                <button
                                  type="button"
                                  className="text-xs text-[#384872] hover:underline"
                                  onClick={() => setDraftSelectedParticipants(new Set(draftCollaborators.map(c => c.id)))}
                                >
                                  {t("common.selectAll")}
                                </button>
                                {draftSelectedParticipants.size > 0 && (
                                  <button
                                    type="button"
                                    className="text-xs text-gray-400 hover:underline"
                                    onClick={() => setDraftSelectedParticipants(new Set())}
                                  >
                                    {t("common.deselectAll")}
                                  </button>
                                )}
                              </div>
                              <p className="text-xs text-gray-400 italic">
                                {draftSelectedParticipants.size}/{draftCollaborators.length} {t("challenge.participants").toLowerCase()}
                              </p>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Active challenge: show teams + participant assignment inline */}
                  {editingChallenge && (editingChallenge.status === "active" || editingChallenge.status === "paused") && challengeForm.teamAssignmentMode && challengeForm.teams.length > 0 && (
                    <div className="border rounded-lg p-4 space-y-3">
                      <Label className="text-sm font-semibold">{t("challenge.teamsSection")}</Label>
                      {/* Teams list — read-only */}
                      <div className="flex flex-wrap gap-2">
                        {challengeForm.teams.map((team, idx) => (
                          <div key={idx} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium text-white" style={{ backgroundColor: team.color || "#384872" }}>
                            {team.name}
                          </div>
                        ))}
                      </div>
                      {/* Participant assignment */}
                      <div className="border-t pt-3 mt-2">
                        <Label className="text-sm font-semibold mb-2 block">{t("challenge.assignParticipants")}</Label>
                        {managingParticipants.length === 0 ? (
                          <p className="text-center py-4 text-xs text-gray-400">{t("challenge.noParticipantsYet")}</p>
                        ) : (
                          <div className="space-y-2 max-h-[200px] overflow-y-auto">
                            {managingParticipants.map((p) => (
                              <div key={p.id} className="flex items-center justify-between gap-3 py-1.5">
                                <span className="text-sm font-medium truncate">
                                  {p.userFirstName} {p.userLastName}
                                </span>
                                <select
                                  className="text-sm border rounded px-2 py-1.5 bg-background min-w-[140px]"
                                  value={teamAssignments[p.id] || ""}
                                  onChange={(e) => {
                                    setTeamAssignments({ ...teamAssignments, [p.id]: e.target.value || null });
                                  }}
                                >
                                  <option value="">{t("challenge.unassigned")}</option>
                                  {challengeForm.teams.filter(t => t.id).map((team) => (
                                    <option key={team.id} value={team.id}>
                                      {team.name}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            ))}
                          </div>
                        )}
                        {managingParticipants.length > 0 && (
                          <Button
                            type="button"
                            size="sm"
                            className="mt-3 w-full"
                            disabled={savingAssignments}
                            onClick={async () => {
                              setSavingAssignments(true);
                              try {
                                const assignments = Object.entries(teamAssignments).map(([participantId, teamId]) => ({
                                  participantId,
                                  teamId,
                                }));
                                await challengesApi.assignTeams(editingChallenge.id, assignments, accountId);
                                showSaveMessage(t("challenge.assignmentsSaved"));
                              } catch (err: any) {
                                alert(challengeErrorMessage(err, t));
                              } finally {
                                setSavingAssignments(false);
                              }
                            }}
                          >
                            {savingAssignments ? t("common.loading") : t("challenge.saveAssignments")}
                          </Button>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="flex justify-end gap-2 pt-2">
                    <Button variant="outline" onClick={() => { setIsChallengeDialogOpen(false); resetChallengeForm(); }}>
                      {t("common.cancel")}
                    </Button>
                    <Button
                      disabled={savingChallenge || !challengeForm.title.trim() || !challengeForm.simulatorId || !challengeForm.primaryObjective || !challengeForm.startDate || !challengeForm.endDate || !challengeForm.maxSessions || !challengeForm.challengeType}
                      onClick={async () => {
                        setSavingChallenge(true);
                        try {
                          const data: Record<string, any> = {
                            title: challengeForm.title.trim(),
                            description: challengeForm.description.trim() || undefined,
                            simulatorId: challengeForm.simulatorId,
                            startDate: challengeForm.startDate || undefined,
                            endDate: challengeForm.endDate || undefined,
                            minSessions: challengeForm.minSessions,
                            maxSessions: challengeForm.maxSessions,
                            orangeThresholdMinutes: challengeForm.orangeThresholdMinutes ? parseInt(challengeForm.orangeThresholdMinutes) : undefined,
                            redThresholdMinutes: challengeForm.redThresholdMinutes ? parseInt(challengeForm.redThresholdMinutes) : undefined,
                            primaryObjective: challengeForm.primaryObjective,
                            secondaryObjective: challengeForm.secondaryObjective || undefined,
                            challengeType: challengeForm.challengeType,
                            difficulty: challengeForm.difficulty || undefined,
                            teamAssignmentMode: challengeForm.teamAssignmentMode || undefined,
                            // V2 inter fields
                            scope: challengeForm.scope,
                            fictiveCompanyName: challengeForm.fictiveCompanyName || undefined,
                            fictiveCompanyDescription: challengeForm.fictiveCompanyDescription || undefined,
                            badgeId: challengeForm.badgeId || undefined,
                            giftName: challengeForm.giftName || undefined,
                            giftPhotoUrl: challengeForm.giftPhotoUrl || undefined,
                            giftDescription: challengeForm.giftDescription || undefined,
                            offers: challengeForm.scope !== "intra" ? challengeForm.offers.filter(o => o.name.trim()) : undefined,
                            personae: challengeForm.scope !== "intra" ? challengeForm.personae : undefined,
                            allowedAccountIds: challengeForm.scope === "inter_cross_clients" ? challengeForm.allowedAccountIds : undefined,
                          };

                          // Build team pre-assignments: userId → teamIndex
                          const preAssignments: Array<{ userId: string; teamIndex: number }> = [];
                          for (const [userId, teamIdx] of Object.entries(draftTeamAssignments)) {
                            if (teamIdx !== null && teamIdx !== undefined) {
                              preAssignments.push({ userId, teamIndex: teamIdx });
                            }
                          }

                          // Include selected participants without teams
                          if (!challengeForm.teamAssignmentMode && draftSelectedParticipants.size > 0) {
                            data.selectedParticipantIds = [...draftSelectedParticipants];
                          }

                          if (editingChallenge) {
                            // Bug A : persister les équipes AUSSI en édition d'un brouillon
                            // (le backend handleUpdateChallenge gère désormais body.teams).
                            if (editingChallenge.status === "draft") {
                              if (challengeForm.teamAssignmentMode && challengeForm.teams.length > 0) {
                                data.teams = challengeForm.teams.filter(t => t.name.trim()).map(t => ({ name: t.name.trim(), color: t.color }));
                              } else if (!challengeForm.teamAssignmentMode) {
                                data.teams = []; // mode équipe désactivé → vider les équipes
                              }
                            }
                            // Send pre-assignments for draft challenges with teams
                            if (editingChallenge.status === "draft" && preAssignments.length > 0) {
                              data.teamPreAssignments = preAssignments;
                            }
                            // Send selected participants without teams for draft
                            if (editingChallenge.status === "draft" && !challengeForm.teamAssignmentMode) {
                              data.selectedParticipantIds = [...draftSelectedParticipants];
                            }
                            await challengesApi.updateChallenge(editingChallenge.id, data, accountId);
                          } else {
                            // Include teams only on creation
                            if (challengeForm.teamAssignmentMode && challengeForm.teams.length > 0) {
                              data.teams = challengeForm.teams.filter(t => t.name.trim()).map(t => ({ name: t.name.trim(), color: t.color }));
                            }
                            if (preAssignments.length > 0) {
                              data.teamPreAssignments = preAssignments;
                            }
                            await challengesApi.createChallenge(data, accountId);
                          }
                          loadChallenges();
                          setIsChallengeDialogOpen(false);
                          resetChallengeForm();
                          showSaveMessage(editingChallenge ? t("common.success") : t("common.success"));
                        } catch (err: any) {
                          alert(challengeErrorMessage(err, t));
                        } finally {
                          setSavingChallenge(false);
                        }
                      }}
                    >
                      {savingChallenge ? t("account.saving") : t("common.save")}
                    </Button>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Challenge confirm action dialog */}
          <Dialog open={confirmAction !== null} onOpenChange={(open) => { if (!open) setConfirmAction(null); }}>
            <DialogContent className="max-w-sm">
              <DialogHeader>
                <DialogTitle>
                  {confirmAction?.type === "activate" && t("challenge.confirmActivate")}
                  {confirmAction?.type === "end" && t("challenge.confirmEnd")}
                  {confirmAction?.type === "delete" && t("challenge.confirmDelete")}
                  {confirmAction?.type === "pause" && t("challenge.confirmPause")}
                  {confirmAction?.type === "resume" && t("challenge.confirmResume")}
                </DialogTitle>
                <DialogDescription>
                  {confirmAction?.type === "activate" && t("challenge.confirmActivateMessage")}
                  {confirmAction?.type === "end" && t("challenge.confirmEndMessage")}
                  {confirmAction?.type === "delete" && t("challenge.confirmDeleteMessage", { name: confirmAction?.challenge.title })}
                  {confirmAction?.type === "pause" && t("challenge.confirmPauseMessage")}
                  {confirmAction?.type === "resume" && t("challenge.confirmResumeMessage")}
                </DialogDescription>
              </DialogHeader>
              <div className="flex justify-end gap-2 mt-4">
                <Button variant="outline" onClick={() => setConfirmAction(null)}>
                  {t("common.cancel")}
                </Button>
                <Button
                  variant={confirmAction?.type === "delete" ? "destructive" : "default"}
                  onClick={async () => {
                    if (!confirmAction) return;
                    try {
                      if (confirmAction.type === "activate") {
                        await challengesApi.activateChallenge(confirmAction.challenge.id, accountId);
                      } else if (confirmAction.type === "end") {
                        await challengesApi.endChallenge(confirmAction.challenge.id, accountId);
                      } else if (confirmAction.type === "pause") {
                        await challengesApi.pauseChallenge(confirmAction.challenge.id, accountId);
                      } else if (confirmAction.type === "resume") {
                        await challengesApi.resumeChallenge(confirmAction.challenge.id, accountId);
                      } else if (confirmAction.type === "delete") {
                        await challengesApi.deleteChallenge(confirmAction.challenge.id, accountId);
                      }
                      loadChallenges();
                      setConfirmAction(null);
                    } catch (err: any) {
                      alert(challengeErrorMessage(err, t));
                    }
                  }}
                >
                  {t("common.confirm")}
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Team Management Modal */}
          {isTeamManagementOpen && managingChallenge && (
            <>
              <div className="fixed inset-0 z-50 bg-black/80" onClick={() => { setIsTeamManagementOpen(false); setShowAddParticipants(false); setShowNewTeamInput(false); }} />
              <div className="fixed left-[50%] top-[50%] z-50 translate-x-[-50%] translate-y-[-50%] max-w-lg w-[95vw] sm:w-[90vw] max-h-[85vh] overflow-y-auto bg-background rounded-lg border shadow-lg p-6">
                <button
                  className="absolute right-4 top-4 rounded-sm opacity-70 hover:opacity-100 transition-opacity"
                  onClick={() => { setIsTeamManagementOpen(false); setShowAddParticipants(false); setShowNewTeamInput(false); }}
                >
                  <X className="h-4 w-4" />
                </button>
                <h2 className="text-lg font-semibold mb-1">{t("challenge.manageTeams")}</h2>
                <p className="text-sm text-gray-500 mb-4">{managingChallenge.title}</p>

                {/* Teams section */}
                {managingTeams.length > 0 && (
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-sm font-semibold text-gray-600 uppercase">{t("challenge.teamsSection")}</h3>
                    </div>
                    <div className="flex flex-wrap gap-2 mb-2">
                      {managingTeams.map((team) => {
                        const memberCount = managingParticipants.filter(p => (teamAssignments[p.id] || p.teamId) === team.id).length;
                        return (
                          <div key={team.id} className="flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-sm" style={{ borderColor: team.color || "#ddd" }}>
                            <div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: team.color || "#384872" }} />
                            <span className="font-medium">{team.name}</span>
                            <span className="text-gray-400 text-xs">({memberCount})</span>
                            {memberCount === 0 && (
                              <button
                                className="text-red-400 hover:text-red-600 ml-0.5"
                                title={t("common.delete")}
                                onClick={async () => {
                                  if (!confirm(t("challenge.deleteTeamConfirm"))) return;
                                  try {
                                    await challengesApi.deleteTeam(managingChallenge.id, team.id, accountId);
                                    await refreshManagingChallenge(managingChallenge.id);
                                  } catch (err: any) {
                                    alert(challengeErrorMessage(err, t));
                                  }
                                }}
                              >
                                <X className="h-3 w-3" />
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    {/* Add new team inline */}
                    {showNewTeamInput ? (
                      <div className="flex items-center gap-2 mt-2">
                        <input
                          className="text-sm border rounded px-2 py-1.5 flex-1"
                          placeholder={t("challenge.teamName")}
                          value={newTeamName}
                          onChange={(e) => setNewTeamName(e.target.value)}
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && newTeamName.trim()) {
                              e.preventDefault();
                              (async () => {
                                setCreatingTeam(true);
                                try {
                                  const color = TEAM_PALETTE[managingTeams.length % TEAM_PALETTE.length];
                                  await challengesApi.createTeam(managingChallenge.id, { name: newTeamName.trim(), color }, accountId);
                                  setNewTeamName("");
                                  setShowNewTeamInput(false);
                                  await refreshManagingChallenge(managingChallenge.id);
                                } catch (err: any) { alert(challengeErrorMessage(err, t)); }
                                finally { setCreatingTeam(false); }
                              })();
                            }
                          }}
                        />
                        <Button
                          size="sm"
                          disabled={creatingTeam || !newTeamName.trim()}
                          onClick={async () => {
                            setCreatingTeam(true);
                            try {
                              const color = TEAM_PALETTE[managingTeams.length % TEAM_PALETTE.length];
                              await challengesApi.createTeam(managingChallenge.id, { name: newTeamName.trim(), color }, accountId);
                              setNewTeamName("");
                              setShowNewTeamInput(false);
                              await refreshManagingChallenge(managingChallenge.id);
                            } catch (err: any) { alert(challengeErrorMessage(err, t)); }
                            finally { setCreatingTeam(false); }
                          }}
                        >
                          <Check className="h-4 w-4" />
                          <span>{t("common.save")}</span>
                        </Button>
                        {/* « Annuler » en toutes lettres : une icône ✕ se lisait « Supprimer » (décision 4.1). */}
                        <Button size="sm" variant="ghost" onClick={() => { setShowNewTeamInput(false); setNewTeamName(""); }}>
                          {t("common.cancel")}
                        </Button>
                      </div>
                    ) : (
                      <button
                        className="text-sm text-purple-600 hover:text-purple-700 font-medium flex items-center gap-1 mt-1"
                        onClick={() => setShowNewTeamInput(true)}
                      >
                        <Plus className="h-3.5 w-3.5" /> {t("challenge.newTeam")}
                      </button>
                    )}
                  </div>
                )}

                {/* Participants section */}
                <div className="mb-2 flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-gray-600 uppercase">{t("challenge.assignParticipants")}</h3>
                  <button
                    className="text-sm text-purple-600 hover:text-purple-700 font-medium flex items-center gap-1"
                    onClick={async () => {
                      if (showAddParticipants) { setShowAddParticipants(false); return; }
                      try {
                        const eligible = managingChallenge?.simulatorId
                          ? await challengesApi.getEligibleUsers(managingChallenge.simulatorId, accountId)
                          : await accountApi.getCollaborators(accountId).then((c: any[]) => c.filter((u: any) => u.status !== "disabled"));
                        const enrolledUserIds = new Set(managingParticipants.map(p => p.userId));
                        const available = proposables(eligible).filter((c: any) => !enrolledUserIds.has(c.id));
                        setAvailableCollabs(available.map((c: any) => ({ id: c.id, firstName: c.firstName || "", lastName: c.lastName || "" })));
                        setSelectedNewParticipants(new Set());
                        setShowAddParticipants(true);
                      } catch (err: any) { alert(challengeErrorMessage(err, t)); }
                    }}
                  >
                    <Plus className="h-3.5 w-3.5" /> {t("challenge.addParticipants")}
                  </button>
                </div>

                {/* Add participants panel */}
                {showAddParticipants && (
                  <div className="mb-4 p-3 bg-gray-50 rounded-lg border">
                    <p className="text-xs text-gray-500 mb-2">{t("challenge.selectCollaborators")}</p>
                    {availableCollabs.length === 0 ? (
                      <p className="text-sm text-gray-400 italic">{t("challenge.noCollaboratorsToAdd")}</p>
                    ) : (
                      <>
                        <div className="space-y-1 max-h-40 overflow-y-auto">
                          {availableCollabs.map(c => (
                            <label key={c.id} className="flex items-center gap-2 py-1 px-2 rounded hover:bg-gray-100 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={selectedNewParticipants.has(c.id)}
                                onChange={(e) => {
                                  const next = new Set(selectedNewParticipants);
                                  e.target.checked ? next.add(c.id) : next.delete(c.id);
                                  setSelectedNewParticipants(next);
                                }}
                                className="rounded"
                              />
                              <span className="text-sm">{c.firstName} {c.lastName}</span>
                            </label>
                          ))}
                        </div>
                        <div className="flex justify-end mt-2">
                          <Button
                            size="sm"
                            disabled={addingParticipants || selectedNewParticipants.size === 0}
                            onClick={async () => {
                              setAddingParticipants(true);
                              try {
                                const count = await challengesApi.addParticipants(managingChallenge.id, [...selectedNewParticipants], undefined, accountId);
                                setShowAddParticipants(false);
                                showSaveMessage(t("challenge.participantsAdded", { count }));
                                await refreshManagingChallenge(managingChallenge.id);
                              } catch (err: any) { alert(challengeErrorMessage(err, t)); }
                              finally { setAddingParticipants(false); }
                            }}
                          >
                            {addingParticipants ? t("common.loading") : t("common.confirm")} ({selectedNewParticipants.size})
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* Participants list with team assignment and remove button */}
                <div className="space-y-2">
                  {managingParticipants.map((p) => (
                    <div key={p.id} className="flex items-center justify-between gap-2 py-2 border-b last:border-0">
                      <span className="text-sm font-medium truncate flex-1 min-w-0">
                        {p.userFirstName} {p.userLastName}
                      </span>
                      {managingTeams.length > 0 && (
                        <select
                          className="text-sm border rounded px-2 py-1.5 bg-background min-w-[140px]"
                          value={teamAssignments[p.id] || ""}
                          onChange={(e) => {
                            setTeamAssignments({ ...teamAssignments, [p.id]: e.target.value || null });
                          }}
                        >
                          <option value="">{t("challenge.unassigned")}</option>
                          {managingTeams.map((team) => (
                            <option key={team.id} value={team.id}>
                              {team.name}
                            </option>
                          ))}
                        </select>
                      )}
                      <button
                        className="text-red-400 hover:text-red-600 shrink-0 p-1"
                        title={t("challenge.removeParticipant")}
                        onClick={async () => {
                          if (!confirm(t("challenge.removeParticipantConfirm"))) return;
                          try {
                            await challengesApi.removeParticipant(managingChallenge.id, p.id, accountId);
                            showSaveMessage(t("challenge.participantRemoved"));
                            await refreshManagingChallenge(managingChallenge.id);
                          } catch (err: any) { alert(challengeErrorMessage(err, t)); }
                        }}
                      >
                        <UserX className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                  {managingParticipants.length === 0 && (
                    <p className="text-sm text-gray-400 text-center py-4">{t("challenge.noParticipants")}</p>
                  )}
                </div>
                <div className="flex justify-end gap-2 mt-4">
                  <Button variant="outline" onClick={() => { setIsTeamManagementOpen(false); setShowAddParticipants(false); setShowNewTeamInput(false); }}>
                    {t("common.close")}
                  </Button>
                  {managingTeams.length > 0 && managingParticipants.length > 0 && (
                    <Button
                      disabled={savingAssignments}
                      onClick={async () => {
                        setSavingAssignments(true);
                        try {
                          const assignments = Object.entries(teamAssignments).map(([participantId, teamId]) => ({
                            participantId,
                            teamId,
                          }));
                          await challengesApi.assignTeams(managingChallenge.id, assignments, accountId);
                          setIsTeamManagementOpen(false);
                          setShowAddParticipants(false);
                          setShowNewTeamInput(false);
                          showSaveMessage(t("challenge.assignmentsSaved"));
                        } catch (err: any) {
                          alert(challengeErrorMessage(err, t));
                        } finally {
                          setSavingAssignments(false);
                        }
                      }}
                    >
                      {savingAssignments ? t("common.loading") : t("challenge.saveAssignments")}
                    </Button>
                  )}
                </div>
              </div>
            </>
          )}
        </TabsContent>

          {/* Confirmation de la désactivation d'un cas (deux boutons, Q1 du 01/10/2026) */}
          <Dialog open={archivingTarget !== null} onOpenChange={(v) => { if (!v) setArchivingTarget(null); }}>
            <DialogContent className="sm:max-w-[425px]">
              <DialogHeader>
                <DialogTitle>{t("account.archiveConfirmTitle")}</DialogTitle>
                <DialogDescription className="pt-2">{t("account.archiveConfirmBody")}</DialogDescription>
              </DialogHeader>
              <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
                <Button variant="ghost" onClick={() => setArchivingTarget(null)}>{t("common.cancel")}</Button>
                <Button
                  data-testid="target-archive-confirm"
                  style={{ backgroundColor: "#384872" }}
                  onClick={async () => { const cible = archivingTarget; setArchivingTarget(null); if (cible) await setTargetArchived(cible, true); }}
                >
                  {t("account.archiveConfirm")}
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Dialogs de confirmation de suppression */}
          <ConfirmDeleteDialog
            open={deletingTarget !== null}
            onConfirm={confirmDeleteTarget}
            onCancel={() => setDeletingTarget(null)}
            itemName={deletingTarget ? [localizeCompanySize(deletingTarget.companySize, i18n.language), parseMultiValue(deletingTarget.industry).map((v) => localizeIndustry(v, i18n.language)).join(", ")].filter(Boolean).join(" — ") || t("account.targets") : ""}
          />

          <ConfirmDeleteDialog
            open={deletingPersona !== null}
            onConfirm={confirmDeletePersona}
            onCancel={() => setDeletingPersona(null)}
            itemName={deletingPersona ? getPersonaLabel(deletingPersona.persona) : "Persona"}
          />

          <ConfirmDeleteDialog
            open={revokingLicence !== null}
            onConfirm={confirmRevokeLicence}
            onCancel={() => setRevokingLicence(null)}
            itemName={revokingLicence ? `${revokingLicence.licenceName} (${revokingLicence.collabName})` : ""}
          />

          {/* ============ PERSONAE ============ */}
          <TabsContent value="personae" className="md:mt-0">
            <PersonaeTab accountId={accountId} />
          </TabsContent>

          {/* ============ CRITÈRES D'ÉVALUATION ============ */}
          <TabsContent value="evaluation-criteria" className="md:mt-0">
            <EvaluationCriteriaTab accountId={accountId} userProfileType={user.profileType} />
          </TabsContent>

          {/* ============ CONTEXTES DE SIMULATION ============ */}
          <TabsContent value="contexts" className="md:mt-0">
            <ContextsTab accountId={accountId} userProfileType={user.profileType} />
          </TabsContent>

          {/* ============ MES OFFRES ============ */}
          <TabsContent value="offers" className="md:mt-0">
            <OffersTab accountId={accountId} />
          </TabsContent>

          {/* ============ FACTURES ============ */}
          <TabsContent value="invoices" className="md:mt-0">
            <InvoicesTab accountId={accountId} />
          </TabsContent>

          </div>{/* fin contenu */}
        </div>{/* fin flex container */}
      </Tabs>

      {/* Wizard tunnel création cas de simulation */}
      <SimulationCaseWizard
        open={showSimWizard}
        onClose={() => setShowSimWizard(false)}
        companySizeOptions={companySizeOptions}
        industryOptions={industryOptions}
        restrictToUnlocked
        onCreated={() => {
          accountApi.getTargets(accountId, { includeArchived: true }).then((list: any[]) => {
            setTargets(list.map((t: any) => ({
              id: t.id, caseType: t.caseType || t.case_type || "B2B",
              simulatorId: t.simulatorId || t.simulator_id || null,
              contextId: t.contextId || t.context_id || null,
              companySize: t.companySize || t.company_size || "",
              industry: t.industry || "", offerIds: t.offerIds || t.offer_ids || [],
              // Idem que le mapping initial : champs nécessaires aux onglets + date + titre libre
              createdAt: t.createdAt || t.created_at || null,
              visibility: t.visibility || "public",
              sourceRealCallSessionId: t.sourceRealCallSessionId || t.source_real_call_session_id || null,
              displayName: t.displayName || t.display_name || null,
              createdBy: t.createdBy ?? t.created_by ?? null,
              ownerUserId: t.ownerUserId ?? t.owner_user_id ?? null,
              archivedAt: t.archivedAt ?? t.archived_at ?? null,
              personae: (t.personae || []).map((p: any) => ({
                ...p, // préserve les variantes i18n *Fr/*En
                id: p.id, targetId: t.id, function: p.function || "",
                decisionMakerProfile: p.decisionMakerProfile || p.decision_maker_profile || "", painPoints: p.painPoints || p.pain_points || "",
                category: p.category || "sales", personaType: p.personaType || p.persona_type || "B2B",
                csp: p.csp || "", ageRange: p.ageRange || p.age_range || "", gender: p.gender || "",
                purchaseCriteria: p.purchaseCriteria || p.purchase_criteria || "", purchasePhase: p.purchasePhase || p.purchase_phase || "",
                productRelationship: p.productRelationship || p.product_relationship || "", contactReason: p.contactReason || p.contact_reason || "",
                expressionStyle: p.expressionStyle || p.expression_style || "", mainExpectation: p.mainExpectation || p.main_expectation || "",
              })),
            })));
          }).catch(() => {});
          accountApi.getOffers(accountId).then((data) => setAccountOffers(data || [])).catch(() => {});
          accountApi.getAccountPersonae(accountId).then((data) => setAccountPersonae(data || [])).catch(() => {});
          setShowSimWizard(false);
        }}
        accountId={accountId}
      />
    </div>
  );
}
